import {
  type AcademicTerm,
  type AssignmentCopyRequest,
  type AssignmentFolderAction,
  type AttendanceRecord,
  answerMedalRanks,
  applyAssignmentFolderAction,
  applyDailyMemoAction,
  applyDailyRecordAction,
  applyDrawAction,
  applyHomeroomAffairChange,
  applySchoolCalendarChanges,
  assignmentCopies,
  assignmentDeletionPlan,
  attendanceCounts,
  buildStudentSummaries,
  type CalendarEvent,
  type CalendarImportCandidate,
  type Classroom,
  calendarAssignments,
  calendarEventForAssignment,
  calendarFingerprint,
  calendarWithPublicHolidays,
  compareStudentNumbers,
  type DailyMemoAction,
  type DailyRecord,
  type DailyRecordAction,
  DEFAULT_CLASS_DUTIES,
  DEFAULT_HOMEROOM_SUBJECTS,
  type DrawAction,
  dateInTerm,
  ensureDateSchoolYear,
  groupStudents,
  HOMEROOM_HOMEWORK_TASK_TYPES,
  type HomeroomAffairChange,
  type HomeroomHomeworkRecord,
  type HomeroomHomeworkTaskType,
  type HomeroomRecord,
  type HomeworkAssignment,
  type HomeworkRecord,
  isCalendarHoliday,
  isHongKongPublicHolidayDate,
  PUBLIC_HOLIDAY_SOURCE_URL,
  parseRosterText,
  type QuizDocument,
  type QuizResponse,
  type QuizSession,
  RosterParseError,
  type RosterRow,
  type SchoolCalendarChange,
  type SchoolYear,
  type SchoolYearAction,
  type SeatingChart,
  type Student,
  type StudentRoutineItem,
  schoolYearDeletionToken,
  schoolYearForDate,
  schoolYears,
  schoolYearTerms,
  scopeStudentRecords,
  studentAttendanceHistory,
  studentMissingHomeworkHistory,
  studentNumberLabel,
  studentRosterKey,
  validateAcademicTerms,
  validateNewStudent,
  validateQuizImport,
  type Workspace,
} from "@classroom/domain";
import type { User } from "@supabase/supabase-js";
import QRCode from "qrcode";
import { useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import AdminRecordExportPanel from "./AdminRecordExportPanel";
import AgendaCourseworkSummary from "./AgendaCourseworkSummary";
import AssignmentRecords, { type AssignmentNavigation } from "./AssignmentRecords";
import AutosaveStatus from "./AutosaveStatus";
import BrandIcon from "./BrandIcon";
import BrandWordmark from "./BrandWordmark";
import CalendarArrangement, { calendarArrangementLabel } from "./CalendarArrangement";
import CalendarImportDialog from "./CalendarImportDialog";
import ClassNameInput from "./ClassNameInput";
import ClassroomPerformance from "./ClassroomPerformance";
import ClassSetupFollowUp from "./ClassSetupFollowUp";
import ClassSetupWizard from "./ClassSetupWizard";
import ClassSwitcher from "./ClassSwitcher";
import DrawResetMode from "./DrawResetMode";
import { EmptyClassDeletion } from "./EmptyClassDeletion";
import GradeCategory from "./GradeCategory";
import GradeOverview from "./GradeOverview";
import GradeTable from "./GradeTable";
import GuidedTour, { DEMO_TOUR_SEQUENCE, type DemoTourStep, type TourStep } from "./GuidedTour";
import HomeroomAffairs from "./HomeroomAffairs";
import HomeroomNotebook from "./HomeroomNotebook";
import { i18n, interfaceLocale, useTranslation } from "./i18n";
import { homeroomHomeworkTaskLabel, teachingItemLabel, weekdayLabels } from "./i18n/labels";
import LanguageSwitcher from "./LanguageSwitcher";
import LegalAcceptanceGate from "./LegalAcceptanceGate";
import LocalDataPanel from "./LocalDataPanel";
import { useDialogFocus } from "./lib/accessibility";
import { downloadJson } from "./lib/api";
import { sortClasses } from "./lib/classOrder";
import { existingSetupDraft } from "./lib/classSetup";
import {
  classSeatingChart,
  classStudents,
  rosterClassId,
  setupProgress,
  suggestedSharedClassId,
} from "./lib/classSharing";
import { courseworkClassSummary } from "./lib/courseworkSummary";
import { useWorkspaceActions, useWorkspaceData } from "./lib/data";
import {
  demoAttendanceRecords,
  demoClasses,
  demoDailyRecords,
  demoEvents,
  demoHomeroomHomeworkRecords,
  demoHomeworkAssignments,
  demoHomeworkRecords,
  demoQuizzes,
  demoSeatingCharts,
  demoStudentRoutineItems,
  demoStudents,
  demoWorkspace,
} from "./lib/demo";
import { createSchoolYearDemo } from "./lib/demoSchoolYear";
import { availableStudentIds, type DrawState, hongKongDate } from "./lib/drawRounds";
import { CALENDAR_IMPORT_RELEASED, QUIZ_RELEASED } from "./lib/features";
import type { RecordExportScope, RecordExportSource } from "./lib/localRecordExport";
import type { LocalAccount } from "./lib/localStore/accountScope";
import { createEncryptedBackup } from "./lib/localStore/backupJob";
import { hasPendingLocalWrites } from "./lib/localStore/client";
import { encryptSnapshot } from "./lib/localStore/encryptedBackup";
import { COLLECTIONS, isRetiredCollection } from "./lib/localStore/schema";
import { useLocalWorkspace } from "./lib/localStore/useLocalWorkspace";
import { buildRecordReport, type RecordExportTab } from "./lib/recordExports";
import type { HistoryPlan, HistorySource } from "./lib/recordImport";
import { changeSchoolYear, schoolYearRows, schoolYearView } from "./lib/schoolYearView";
import type { SidebarMode } from "./lib/sidebarPreferences";
import { waitForLocalSaves } from "./lib/signOut";
import { buildStudentDutyReport } from "./lib/studentDutyReport";
import { useAutosavedDraft } from "./lib/useAutosavedDraft";
import { useResponseStudentDraw } from "./lib/useResponseStudentDraw";
import { useSaveBeforeUnload } from "./lib/useSaveBeforeUnload";
import { useSidebarLayout } from "./lib/useSidebarLayout";
import {
  createNavigationSession,
  navigationBoolean,
  navigationChoice,
  navigationDate,
  navigationId,
  navigationInteger,
  useNavigationState,
  WorkspaceNavigationContext,
} from "./lib/workspaceNavigation";
import NewStudentForm from "./NewStudentForm";
import PublicHolidayNotice from "./PublicHolidayNotice";
import { useQuickBackup } from "./QuickBackup";
import RecordActionButton from "./RecordActionButton";
import RecordExportButton from "./RecordExportButton";
import { RecordExportDialog } from "./RecordExportDialog";
import RecordImportPanel from "./RecordImportPanel";
import RosterImportPanel, { type RosterImportMethod } from "./RosterImportPanel";
import SchoolCalendarEditor from "./SchoolCalendarEditor";
import SessionAccountMenu from "./SessionAccountMenu";
import ToolPageHeader, { ToolPageNavigation, ToolPageToolbar } from "./ToolPageHeader";
import "./record-autosave.css";
import "./sidebar-navigation.css";
import "./personal-tool-navigation.css";
import SchoolYearSettings, { type YearDeletion } from "./SchoolYearSettings";
import SelectMenu from "./SelectMenu";
import StudentPagination, { studentPageSize, useStudentPagination } from "./StudentPagination";
import TermComparisonDialog from "./TermComparisonDialog";
import TermOverview from "./TermOverview";
import TermSettings from "./TermSettings";
import { toolIconPaths } from "./ToolIcon";

type Tab =
  | "today"
  | "records"
  | "homework"
  | "attendance"
  | "homeroomHomework"
  | "studentRecords"
  | "homeroom"
  | "homeroomDuties"
  | "seating"
  | "calendar"
  | "groups"
  | "quiz"
  | "roster"
  | "billing";
type AdminSection = "classes" | "history" | "export" | "quizzes" | "calendar" | "tools" | "terms";
type RecordMode = "materials" | "answers" | "assignments" | "grades" | "gradeTable" | "performance";
const performanceTabs: { mode: RecordMode; label: string }[] = [
  {
    mode: "performance",
    get label() {
      return i18n.t("screenCopy:teacherConsole.participationOverview");
    },
  },
  {
    mode: "answers",
    get label() {
      return i18n.t("landing:typeAnswers");
    },
  },
  {
    mode: "materials",
    get label() {
      return i18n.t("adminRecords:kinds.materials");
    },
  },
];
const courseworkTabs: { mode: RecordMode; label: string }[] = [
  { mode: "grades", label: "" },
  { mode: "gradeTable", label: "" },
  {
    mode: "assignments",
    get label() {
      return i18n.t("screenCopy:teacherConsole.recordEntry");
    },
  },
];
type ConsoleProps = {
  user: User | null;
  preview: boolean;
  localMode?: boolean;
  localAccount?: LocalAccount;
  assertWritable?: () => void;
  syncControl?: React.ReactNode;
  backupControl?: React.ReactNode;
  guidancePaused?: boolean;
  onInitialClassSetupComplete?: () => void;
  onExitPreview: () => void;
  onSignOut: () => Promise<void>;
};
const LOCAL_TOUR_STATUS_KEY = "classroom-free-beta-tour-v1";
const LOCAL_ROSTER_TOUR_STATUS_KEY = "classroom-trial-roster-tour-v1";

type NavItem = {
  id: Tab;
  icon: string;
  label: string;
  section?: string;
  roles?: Array<Classroom["classRole"]>;
  submenu?: boolean;
};

const nav: NavItem[] = [
  {
    id: "today",
    icon: "曆",
    get label() {
      return i18n.t("personalTools:dayLabel");
    },
  },
  {
    id: "records",
    icon: "記",
    get label() {
      return i18n.t("marketing:studentsTab");
    },
    get section() {
      return i18n.t("marketing:teachingToolsTitle");
    },
    roles: ["teaching"],
    submenu: true,
  },
  {
    id: "groups",
    icon: "組",
    get label() {
      return i18n.t("guidedTour:steps.demoGroups.title");
    },
    roles: ["teaching"],
  },
  {
    id: "quiz",
    icon: "測",
    get label() {
      return i18n.t("screenCopy:teacherConsole.questionBankAndQuizzes");
    },
    roles: ["teaching"],
  },
  {
    id: "attendance",
    icon: "點",
    get label() {
      return i18n.t("marketing:attendanceTab");
    },
    get section() {
      return i18n.t("marketing:homeroomToolsTitle");
    },
    roles: ["homeroom"],
  },
  {
    id: "homeroomHomework",
    icon: "欠",
    get label() {
      return i18n.t("guidedTour:steps.demoHomeroomHomework.title");
    },
    roles: ["homeroom"],
  },
  {
    id: "homeroom",
    icon: "班",
    get label() {
      return i18n.t("marketing:homeroomTab");
    },
    roles: ["homeroom"],
  },
  {
    id: "homeroomDuties",
    icon: "工",
    get label() {
      return i18n.t("marketing:dutiesTab");
    },
    roles: ["homeroom"],
  },
  {
    id: "studentRecords",
    icon: "生",
    get label() {
      return i18n.t("marketing:studentsTab");
    },
    roles: ["homeroom"],
  },
  {
    id: "seating",
    icon: "座",
    get label() {
      return i18n.t("marketing:seatingTab");
    },
    get section() {
      return i18n.t("screenCopy:teacherConsole.sharedTools");
    },
  },
  {
    id: "calendar",
    icon: "曆",
    get label() {
      return i18n.t("guidedTour:steps.demoCalendar.title");
    },
  },
  {
    id: "roster",
    icon: "管",
    get label() {
      return i18n.t("guidedTour:steps.demoRoster.title");
    },
    get section() {
      return i18n.t("screenCopy:teacherConsole.administration");
    },
  },
  {
    id: "billing",
    icon: "訂",
    get label() {
      return i18n.t("guidedTour:steps.demoData.title");
    },
  },
];
// Compile-time checklist: every released Demo tab must have a feature-tour step.
// Quiz stays excluded until QUIZ_RELEASED is enabled.
const demoTourTabSteps: Record<Exclude<Tab, "quiz" | "homework">, DemoTourStep> = {
  today: "demoToday",
  records: "demoPerformance",
  groups: "demoGroups",
  attendance: "demoAttendance",
  homeroomHomework: "demoHomeroomHomework",
  studentRecords: "demoStudentRecords",
  homeroom: "demoHomeroom",
  homeroomDuties: "demoHomeroomDuties",
  seating: "demoSeating",
  calendar: "demoCalendar",
  roster: "demoRoster",
  billing: "demoData",
};

const demoTourDestinations: Record<
  DemoTourStep,
  { tab?: Tab; role?: Classroom["classRole"]; recordMode?: RecordMode; adminSection?: AdminSection; picker?: boolean }
> = {
  demoWelcome: { picker: true },
  demoToday: { tab: "today" },
  demoPerformance: { tab: "records", role: "teaching", recordMode: "performance" },
  demoCoursework: { tab: "records", role: "teaching", recordMode: "grades" },
  demoGroups: { tab: "groups", role: "teaching" },
  demoSeating: { tab: "seating", role: "teaching" },
  demoCalendar: { tab: "calendar", role: "teaching" },
  demoAttendance: { tab: "attendance", role: "homeroom" },
  demoHomeroomHomework: { tab: "homeroomHomework", role: "homeroom" },
  demoStudentRecords: { tab: "studentRecords", role: "homeroom" },
  demoHomeroom: { tab: "homeroom", role: "homeroom" },
  demoHomeroomDuties: { tab: "homeroomDuties", role: "homeroom" },
  demoToolSettings: { tab: "roster", adminSection: "tools" },
  demoRoster: { tab: "roster", adminSection: "classes" },
  demoData: { tab: "billing" },
};
// Keep the stored homework visibility key for the coursework submenu.
const configurableNav: NavItem[] = [
  ...nav.filter((item) => !["today", "roster", "billing"].includes(item.id)),
  {
    id: "homework",
    icon: "績",
    get label() {
      return i18n.t("adminRecords:kinds.coursework");
    },
    roles: ["teaching"],
  },
];
const configurableIds = new Set(configurableNav.map((item) => item.id));
const TEACHING_ITEM_TYPES = ["功課", "測驗", "默書", "溫習", "帶備物品", "回條"] as const;
const TEACHING_ITEM_EVENT_TYPES: Record<(typeof TEACHING_ITEM_TYPES)[number] | "other", CalendarEvent["type"]> = {
  功課: "homework",
  測驗: "assessment",
  默書: "dictation",
  溫習: "reminder",
  帶備物品: "reminder",
  回條: "reminder",
  other: "reminder",
};

const navPaths: Record<Tab | "performance", string> = {
  today: "M4 5h16v16H4zM8 2v6M16 2v6M4 10h16M8 14h3M8 18h6",
  records:
    "M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2ZM12 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM8 15a4 4 0 0 1 8 0M8 18h8",
  performance: "M13 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0ZM7 21v-5l-3-4 2-2 4 4h3l4-5V3h3v7l-4 7v4H7Z",
  homework: "M3 4h12v16H3zM3 9h11M8 4v16m7-7 4-4 2 2-4 4-3 1 1-3Z",
  groups:
    "M12 7a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM12 8v3M5 11h14M5 11v2M19 11v2M5 18a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM19 18a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM2 22v-1a3 3 0 0 1 6 0v1M16 22v-1a3 3 0 0 1 6 0v1",
  attendance:
    "M9 11l2 2 4-4M9 17h6M8 3h8l1 3H7l1-3ZM5 5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2",
  homeroomHomework: "M4 5h16v15H4zM8 3v4M16 3v4M4 10h16M8 15h8",
  studentRecords:
    "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
  homeroom: "M3 10 12 3l9 7v11H3V10ZM9 21v-7h6v7",
  homeroomDuties: "M4 7h16M4 12h16M4 17h16M8 4v16M16 4v16",
  quiz: "m12 3 1.7 4.3L18 9l-4.3 1.7L12 15l-1.7-4.3L6 9l4.3-1.7L12 3ZM19 16l.7 1.3L21 18l-1.3.7L19 20l-.7-1.3L17 18l1.3-.7L19 16ZM5 15l1 2 2 1-2 1-1 2-1-2-2-1 2-1 1-2Z",
  seating: toolIconPaths.seating,
  calendar: toolIconPaths.calendar,
  roster: "m9 3-1 3-3 1-2 3 2 2-1 3 2 3 3-1 2 3h3l1-3 3-1 2-3-2-2 1-3-2-3-3 1-2-3H9Zm7 9a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z",
  billing: "M5 5h14l3 8v7H2v-7l3-8ZM2 13h20M6 17h1M10 8h4m-2-3v6m-2-2 2 2 2-2",
};
function SidebarToggleIcon({ expand = false }: { expand?: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="22"
      height="22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="16" rx="3" />
      <path d="M9 4v16" />
      <path d={expand ? "m13 9 3 3-3 3" : "m16 9-3 3 3 3"} />
    </svg>
  );
}

function NavIcon({ id }: { id: Tab | "performance" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={navPaths[id]} />
    </svg>
  );
}

function PersonalToolEntry({
  tab,
  active = false,
  home = false,
  onOpen,
}: {
  tab: "today";
  active?: boolean;
  home?: boolean;
  onOpen: () => void;
}) {
  const { t } = useTranslation("personalTools");
  return (
    <button
      type="button"
      className={`personal-tool-entry${home ? " personal-tool-card" : ""}${active ? " is-active" : ""}`}
      aria-label={t("dayLabel")}
      aria-current={active ? "page" : undefined}
      data-personal-tool={tab}
      data-demo-nav={home ? undefined : tab}
      onClick={onOpen}
    >
      <span className="personal-tool-glyph" aria-hidden="true">
        <NavIcon id={tab} />
      </span>
      <span className="personal-tool-copy">
        <strong>{t("dayLabel")}</strong>
        {home && <small>{t("agendaEntryHint")}</small>}
      </span>
      {home && (
        <svg
          className="personal-tool-arrow"
          aria-hidden="true"
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 12h14m-5-5 5 5-5 5" />
        </svg>
      )}
    </button>
  );
}

function CalendarArrowIcon({ direction }: { direction: "left" | "right" }) {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={direction === "left" ? "M15 6 9 12 15 18" : "M9 6 15 12 9 18"} />
    </svg>
  );
}

function id(prefix: string) {
  return `${prefix}_${crypto.randomUUID()}`;
}
function today() {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}
function isoNow() {
  return new Date().toISOString();
}
function hasPendingRecordSaves() {
  return hasPendingLocalWrites() || Boolean(document.querySelector('[data-save-state="saving"]'));
}
function seatingTracks(count: number, aisles: number[], seatTrack: string, normalGap: string, aisleGap: string) {
  const active = new Set(aisles);
  return Array.from({ length: count * 2 - 1 }, (_, index) =>
    index % 2 === 0 ? seatTrack : active.has((index - 1) / 2) ? aisleGap : normalGap,
  ).join(" ");
}
function studentGenderClass(student?: Student) {
  const gender = student?.gender?.trim().toLocaleLowerCase();
  return gender === "m" || gender === "male" || gender === "男"
    ? "gender-male"
    : gender === "f" || gender === "female" || gender === "女"
      ? "gender-female"
      : "";
}
function studentGenderLabel(student?: Student) {
  return studentGenderClass(student) === "gender-male"
    ? i18n.t("rosterAdmin:male")
    : studentGenderClass(student) === "gender-female"
      ? i18n.t("rosterAdmin:female")
      : "";
}

export default function TeacherConsole(props: ConsoleProps) {
  const navigationScope = props.preview
    ? undefined
    : `${props.localMode ? "local" : "cloud"}:${props.localAccount?.workspaceId || props.user?.id || "local"}`;
  const navigationSession = useMemo(() => createNavigationSession(navigationScope), [navigationScope]);
  return (
    <WorkspaceNavigationContext.Provider value={navigationSession}>
      <TeacherWorkspace key={navigationScope || "demo"} {...props} />
    </WorkspaceNavigationContext.Provider>
  );
}

function TeacherWorkspace({
  user,
  preview,
  localMode = false,
  localAccount,
  assertWritable,
  syncControl,
  backupControl,
  guidancePaused = false,
  onInitialClassSetupComplete,
  onExitPreview,
  onSignOut,
}: ConsoleProps) {
  const navigationSession = useContext(WorkspaceNavigationContext);
  const localPreferenceKey = (key: string) => (localAccount ? `${key}:${localAccount.workspaceId}` : key);
  const { t: termText } = useTranslation("terms");
  const { t: assignmentText } = useTranslation("assignmentRecords");
  const { t: interfaceText } = useTranslation("classroomInterface");
  const { t: yearText } = useTranslation("schoolYears");
  const { t: brandText } = useTranslation("brand");
  const { t: authText } = useTranslation("auth");
  const { t: updateText } = useTranslation("pwaInstall");
  const cloud = useWorkspaceData(preview || localMode ? null : user);
  const cloudActions = useWorkspaceActions(cloud.workspaceId);
  const local = useLocalWorkspace(localMode, assertWritable, localAccount);
  // Cover immediate writes as well as editors with a separate draft queue.
  useSaveBeforeUnload(hasPendingRecordSaves);
  const { t: workText } = useTranslation("personalTools");
  const [signOutState, setSignOutState] = useState<"waiting" | "signingOut" | null>(null);
  const [signOutError, setSignOutError] = useState("");
  const signOutRunning = useRef(false);
  // Display the last signed-in identity during recovery after an SDK logout
  // failure; permission checks still use the live Auth session separately.
  const sessionUser = useRef(user);
  if (user) sessionUser.current = user;
  const signOutDialogRef = useDialogFocus(
    Boolean(signOutState),
    () => {},
    () => document.querySelector<HTMLButtonElement>("[data-session-account-trigger]"),
  );
  async function leaveSession() {
    if (preview || (localMode && !sessionUser.current)) {
      onExitPreview();
      return;
    }
    if (signOutRunning.current) return;
    signOutRunning.current = true;
    setSignOutError("");
    setSignOutState("waiting");
    try {
      await waitForLocalSaves();
      if (document.querySelector('[data-save-state="dirty"]') && !window.confirm(authText("signOutUnsaved"))) return;
      setSignOutState("signingOut");
      try {
        await onSignOut();
      } catch {
        throw new Error(authText("signOutFailed"));
      }
    } catch (caught) {
      setSignOutError(caught instanceof Error ? caught.message : authText("signOutFailed"));
    } finally {
      signOutRunning.current = false;
      setSignOutState(null);
    }
  }
  const sessionActions = (
    <div className="session-account-actions">
      <div className="session-account-controls">
        <SessionAccountMenu>
          {sessionUser.current?.email && <small className="session-email">{sessionUser.current.email}</small>}
          <a href="/support" target="_blank" rel="noopener noreferrer">
            {authText("supportLink")}
          </a>
          <button type="button" disabled={Boolean(signOutState)} onClick={() => void leaveSession()}>
            {signOutState === "waiting"
              ? authText("signOutWaiting")
              : signOutState === "signingOut"
                ? authText("signOutBusy")
                : sessionUser.current
                  ? authText("signOut")
                  : localMode
                    ? i18n.t("screenCopy:teacherConsole.leaveLocalTrial")
                    : preview
                      ? i18n.t("screenCopy:teacherConsole.leavePreview")
                      : authText("signOut")}
          </button>
        </SessionAccountMenu>
        <LanguageSwitcher />
      </div>
      <small
        className="session-app-version"
        title={updateText("currentVersion", { build: `${__APP_RELEASE_TAG__} (${__APP_BUILD_ID__})` })}
      >
        {updateText("versionLabel", { build: __APP_RELEASE_TAG__ })}
      </small>
      {signOutError && (
        <p className="form-error" role="alert">
          {signOutError}
        </p>
      )}
      {signOutState &&
        createPortal(
          <div className="modal-backdrop">
            <section
              ref={signOutDialogRef}
              className="paper-card session-signout-progress"
              role="dialog"
              aria-modal="true"
              aria-label={authText("signOut")}
              tabIndex={-1}
            >
              <p role="status">{authText(signOutState === "waiting" ? "signOutWaiting" : "signOutBusy")}</p>
            </section>
          </div>,
          document.body,
        )}
    </div>
  );
  const recordActions = localMode ? local.actions : cloudActions;
  const isSchoolYearDemo = preview && new URLSearchParams(location.search).get("scenario") === "school-year";
  const [demo, setDemo] = useState(() => ({
    workspace: ensureDateSchoolYear(demoWorkspace, hongKongDate()),
    hiddenTools: [] as string[],
    classes: demoClasses,
    students: demoStudents,
    dailyRecords: demoDailyRecords,
    homeworkAssignments: demoHomeworkAssignments,
    homeworkRecords: demoHomeworkRecords,
    calendarEvents: demoEvents,
    homeroomRecords: [] as HomeroomRecord[],
    attendanceRecords: demoAttendanceRecords,
    homeroomHomeworkRecords: demoHomeroomHomeworkRecords,
    studentRoutineItems: demoStudentRoutineItems,
    seatingCharts: demoSeatingCharts,
    quizzes: demoQuizzes,
    quizSessions: [] as QuizSession[],
    quizResponses: [] as QuizResponse[],
    ...(isSchoolYearDemo ? createSchoolYearDemo(Number(demoWorkspace.academicTerms?.[0]?.startDate.slice(0, 4))) : {}),
  }));
  const [demoYearId, setDemoYearId] = useState<string | undefined>(
    () => schoolYearForDate(demoWorkspace, hongKongDate())?.id,
  );
  const storedSource = localMode ? local.data : preview ? demo : cloud;
  const workspace = storedSource.workspace;
  const workspaceLoading = localMode ? local.data.loading : !preview && cloud.loading;
  useEffect(() => {
    // Restore only the page mounted at startup. Later navigation keeps each
    // feature's normal entry point instead of reopening an old detail page.
    if (navigationSession && workspace && !workspaceLoading) navigationSession.restoring = false;
  }, [navigationSession, workspace, workspaceLoading]);
  const years = workspace ? schoolYears(workspace) : [];
  const currentYearId = workspace
    ? schoolYearForDate(workspace, hongKongDate())?.id || workspace.currentSchoolYearId || years[0]?.id || ""
    : "";
  const selectedYearId = (localMode ? local.selectedYearId : demoYearId) || currentYearId;
  const selectedYear = years.find((year) => year.id === selectedYearId) || years[0];
  const readOnlyYear = selectedYear?.status === "archived";
  const readOnlyYearRef = useRef(readOnlyYear);
  readOnlyYearRef.current = readOnlyYear;
  const yearSource = preview && selectedYear ? schoolYearView(demo, selectedYear.id) : storedSource;
  const source = {
    ...yearSource,
    homeworkAssignments: calendarAssignments(yearSource.calendarEvents, yearSource.homeworkAssignments),
  };
  const displayCalendarEvents = useMemo(
    () => calendarWithPublicHolidays(source.calendarEvents, workspace?.id || ""),
    [source.calendarEvents, workspace?.id],
  );
  const academicTerms = workspace && selectedYear ? schoolYearTerms(workspace, selectedYear.id) : [];
  const [selectedTermId, setSelectedTermId] = useNavigationState<string | undefined>(
    "selectedTerm",
    undefined,
    (value): value is string | undefined => value === undefined || navigationId(value),
  );
  const [homeroomTermView, setHomeroomTermView] = useNavigationState("homeroomTermView", false, navigationBoolean);
  const selectedTerm = readOnlyYear ? undefined : academicTerms.find((term) => dateInTerm(today(), term));
  const termSource = scopeStudentRecords(source, selectedTerm);
  const courseworkTerm =
    academicTerms.find((term) => term.id === selectedTermId) ||
    selectedTerm ||
    (readOnlyYear ? academicTerms[0] : undefined);
  const courseworkSource = scopeStudentRecords(source, courseworkTerm);
  // The agenda follows current work even after a teacher browses a future term.
  const agendaCourseworkTerm = selectedTerm || courseworkTerm;
  const agendaCourseworkSource = selectedTerm ? termSource : courseworkSource;
  const defaultRecordDate = selectedTerm
    ? today() < selectedTerm.startDate
      ? selectedTerm.startDate
      : today() > selectedTerm.endDate
        ? selectedTerm.endDate
        : today()
    : selectedYear?.startDate && today() < selectedYear.startDate
      ? selectedYear.startDate
      : selectedYear?.endDate && today() > selectedYear.endDate
        ? selectedYear.endDate
        : today();
  function selectTerm(termId: string) {
    if (document.querySelector('[data-save-state="saving"]')) {
      setToast(termText("busy"));
      return;
    }
    if (document.querySelector('[data-save-state="dirty"]') && !window.confirm(termText("unsaved"))) return;
    setSelectedTermId(termId);
  }
  function manageTerms() {
    setAdminInitialSection("terms");
    moveTo("roster");
  }
  function assertRecordDate(date: string) {
    if (selectedYear && !selectedYear.legacy && (date < selectedYear.startDate! || date > selectedYear.endDate!))
      throw new Error(yearText("invalid"));
    const parsed = new Date(`${date}T00:00:00Z`);
    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(date) ||
      !Number.isFinite(parsed.getTime()) ||
      parsed.toISOString().slice(0, 10) !== date
    )
      throw new Error(termText("errors.invalidDate"));
  }

  const localTourReplay =
    localMode && import.meta.env.DEV && ["127.0.0.1", "localhost", "::1"].includes(location.hostname);
  const billingRoute = location.pathname.startsWith("/settings/billing");
  const [activeTab, setActiveTab] = useNavigationState<Tab>(
    "activeTab",
    billingRoute ? "billing" : "today",
    navigationChoice(billingRoute ? ["billing"] : nav.map((item) => item.id)),
  );
  const [classPickerOpen, setClassPickerOpen] = useNavigationState(
    "classPickerOpen",
    !location.pathname.startsWith("/settings"),
    (value): value is boolean => navigationBoolean(value) && (!billingRoute || !value),
  );
  const [guideOpen, setGuideOpen] = useState(false);
  const [classSetupOpen, setClassSetupOpen] = useState(
    localMode && new URLSearchParams(location.search).get("setup") === "1",
  );
  const [classSetupDismissed, setClassSetupDismissed] = useState(false);
  const [tourStep, setTourStep] = useState<TourStep | null>(null);
  const [continuingSetup, setContinuingSetup] = useState(false);
  const [setupTourDismissed, setSetupTourDismissed] = useState(false);
  const [setupFollowUpClassId, setSetupFollowUpClassId] = useState("");
  const { t: setupStatusText } = useTranslation("classSetupStatus");
  const [demoTourStep, setDemoTourStep] = useState<DemoTourStep | null>(null);
  const tourStepRef = useRef<TourStep | null>(null);
  const [tourClassId, setTourClassId] = useState("");
  const [tourCreatedName, setTourCreatedName] = useState("");
  const [adminInitialSection, setAdminInitialSection] = useNavigationState<AdminSection>(
    "adminSection",
    "classes",
    navigationChoice([
      "classes",
      "history",
      "export",
      "calendar",
      "tools",
      "terms",
      ...(QUIZ_RELEASED ? ["quizzes" as const] : []),
    ]),
  );
  const [rosterImportMethod, setRosterImportMethod] = useState<RosterImportMethod>("excel");
  const [rosterImportRequested, setRosterImportRequested] = useState(false);
  const [viewHistory, setViewHistory] = useState<
    Array<{
      tab: Tab;
      classId: string;
      picker: boolean;
      recordMode: RecordMode;
    }>
  >([]);
  const [requestedRecordMode, setRecordMode] = useNavigationState<RecordMode>(
    "recordMode",
    "performance",
    navigationChoice(["materials", "answers", "assignments", "grades", "gradeTable", "performance"]),
  );
  const recordMode: RecordMode =
    source.hiddenTools.includes("homework") && ["grades", "gradeTable", "assignments"].includes(requestedRecordMode)
      ? "performance"
      : requestedRecordMode;
  const isCoursework = recordMode === "grades" || recordMode === "gradeTable" || recordMode === "assignments";
  const recordTabs = isCoursework ? courseworkTabs : performanceTabs;
  const recordGroupLabel = isCoursework
    ? i18n.t("adminRecords:kinds.coursework")
    : i18n.t("guidedTour:steps.demoPerformance.title");
  const assignmentNavigation = useRef<AssignmentNavigation>(null);
  const { t: gradeText } = useTranslation("gradeOverview");
  const [tableTarget, setTableTarget] = useState<{ id?: string; request: number }>({ request: 0 });
  const [courseworkFollowupRequest, setCourseworkFollowupRequest] = useState(0);
  const [recordMenuOpen, setRecordMenuOpen] = useState(false);
  const [termComparisonOpen, setTermComparisonOpen] = useState(false);
  const sidebarLayout = useSidebarLayout(
    preview ? "demo" : `teacher:${localAccount?.workspaceId || user?.id || "local"}`,
  );
  const [sidebarMenuOpen, setSidebarMenuOpen] = useState(false);
  const [calendarFocusDate, setCalendarFocusDate] = useState<string>();
  const [calendarFocusEvent, setCalendarFocusEvent] = useState<CalendarEvent>();
  const [selectedClassId, setSelectedClassId] = useNavigationState(
    "selectedClass",
    () => {
      try {
        return localMode ? localStorage.getItem(localPreferenceKey("classroom-local-selected-class")) || "" : "";
      } catch {
        return "";
      }
    },
    navigationId,
  );
  const [toast, setToast] = useState("");
  const { t: backupText } = useTranslation("quickBackup");
  const quickBackup = useQuickBackup({
    enabled: preview || localMode,
    preview,
    assertSaved: () => {
      if (document.querySelector('[data-save-state="dirty"], [data-save-state="saving"]'))
        throw new Error(backupText("saveFirst"));
    },
    createBackup: async (password, signal) => {
      if (localMode)
        return createEncryptedBackup(
          local.actions.exportSnapshot,
          password,
          crypto.randomUUID(),
          signal,
          () => undefined,
        );
      if (!preview) throw new Error(backupText("failed"));
      // Demo is a logical snapshot of in-memory fixtures; it never opens the local database.
      return encryptSnapshot(
        {
          workspace: { ...demo.workspace },
          hiddenTools: [...demo.hiddenTools],
          records: COLLECTIONS.flatMap((collection) =>
            collection === "quizAttempts" || isRetiredCollection(collection)
              ? []
              : demo[collection].map((record) => ({ collection, recordId: record.id, data: { ...record } })),
          ),
          selectedSchoolYearId: demoYearId,
        },
        password,
      );
    },
    onMessage: setToast,
  });
  const [exportRequest, setExportRequest] = useState<{
    tab: RecordExportTab;
    studentId?: string;
    scope?: RecordExportScope;
    recordTerm?: AcademicTerm | null;
  } | null>(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(preview || localMode);
  const [legalLoading, setLegalLoading] = useState(!preview && !localMode);
  const [legalSaving, setLegalSaving] = useState(false);
  // Keep the feature entry beside its content while the Demo tour is showing.
  const navigationMode = demoTourStep ? "full" : sidebarLayout.mode;
  const sidebarDrawerMode = navigationMode !== "full";
  const sidebarDrawerOpen = sidebarMenuOpen;
  const exportDialogRef = useDialogFocus<HTMLElement>(
    Boolean(exportRequest && exportRequest.scope?.intent !== "print"),
    () => {
      if (!exportBusy) setExportRequest(null);
    },
  );
  const calendarSidebarRef = useDialogFocus<HTMLElement>(
    sidebarDrawerMode && sidebarDrawerOpen,
    () => setSidebarMenuOpen(false),
    () => document.querySelector<HTMLButtonElement>("[data-sidebar-toggle], [data-demo-nav-trigger]"),
  );

  const stopTour = useCallback(
    (completed = false) => {
      tourStepRef.current = null;
      setTourStep(null);
      setTourClassId("");
      setTourCreatedName("");
      setGuideOpen(false);
      setContinuingSetup(false);
      setSetupTourDismissed(true);
      try {
        localStorage.setItem(localPreferenceKey(LOCAL_TOUR_STATUS_KEY), completed ? "completed" : "dismissed");
        localStorage.setItem(localPreferenceKey(LOCAL_ROSTER_TOUR_STATUS_KEY), completed ? "completed" : "dismissed");
      } catch {
        /* Private browsing can deny localStorage. */
      }
      if (completed) {
        setSetupFollowUpClassId(selectedClassId);
        setToast(interfaceText("setupTutorialComplete"));
      }
    },
    [interfaceText, selectedClassId],
  );

  useEffect(() => {
    tourStepRef.current = tourStep;
  }, [tourStep]);

  useEffect(() => {
    if (readOnlyYear || !localMode || classSetupOpen || local.data.loading || local.data.error || source.classes.length)
      return;
    if (location.pathname.startsWith("/app")) {
      let hasChosenManualSetup = false;
      try {
        hasChosenManualSetup = Boolean(localStorage.getItem(localPreferenceKey(LOCAL_TOUR_STATUS_KEY)));
      } catch {
        // The current tab still remembers a manual choice if localStorage is unavailable.
      }
      if (!classSetupDismissed && !hasChosenManualSetup) setClassSetupOpen(true);
      return;
    }
    try {
      if (localStorage.getItem(localPreferenceKey(LOCAL_TOUR_STATUS_KEY))) return;
    } catch {
      /* Continue with an in-memory tour. */
    }
    setGuideOpen(true);
    setTourStep((current) => current || "className");
  }, [localMode, classSetupOpen, local.data.loading, local.data.error, source.classes.length, classSetupDismissed]);

  useEffect(() => {
    if (
      readOnlyYear ||
      !localMode ||
      classSetupOpen ||
      !classPickerOpen ||
      local.data.loading ||
      local.data.error ||
      setupTourDismissed ||
      tourStep
    )
      return;
    try {
      // The old wizard marked its class-creation tour dismissed on completion.
      // Remember the roster/seating choice separately so those users can continue.
      if (localStorage.getItem(localPreferenceKey(LOCAL_ROSTER_TOUR_STATUS_KEY))) return;
    } catch {
      // The current tab retains the user's choice if localStorage is unavailable.
    }
    const needsSetup = (item: Classroom) =>
      item.active && !setupProgress(item, source.classes, source.students, source.seatingCharts).hasSeating;
    const incomplete =
      source.classes.find((item) => item.id === selectedClassId && needsSetup(item)) || source.classes.find(needsSetup);
    if (!incomplete) return;
    setTourClassId(incomplete.id);
    setTourCreatedName("");
    setContinuingSetup(true);
    setGuideOpen(true);
    setTourStep(
      setupProgress(incomplete, source.classes, source.students, source.seatingCharts).hasRoster
        ? "seatingLink"
        : "rosterLink",
    );
  }, [
    localMode,
    classSetupOpen,
    classPickerOpen,
    local.data.loading,
    local.data.error,
    setupTourDismissed,
    tourStep,
    source.classes,
    source.students,
    source.seatingCharts,
    selectedClassId,
  ]);

  function startTour(fromBeginning = false) {
    if (!localMode || readOnlyYear) return;
    const incomplete = fromBeginning
      ? undefined
      : source.classes.find(
          (item) =>
            item.active && !setupProgress(item, source.classes, source.students, source.seatingCharts).hasSeating,
        );
    setTourClassId(incomplete?.id || "");
    setTourCreatedName("");
    setGuideOpen(true);
    setClassPickerOpen(true);
    setTourStep(
      !incomplete
        ? "className"
        : setupProgress(incomplete, source.classes, source.students, source.seatingCharts).hasRoster
          ? "seatingLink"
          : "rosterLink",
    );
  }

  function nextTourStep() {
    if (tourStep === "className") setTourStep("classRole");
    else if (tourStep === "classRole") setTourStep("classSubmit");
    else if (tourStep === "rosterInput") setTourStep("rosterSubmit");
    else if (tourStep === "seatingNav") {
      moveTo("seating", selectedClassId);
      setTourStep("seatingStudent");
    }
  }

  function previousTourStep() {
    if (tourStep === "rosterSubmit") setTourStep("rosterInput");
  }

  const stopDemoTour = useCallback((completed = false) => {
    setDemoTourStep(null);
    if (completed) setToast(i18n.t("screenCopy:teacherConsole.featureTourCompleteYouCanNowEdit"));
  }, []);

  function showDemoTourStep(step: DemoTourStep) {
    if (!preview) return;
    const destination = demoTourDestinations[step];
    setDemoTourStep(step);
    setRecordMenuOpen(destination.tab === "records");
    setSidebarMenuOpen(false);
    if (destination.picker) {
      setGuideOpen(false);
      setClassPickerOpen(true);
      return;
    }
    const targetClass = destination.role
      ? source.classes.find((item) => item.active && item.classRole === destination.role)
      : source.classes.find((item) => item.id === selectedClassId) || source.classes[0];
    if (targetClass) setSelectedClassId(targetClass.id);
    if (destination.recordMode) setRecordMode(destination.recordMode);
    if (destination.adminSection) setAdminInitialSection(destination.adminSection);
    if (destination.tab) setActiveTab(destination.tab);
    setClassPickerOpen(false);
  }

  function nextDemoTourStep() {
    if (!demoTourStep) return;
    const index = DEMO_TOUR_SEQUENCE.indexOf(demoTourStep);
    const next = DEMO_TOUR_SEQUENCE[index + 1];
    if (next) showDemoTourStep(next);
    else stopDemoTour(true);
  }

  function previousDemoTourStep() {
    if (!demoTourStep) return;
    const index = DEMO_TOUR_SEQUENCE.indexOf(demoTourStep);
    const previous = DEMO_TOUR_SEQUENCE[index - 1];
    if (previous) showDemoTourStep(previous);
  }

  useEffect(() => {
    if (!preview) return;
    if (import.meta.env.DEV) {
      const missing = nav.filter(
        (item) => item.id !== "quiz" && !demoTourTabSteps[item.id as Exclude<Tab, "quiz" | "homework">],
      );
      if (missing.length)
        console.warn(
          i18n.t("screenCopy:teacherConsole.demoFeatureTourDoesNotCover"),
          missing.map((item) => item.id).join(", "),
        );
    }
    showDemoTourStep("demoWelcome");
    // Start the tour only when entering Demo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  useEffect(() => {
    if (!workspaceLoading && workspace && !source.classes.some((item) => item.id === selectedClassId)) {
      setSelectedClassId(source.classes[0]?.id || "");
      // Backup restore and year changes can replace the current class while a
      // global page is showing its result. Keep that page mounted.
      if (selectedClassId && !["today", "roster", "billing"].includes(activeTab)) setClassPickerOpen(true);
    }
  }, [source.classes, workspace, workspaceLoading, selectedClassId, activeTab, setSelectedClassId, setClassPickerOpen]);
  useEffect(() => {
    if (localMode && selectedClassId) {
      try {
        localStorage.setItem(localPreferenceKey("classroom-local-selected-class"), selectedClassId);
      } catch {
        // Session navigation still works when localStorage is unavailable.
      }
    }
  }, [localMode, selectedClassId]);
  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);
  useEffect(() => {
    setSidebarMenuOpen(false);
  }, [activeTab, sidebarLayout.width, navigationMode]);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [activeTab, selectedClassId]);
  useEffect(() => {
    if (preview || localMode) {
      setLegalAccepted(true);
      setLegalLoading(false);
      return;
    }
    if (!cloud.workspaceId) return;
    let active = true;
    setLegalLoading(true);
    cloudActions
      .getLegalAcceptance()
      .then((accepted) => {
        if (active) setLegalAccepted(accepted);
      })
      .catch(() => {
        if (active) setLegalAccepted(false);
      })
      .finally(() => {
        if (active) setLegalLoading(false);
      });
    return () => {
      active = false;
    };
  }, [preview, localMode, cloud.workspaceId, cloudActions]);
  const selectedClass = source.classes.find((item) => item.id === selectedClassId);
  const allowedNav = nav.filter(
    (item) =>
      (item.id !== "quiz" || (QUIZ_RELEASED && !localMode)) &&
      (!item.roles || (selectedClass && item.roles.includes(selectedClass.classRole))),
  );
  const hiddenTools = source.hiddenTools.filter((item) => configurableIds.has(item as Tab));
  const visibleNav = allowedNav
    .filter((item) => item.id !== "today" && !hiddenTools.includes(item.id))
    .map((item) =>
      item.id === "billing" && (localMode || preview)
        ? {
            ...item,
            label: localMode ? i18n.t("screenCopy:teacherConsole.localData") : i18n.t("personalTools:demoMode"),
          }
        : item,
    );
  const classSwitcher = (
    <ClassSwitcher
      value={selectedClassId}
      items={source.classes
        .filter((item) => item.active)
        .map((item) => ({ id: item.id, name: item.name, role: item.classRole }))}
      onChange={(id) => {
        const next = source.classes.find((item) => item.id === id);
        if (next) chooseClass(next);
      }}
    />
  );
  function chooseSidebarMode(mode: SidebarMode) {
    setSidebarMenuOpen(false);
    sidebarLayout.choose(mode);
    requestAnimationFrame(() => {
      document.querySelector<HTMLButtonElement>("[data-sidebar-toggle]")?.focus();
    });
  }
  const students = classStudents(selectedClass, source.classes, source.students);
  const effectiveCharts = source.classes.reduce<SeatingChart[]>((result, classItem) => {
    const chart = classSeatingChart(classItem, source.classes, source.seatingCharts);
    if (chart) result.push({ ...chart, id: classItem.id, classId: classItem.id });
    return result;
  }, []);
  const context = {
    workspace: workspace!,
    workspaceId: workspace?.id || "",
    classItem: selectedClass,
    students,
    preview,
    toast: setToast,
    recordTerm: selectedTerm,
    defaultRecordDate,
    readOnly: readOnlyYear,
  };

  async function moveTo(tab: Tab, classId = selectedClassId, picker = false, focusEvent?: CalendarEvent) {
    setSidebarMenuOpen(false);
    if (tab === activeTab && classId === selectedClassId && picker === classPickerOpen) return;
    if (document.querySelector(".school-calendar-editor") && !canLeaveClass()) return;
    setSetupFollowUpClassId("");
    setViewHistory((current) => [
      ...current,
      { tab: activeTab, classId: selectedClassId, picker: classPickerOpen, recordMode },
    ]);
    setSelectedClassId(classId);
    setCourseworkFollowupRequest(0);
    setActiveTab(tab);
    setClassPickerOpen(picker);
    setRecordMenuOpen(false);
    setSidebarMenuOpen(false);
    setCalendarFocusEvent(focusEvent);
  }

  function openRecordMode(mode: typeof recordMode) {
    if (activeTab === "records" && !classPickerOpen && mode !== recordMode) {
      setViewHistory((current) => [
        ...current,
        { tab: activeTab, classId: selectedClassId, picker: classPickerOpen, recordMode },
      ]);
    }
    setRecordMode(mode);
    openTool("records");
    setRecordMenuOpen(true);
  }

  async function goBack() {
    if (document.querySelector(".school-calendar-editor") && !canLeaveClass()) return;
    if (
      activeTab === "records" &&
      recordMode === "assignments" &&
      assignmentNavigation.current?.goBack(returnToPreviousView)
    )
      return;
    if ((document.querySelector("[data-autosave]") || hasPendingLocalWrites()) && !canLeaveClass()) return;
    returnToPreviousView();
  }

  function returnToPreviousView() {
    const previous = viewHistory.at(-1);
    if (!previous) {
      setClassPickerOpen(true);
      return;
    }
    setViewHistory((current) => current.slice(0, -1));
    setSelectedClassId(previous.classId);
    setCourseworkFollowupRequest(0);
    setActiveTab(previous.tab);
    setRecordMode(previous.recordMode);
    setClassPickerOpen(previous.picker);
    setRecordMenuOpen(false);
    setSidebarMenuOpen(false);
    setCalendarFocusEvent(undefined);
  }

  function canLeaveClass() {
    if (hasPendingRecordSaves()) {
      setToast(setupStatusText("waitForSave"));
      return false;
    }
    return (
      !document.querySelector('[data-save-state="dirty"], [data-save-state="error"]') ||
      window.confirm(setupStatusText("leaveUnsaved"))
    );
  }

  function openClassPicker() {
    if (canLeaveClass()) moveTo(activeTab, selectedClassId, true);
  }

  function openClassRoster(classId: string) {
    if (!canLeaveClass()) return false;
    setRosterImportMethod("csv");
    setRosterImportRequested(true);
    setAdminInitialSection("classes");
    moveTo("roster", classId);
    return true;
  }

  function chooseClass(classItem: Classroom) {
    if ((classPickerOpen || classItem.id !== selectedClassId) && !canLeaveClass()) return;
    const nextTab =
      activeTab === "today" || activeTab === "billing"
        ? classItem.classRole === "homeroom"
          ? "attendance"
          : "records"
        : nav.find((item) => item.id === activeTab)?.roles?.includes(classItem.classRole) === false
          ? classItem.classRole === "homeroom"
            ? "attendance"
            : "records"
          : activeTab;
    moveTo(nextTab, classItem.id);
  }

  function openTool(tab: Tab) {
    if (tab !== activeTab && (document.querySelector("[data-autosave]") || hasPendingLocalWrites()) && !canLeaveClass())
      return;
    if (tab === "roster") {
      setRosterImportRequested(false);
      if (activeTab !== "roster") setAdminInitialSection("classes");
    }
    const item = nav.find((entry) => entry.id === tab);
    const targetClass =
      item?.roles && (!selectedClass || !item.roles.includes(selectedClass.classRole))
        ? source.classes.find((classItem) => item.roles!.includes(classItem.classRole))
        : selectedClass;
    moveTo(tab, targetClass?.id || selectedClassId);
    if (localMode && tourStep === "seatingNav" && tab === "seating") setTourStep("seatingStudent");
  }

  useEffect(() => {
    if (workspaceLoading || !workspace) return;
    if (!allowedNav.some((item) => item.id === activeTab) || hiddenTools.includes(activeTab)) {
      setActiveTab("today");
      setClassPickerOpen(true);
    }
  }, [
    selectedClass?.classRole,
    activeTab,
    allowedNav,
    hiddenTools,
    workspace,
    workspaceLoading,
    setActiveTab,
    setClassPickerOpen,
  ]);

  if (!workspace && !preview) {
    return (
      <main className="loading-screen">
        <BrandIcon className="brand-loader" />
        <p>
          {(localMode ? local.data.error : cloud.error) || i18n.t("screenCopy:teacherConsole.preparingYourClassroom")}
        </p>
      </main>
    );
  }
  if (!preview && !localMode && legalLoading) {
    return (
      <main className="loading-screen">
        <span className="loader-mark">私</span>
        <p>{i18n.t("screenCopy:teacherConsole.checkingPrivacyAndTermsAcceptance")}</p>
      </main>
    );
  }
  if (!preview && !localMode && !legalAccepted) {
    return (
      <LegalAcceptanceGate
        busy={legalSaving}
        onAccept={async (profile) => {
          setLegalSaving(true);
          try {
            await cloudActions.acceptLegalTerms(profile);
            setLegalAccepted(true);
          } finally {
            setLegalSaving(false);
          }
        }}
      />
    );
  }

  const actions = {
    saveAcademicTerms: async (terms: AcademicTerm[]) => {
      if (
        workspace?.schoolYears &&
        !selectedYear?.legacy &&
        terms.some((term) => term.schoolYear !== selectedYear?.name)
      )
        throw new Error(yearText("invalid"));
      const issue = validateAcademicTerms(terms);
      if (issue) throw new Error(termText(`errors.${issue}`));
      if (preview)
        setDemo((current) => ({
          ...current,
          workspace: {
            ...current.workspace,
            academicTerms: current.workspace.schoolYears
              ? [
                  ...(current.workspace.academicTerms || []).filter(
                    (term) => !academicTerms.some((old) => old.id === term.id),
                  ),
                  ...terms,
                ]
              : terms,
            updatedAt: isoNow(),
          },
        }));
      else if (localMode) await local.actions.saveAcademicTerms(terms);
      else throw new Error(termText("errors.cloud"));
    },
    addClass: async (name: string, role: Classroom["classRole"], sharedDataClassId?: string) => {
      const className = name.toLocaleUpperCase();
      if (sharedDataClassId && !source.classes.some((item) => item.id === sharedDataClassId && !item.sharedDataClassId))
        throw new Error(i18n.t("screenCopy:teacherConsole.sharedClassNotFoundSelectItAgain"));
      if (preview) {
        const now = isoNow();
        setDemo((current) => ({
          ...current,
          classes: [
            ...current.classes,
            {
              id: id("class"),
              schoolYearId: selectedYearId,
              workspaceId: current.workspace.id,
              name: className,
              classRole: role,
              sharedDataClassId,
              active: true,
              createdAt: now,
              updatedAt: now,
            },
          ],
        }));
      } else await recordActions.addClass(className, role, sharedDataClassId);
    },
    deleteEmptyClass: async (classId: string, confirmedName: string) => {
      if (!localMode) throw new Error("local-only");
      await local.actions.deleteEmptyClass(classId, confirmedName);
      setSelectedClassId("");
      setClassPickerOpen(true);
    },
    addStudent: async (classId: string, row: RosterRow): Promise<Student> => {
      if (preview) {
        const classroom = source.classes.find((item) => item.id === classId && item.active && !item.sharedDataClassId);
        if (!classroom) throw new Error("invalid-roster-class");
        const validated = validateNewStudent(
          row,
          source.students.filter((student) => student.classId === classId),
        );
        const now = isoNow();
        const student: Student = {
          ...validated,
          id: id("student"),
          workspaceId: source.workspace!.id,
          classId,
          active: true,
          createdAt: now,
          updatedAt: now,
        };
        setDemo((current) => ({ ...current, students: [...current.students, student] }));
        return student;
      }
      if (localMode) return local.actions.addStudent(classId, row);
      throw new Error("invalid-roster-class");
    },
    importStudents: async (classId: string, rows: ReturnType<typeof parseRosterText>) => {
      if (preview) {
        const now = isoNow();
        setDemo((current) => ({
          ...current,
          students: [
            ...current.students.filter(
              (student) =>
                student.classId !== classId || !rows.some((row) => studentRosterKey(row) === studentRosterKey(student)),
            ),
            ...rows.map((row) => {
              const previous = current.students.find(
                (student) => student.classId === classId && studentRosterKey(student) === studentRosterKey(row),
              );
              return {
                ...previous,
                ...row,
                id: previous?.id || id("student"),
                workspaceId: current.workspace.id,
                classId,
                active: true,
                createdAt: previous?.createdAt || now,
                updatedAt: now,
              } as Student;
            }),
          ],
        }));
      } else await recordActions.importStudents(classId, rows);
    },
    updateStudent: async (studentId: string, patch: Partial<Student>) => {
      if (preview)
        setDemo((current) => ({
          ...current,
          students: current.students.map((student) => (student.id === studentId ? { ...student, ...patch } : student)),
        }));
      else await recordActions.updateStudent(studentId, patch);
    },
    deleteStudent: async (studentId: string) => {
      if (!preview) {
        await recordActions.deleteStudentData(studentId);
        return;
      }
      setDemo((current) => ({
        ...current,
        students: current.students.filter((item) => item.id !== studentId),
        dailyRecords: current.dailyRecords.map((item) => ({
          ...item,
          students: Object.fromEntries(Object.entries(item.students).filter(([id]) => id !== studentId)),
        })),
        homeworkRecords: current.homeworkRecords.map((item) => ({
          ...item,
          students: Object.fromEntries(Object.entries(item.students).filter(([id]) => id !== studentId)),
        })),
        attendanceRecords: current.attendanceRecords.map((item) => ({
          ...item,
          studentIds: item.studentIds.filter((id) => id !== studentId),
          exceptions: Object.fromEntries(Object.entries(item.exceptions).filter(([id]) => id !== studentId)),
        })),
        homeroomHomeworkRecords: current.homeroomHomeworkRecords.map((item) => ({
          ...item,
          missingStudentIds: item.missingStudentIds.filter((id) => id !== studentId),
        })),
        studentRoutineItems: current.studentRoutineItems.map((item) => ({
          ...item,
          studentIds: item.studentIds.filter((id) => id !== studentId),
        })),
        seatingCharts: current.seatingCharts.map((item) => ({
          ...item,
          seats: Object.fromEntries(Object.entries(item.seats).filter(([, id]) => id !== studentId)),
        })),
        homeroomRecords: current.homeroomRecords.map((item) => ({
          ...item,
          duties: Object.fromEntries(Object.entries(item.duties).filter(([, id]) => id !== studentId)),
        })),
        classes: current.classes.map((item) => ({
          ...item,
          dailyMemos: item.dailyMemos?.map((memo) => ({
            ...memo,
            studentIds: memo.studentIds.filter((id) => id !== studentId),
          })),
          drawState: item.drawState
            ? {
                ...item.drawState,
                drawnStudentIds: item.drawState.drawnStudentIds.filter((id) => id !== studentId),
                history: item.drawState.history?.filter((entry) => entry.studentId !== studentId),
              }
            : undefined,
          dutyAssignments: item.dutyAssignments
            ? Object.fromEntries(Object.entries(item.dutyAssignments).filter(([, id]) => id !== studentId))
            : undefined,
        })),
        quizResponses: current.quizResponses.filter((item) => item.studentId !== studentId),
      }));
    },
    updateClassSettings: async (
      classId: string,
      patch: Partial<Pick<Classroom, "customSubjects" | "customDuties" | "dutyAssignments" | "drawState">>,
    ) => {
      if (preview)
        setDemo((current) => ({
          ...current,
          classes: current.classes.map((item) =>
            item.id === classId ? { ...item, ...patch, updatedAt: isoNow() } : item,
          ),
        }));
      else await recordActions.updateClassSettings(classId, patch);
    },
    saveDrawAction: async (classId: string, action: DrawAction): Promise<DrawState> => {
      if (localMode && !preview) return local.actions.saveDrawAction(classId, action);
      const classroom = source.classes.find((item) => item.id === classId);
      if (!classroom) throw new Error(i18n.t("dailyMemos:missingClass"));
      const next = applyDrawAction(classroom, classStudents(classroom, source.classes, source.students), action);
      if (preview)
        setDemo((current) => ({
          ...current,
          classes: current.classes.map((item) =>
            item.id === classId ? { ...item, drawState: next, updatedAt: isoNow() } : item,
          ),
        }));
      else await recordActions.updateClassSettings(classId, { drawState: next });
      return next;
    },
    saveAssignmentFolderAction: async (classId: string, action: AssignmentFolderAction) => {
      if (localMode && !preview) return local.actions.saveAssignmentFolderAction(classId, action);
      if (preview) {
        const classroom = source.classes.find((item) => item.id === classId);
        if (!classroom) throw new Error(i18n.t("dailyMemos:missingClass"));
        applyAssignmentFolderAction(classroom, action);
        setDemo((current) => ({
          ...current,
          classes: current.classes.map((item) =>
            item.id === classId
              ? { ...item, assignmentFolders: applyAssignmentFolderAction(item, action), updatedAt: isoNow() }
              : item,
          ),
        }));
      } else {
        const classroom = source.classes.find((item) => item.id === classId);
        if (!classroom) throw new Error(i18n.t("dailyMemos:missingClass"));
        await cloudActions.updateClassSettings(classId, {
          assignmentFolders: applyAssignmentFolderAction(classroom, action),
        });
      }
    },
    saveDailyMemoAction: async (classId: string, action: DailyMemoAction) => {
      if (localMode && !preview) return local.actions.saveDailyMemoAction(classId, action);
      const classroom = source.classes.find((item) => item.id === classId);
      if (!classroom) throw new Error("missing-class");
      const dailyMemos = applyDailyMemoAction(classroom, action);
      if (preview)
        setDemo((current) => ({
          ...current,
          classes: current.classes.map((item) =>
            item.id === classId
              ? { ...item, dailyMemos: applyDailyMemoAction(item, action), updatedAt: isoNow() }
              : item,
          ),
        }));
      else await cloudActions.updateClassSettings(classId, { dailyMemos });
    },
    saveHiddenTools: async (ids: string[]) => {
      if (preview) setDemo((current) => ({ ...current, hiddenTools: ids }));
      else if (localMode) await local.actions.saveHiddenTools(ids);
      else await cloudActions.saveHiddenTools(user!.id, ids);
    },
    saveDailyAction: async (classId: string, date: string, action: DailyRecordAction) => {
      assertRecordDate(date);
      if (localMode && !preview) return local.actions.saveDailyRecordAction(classId, date, action);
      if (!preview) throw new Error(interfaceText("recordLocalOnly"));
      setDemo((current) => {
        const record = current.dailyRecords
          .filter((item) => item.classId === classId && item.date === date)
          .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0] || {
          id: `${classId}_${date}`,
          workspaceId: current.workspace!.id,
          classId,
          date,
          students: {},
          updatedAt: "",
        };
        const next = applyDailyRecordAction(record, action);
        return next === record
          ? current
          : { ...current, dailyRecords: upsert(current.dailyRecords, { ...next, updatedAt: isoNow() }) };
      });
    },
    copyAssignments: async (request: AssignmentCopyRequest) => {
      for (const target of request.targets) assertRecordDate(target.dueDate);
      if (localMode && !preview) return local.actions.copyTeachingAssignments(request);
      if (!preview) throw new Error(assignmentText("copy.failed"));
      const sourceClass = source.classes.find((item) => item.id === request.source.classId);
      if (!sourceClass) throw new Error("invalid-copy");
      const copies = assignmentCopies(request.source, sourceClass, source.classes, request.targets, request.createdAt);
      const events = copies.map((item) => calendarEventForAssignment(item));
      setDemo((current) => ({
        ...current,
        homeworkAssignments: [
          ...current.homeworkAssignments,
          ...copies.map((item, index) => ({ ...item, calendarEventId: events[index].id })),
        ],
        calendarEvents: [...current.calendarEvents, ...events],
      }));
    },
    saveAffairChange: async (classId: string, date: string, change: HomeroomAffairChange) => {
      assertRecordDate(date);
      if (localMode && !preview) return local.actions.saveHomeroomAffairChange(classId, date, change);
      if (!preview) throw new Error(i18n.t("homeroomNotebook:failed"));
      const current = source.homeroomRecords.find((item) => item.classId === classId && item.date === date) || {
        id: `${classId}_${date}`,
        workspaceId: workspace!.id,
        classId,
        date,
        affairs: [],
        duties: {},
        updatedAt: "",
      };
      const next = applyHomeroomAffairChange(current, change);
      setDemo((data) => ({ ...data, homeroomRecords: upsert(data.homeroomRecords, { ...next, updatedAt: isoNow() }) }));
    },
    saveAssignment: async (item: HomeworkAssignment) => {
      assertRecordDate(item.dueDate);
      const event = calendarEventForAssignment(
        item,
        source.calendarEvents.find((event) => event.id === item.calendarEventId),
      );
      const assignment = { ...item, calendarEventId: event.id };
      if (preview)
        setDemo((current) => ({
          ...current,
          homeworkAssignments: upsert(current.homeworkAssignments, assignment),
          calendarEvents: upsert(current.calendarEvents, event),
        }));
      else await recordActions.saveTeachingRecords([event], [assignment]);
    },
    saveHomework: async (item: HomeworkRecord) => {
      const assignment = source.homeworkAssignments.find((assignment) => assignment.id === item.assignmentId);
      if (!assignment) throw new Error(i18n.t("screenCopy:teacherConsole.matchingTaskNotFoundReopenTheRecord"));
      assertRecordDate(assignment.dueDate);
      if (preview)
        setDemo((current) => ({
          ...current,
          homeworkAssignments: upsert(current.homeworkAssignments, assignment),
          homeworkRecords: upsert(current.homeworkRecords, item),
        }));
      else await recordActions.saveTeachingRecords([], [assignment], [item]);
    },
    deleteAssignment: async (item: HomeworkAssignment, confirmationToken: string) => {
      assertRecordDate(item.dueDate);
      if (!preview) {
        if (!localMode) throw new Error(assignmentText("deleteUnavailable"));
        await local.actions.deleteTeachingAssignment(item.classId, item.id, confirmationToken);
        return;
      }
      const plan = assignmentDeletionPlan(
        item.classId,
        item.id,
        source.calendarEvents,
        source.homeworkAssignments,
        source.homeworkRecords,
      );
      if (plan.token !== confirmationToken) throw new Error(assignmentText("deleteChanged"));
      setDemo((current) => ({
        ...current,
        homeworkAssignments: current.homeworkAssignments.filter((assignment) => assignment.id !== item.id),
        homeworkRecords: current.homeworkRecords.filter((record) => record.assignmentId !== item.id),
        calendarEvents: current.calendarEvents.filter((event) => event.id !== plan.calendarEventId),
      }));
    },
    saveEvent: async (item: CalendarEvent) => {
      item = { ...item, schoolYearId: item.schoolYearId || selectedYearId };
      assertRecordDate(item.date);
      if (item.endDate) assertRecordDate(item.endDate);
      const assignments = calendarAssignments([item], source.homeworkAssignments).filter(
        (assignment) => assignment.calendarEventId === item.id,
      );
      if (preview)
        setDemo((current) => ({
          ...current,
          calendarEvents: upsert(current.calendarEvents, item),
          homeworkAssignments: [
            ...current.homeworkAssignments.filter(
              (assignment) => !assignments.some((next) => next.id === assignment.id),
            ),
            ...assignments,
          ],
        }));
      else await recordActions.saveTeachingRecords([item], assignments);
    },
    saveEvents: async (items: CalendarEvent[]) => {
      items = items.map((item) => ({ ...item, schoolYearId: item.schoolYearId || selectedYearId }));
      for (const item of items) {
        assertRecordDate(item.date);
        if (item.endDate) assertRecordDate(item.endDate);
      }
      const assignments = calendarAssignments(items, source.homeworkAssignments).filter((assignment) =>
        items.some((event) => event.id === assignment.calendarEventId),
      );
      if (preview)
        setDemo((current) => ({
          ...current,
          homeworkAssignments: [
            ...current.homeworkAssignments.filter(
              (assignment) => !assignments.some((next) => next.id === assignment.id),
            ),
            ...assignments,
          ],
          calendarEvents: [
            ...items,
            ...current.calendarEvents.filter((event) => !items.some((item) => item.id === event.id)),
          ],
        }));
      else await recordActions.saveTeachingRecords(items, assignments);
    },
    deleteEvent: async (eventId: string) =>
      preview
        ? setDemo((current) => ({
            ...current,
            calendarEvents: current.calendarEvents.filter((item) => item.id !== eventId),
          }))
        : recordActions.deleteCalendarEvent(eventId),
    deleteEvents: async (eventIds: string[]) => {
      if (preview)
        setDemo((current) => ({
          ...current,
          calendarEvents: current.calendarEvents.filter((item) => !eventIds.includes(item.id)),
        }));
      else await recordActions.deleteCalendarEvents(eventIds);
    },
    saveSchoolCalendar: async (changes: SchoolCalendarChange[]) => {
      for (const { after } of changes) {
        if (!after) continue;
        assertRecordDate(after.date);
        if (after.endDate) assertRecordDate(after.endDate);
      }
      if (preview) {
        const next = applySchoolCalendarChanges(demo.calendarEvents, changes);
        setDemo((current) => ({ ...current, calendarEvents: next }));
      } else if (localMode) await local.actions.saveSchoolCalendarChanges(changes);
      else throw new Error(yearText("readOnlyError"));
    },
    saveAttendance: async (item: AttendanceRecord) => {
      assertRecordDate(item.date);
      return preview
        ? setDemo((current) => ({ ...current, attendanceRecords: upsert(current.attendanceRecords, item) }))
        : recordActions.saveAttendanceRecord(item);
    },
    saveHomeroomHomework: async (item: HomeroomHomeworkRecord) => {
      assertRecordDate(item.date);
      return preview
        ? setDemo((current) => ({ ...current, homeroomHomeworkRecords: upsert(current.homeroomHomeworkRecords, item) }))
        : recordActions.saveHomeroomHomeworkRecord(item);
    },
    deleteHomeroomHomework: async (recordId: string) =>
      preview
        ? setDemo((current) => ({
            ...current,
            homeroomHomeworkRecords: current.homeroomHomeworkRecords.filter((item) => item.id !== recordId),
          }))
        : recordActions.deleteHomeroomHomeworkRecord(recordId),
    saveSeating: async (item: SeatingChart) =>
      preview
        ? setDemo((current) => ({ ...current, seatingCharts: upsert(current.seatingCharts, item) }))
        : recordActions.saveSeatingChart(item),
    saveQuiz: async (item: QuizDocument) => {
      if (localMode) throw new Error(i18n.t("screenCopy:teacherConsole.localQuizzesAreNotAvailableYet"));
      return preview
        ? setDemo((current) => ({ ...current, quizzes: upsert(current.quizzes, item) }))
        : cloudActions.saveQuiz(item);
    },
    deleteQuiz: async (quizId: string) => {
      if (localMode) throw new Error(i18n.t("screenCopy:teacherConsole.localQuizzesAreNotAvailableYet"));
      return preview
        ? setDemo((current) => ({ ...current, quizzes: current.quizzes.filter((item) => item.id !== quizId) }))
        : cloudActions.deleteQuiz(quizId);
    },
    saveStudentRoutineItem: async (item: StudentRoutineItem) => {
      assertRecordDate(
        item.date || new Date(new Date(item.createdAt).getTime() + 8 * 3600_000).toISOString().slice(0, 10),
      );
      return preview
        ? setDemo((current) => ({ ...current, studentRoutineItems: upsert(current.studentRoutineItems, item) }))
        : recordActions.saveStudentRoutineItem(item);
    },
    deleteStudentRoutineItem: async (itemId: string) =>
      preview
        ? setDemo((current) => ({
            ...current,
            studentRoutineItems: current.studentRoutineItems.filter((item) => item.id !== itemId),
          }))
        : recordActions.deleteStudentRoutineItem(itemId),
    saveHistoryPlan: async (plan: HistoryPlan) => {
      if (preview)
        setDemo((current) => ({
          ...current,
          attendanceRecords: plan.attendanceRecords.reduce(upsert<AttendanceRecord>, current.attendanceRecords),
          homeroomHomeworkRecords: plan.homeroomHomeworkRecords.reduce(
            upsert<HomeroomHomeworkRecord>,
            current.homeroomHomeworkRecords,
          ),
          dailyRecords: plan.dailyRecords.reduce(upsert<DailyRecord>, current.dailyRecords),
          studentRoutineItems: plan.studentRoutineItems.reduce(upsert<StudentRoutineItem>, current.studentRoutineItems),
        }));
      else await recordActions.saveHistoryPlan(plan);
    },
    createSession: async (quizId: string, classId: string, duration: number) => {
      if (localMode) throw new Error(i18n.t("screenCopy:teacherConsole.localQuizzesAreNotAvailableYet"));
      if (!preview) return cloudActions.createQuizSession(quizId, classId, duration);
      const quiz = demo.quizzes.find((item) => item.id === quizId)!;
      const now = new Date();
      const session = {
        id: id("session"),
        workspaceId: demo.workspace.id,
        quizId,
        classId,
        code: "482716",
        title: quiz.title,
        status: "open" as const,
        durationMinutes: duration,
        questionCount: quiz.questions.length,
        createdAt: now.toISOString(),
        expiresAt: new Date(now.getTime() + 10_800_000).toISOString(),
        joinUrl: `${location.origin}/join/482716`,
      };
      setDemo((current) => ({ ...current, quizSessions: [session, ...current.quizSessions] }));
      return session;
    },
  };

  const guardedActions = new Proxy(actions, {
    get(target, key) {
      const operation = Reflect.get(target, key);
      if (typeof operation !== "function" || key === "saveHiddenTools") return operation;
      return (...args: unknown[]) => {
        if (readOnlyYearRef.current) return Promise.reject(new Error(yearText("readOnlyError")));
        return Reflect.apply(operation, target, args);
      };
    },
  });
  async function selectSchoolYear(id?: string) {
    if (document.querySelector('[data-save-state="saving"]')) {
      setToast(yearText("busy"));
      return;
    }
    if (document.querySelector('[data-save-state="dirty"]') && !window.confirm(yearText("unsaved"))) return;
    try {
      if (localMode) await local.selectYear(id);
      else setDemoYearId(id || currentYearId);
      setSelectedClassId("");
      setClassPickerOpen(true);
      setViewHistory([]);
      setSelectedTermId(undefined);
      setCalendarFocusEvent(undefined);
      setGuideOpen(false);
      stopTour();
    } catch {
      setToast(yearText("switchFailed"));
    }
  }
  async function saveSchoolYear(action: SchoolYearAction) {
    if (!preview && !localMode) throw new Error(yearText("failed"));
    if (
      (action.type === "create" || action.type === "archive") &&
      document.querySelector('[data-save-state="dirty"]') &&
      !window.confirm(yearText("unsaved"))
    )
      return;
    if (localMode) await local.actions.saveSchoolYearAction(action);
    else {
      const next = changeSchoolYear(demo, action);
      setDemo(next);
      if (action.type === "delete" && demoYearId === action.id) setDemoYearId(next.workspace.currentSchoolYearId);
    }
    if (action.type === "create") {
      if (localMode) await local.selectYear(action.id);
      else setDemoYearId(action.id);
      setSelectedClassId("");
      setViewHistory([]);
      setClassPickerOpen(true);
    }
    setToast(yearText("saved"));
  }
  async function previewYearDeletion(id: string): Promise<YearDeletion> {
    if (localMode) return local.actions.previewSchoolYearDeletion(id);
    const view = schoolYearView(demo, id),
      rows = schoolYearRows(view);
    return {
      year: schoolYears(demo.workspace).find((year) => year.id === id)!,
      classes: view.classes.length,
      students: view.students.length,
      records: rows.length,
      token: schoolYearDeletionToken(demo.workspace, schoolYearRows(demo), id),
    };
  }
  const browsingYear = selectedYear && selectedYear.id !== currentYearId;
  const yearNotice =
    browsingYear || readOnlyYear ? (
      <div className="school-year-read-only" role="note">
        <span>
          {browsingYear
            ? yearText(selectedYear.startDate && selectedYear.startDate > hongKongDate() ? "preparing" : "viewing", {
                name: selectedYear.name,
              })
            : ""}
          {readOnlyYear && <small>{yearText("readOnly")}</small>}
        </span>
        {browsingYear && (
          <button type="button" onClick={() => void selectSchoolYear()}>
            {yearText("returnCurrent")}
          </button>
        )}
      </div>
    ) : null;
  const sessionLabel = preview
    ? isSchoolYearDemo
      ? interfaceText("demoSchoolYear", { year: demo.workspace.academicTerms?.[0]?.schoolYear || "" })
      : interfaceText("demoLabel")
    : interfaceText("localTrial");
  const sessionStatus = (
    <div className="console-header-actions">
      {localMode && local.storageStatus && !local.storageStatus.persistent && (
        <span
          className="local-storage-notice"
          role="note"
          aria-label={interfaceText("backupReminder")}
          title={interfaceText("backupReminder")}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path d="M12 3 2 21h20L12 3Z" strokeLinejoin="round" />
            <path d="M12 9v5m0 3v1" strokeLinecap="round" />
          </svg>
          <span className="local-storage-notice-text">{interfaceText("backupReminderShort")}</span>
        </span>
      )}
      {(preview || localMode) && (
        <div className="demo-preview-tools" data-demo-tour={preview ? "demo-banner" : undefined} role="note">
          <span className="demo-preview-label" title={sessionLabel}>
            {sessionLabel}
          </span>
          {preview && (
            <button
              type="button"
              className="demo-preview-reset"
              aria-label={interfaceText("resetDemo")}
              title={interfaceText("resetDemo")}
              onClick={() => location.reload()}
            >
              <span aria-hidden="true">↻</span>
            </button>
          )}
        </div>
      )}
      {syncControl}
      {backupControl}
    </div>
  );
  const pageContext = { ...context, sessionStatus };
  const classSetupStorageKey = `classroom-class-setup-v1:${JSON.stringify([workspace?.id, selectedYearId])}`;
  if (localMode && classSetupOpen && !readOnlyYear && !local.data.loading && workspace)
    return (
      <ClassSetupWizard
        key={classSetupStorageKey}
        draftStorageKey={classSetupStorageKey}
        sessionControl={
          <>
            {syncControl}
            {backupControl}
          </>
        }
        initialDraft={source.classes.length ? existingSetupDraft(source.classes) : undefined}
        onCancel={() => {
          setClassSetupDismissed(true);
          setClassSetupOpen(false);
          startTour();
        }}
        onComplete={async (draft) => {
          await local.actions.saveClassSetup(draft);
          const params = new URLSearchParams(location.search);
          params.delete("setup");
          const search = params.toString();
          history.replaceState(history.state, "", `${location.pathname}${search ? `?${search}` : ""}`);
          setClassSetupDismissed(true);
          setClassSetupOpen(false);
          setClassPickerOpen(true);
          setGuideOpen(false);
          setTourStep(null);
          if (source.classes.length === 0) onInitialClassSetupComplete?.();
        }}
      />
    );

  if (classPickerOpen)
    return (
      <>
        {yearNotice}
        <ClassPicker
          onPersonalTool={(tab) => void moveTo(tab)}
          readOnly={readOnlyYear}
          classes={source.classes}
          students={source.students}
          charts={source.seatingCharts}
          preview={preview}
          localMode={localMode}
          sessionActions={sessionActions}
          sessionStatus={sessionStatus}
          guideOpen={guideOpen || source.classes.length === 0}
          continuingSetup={continuingSetup}
          onGuideOpen={() => setGuideOpen(true)}
          onGuideClose={() => {
            setGuideOpen(false);
            if (tourStep) stopTour();
          }}
          onGuideClassChange={(classId) => {
            setTourClassId(classId);
            setSelectedClassId(classId);
            if (tourStep) {
              const item = source.classes.find((entry) => entry.id === classId);
              setTourStep(
                setupProgress(item, source.classes, source.students, source.seatingCharts).hasRoster
                  ? "seatingLink"
                  : "rosterLink",
              );
            }
          }}
          onChoose={chooseClass}
          onManage={() => {
            setRosterImportRequested(false);
            setAdminInitialSection("classes");
            moveTo("roster");
          }}
          onRoster={(classId) => {
            if (openClassRoster(classId) && tourStep === "rosterLink") setTourStep("rosterInput");
          }}
          onSeating={(classId) => {
            if (!canLeaveClass()) return;
            moveTo("seating", classId);
            if (tourStep === "seatingLink") setTourStep("seatingStudent");
          }}
          onSchoolCalendar={() => {
            setAdminInitialSection("calendar");
            moveTo("roster");
          }}
          onToolSettings={() => {
            setAdminInitialSection("tools");
            moveTo("roster");
          }}
          onAddClass={guardedActions.addClass}
          onDemoTourStart={() => showDemoTourStep("demoWelcome")}
          onTourReplay={localTourReplay ? () => startTour(true) : undefined}
          tourClassId={tourClassId}
          tourCreatedName={tourCreatedName}
          onTourClassCreated={(name) => {
            if (tourStepRef.current === "classSubmit") {
              setTourCreatedName(name);
              setTourStep("rosterLink");
            }
          }}
        />
        {localMode && tourStep && !guidancePaused && (
          <GuidedTour
            step={tourStep}
            continueSetup={continuingSetup}
            onNext={nextTourStep}
            onBack={previousTourStep}
            onSkip={stopTour}
          />
        )}
        {preview && demoTourStep && (
          <GuidedTour
            variant="demo"
            step={demoTourStep}
            onNext={nextDemoTourStep}
            onBack={previousDemoTourStep}
            onSkip={stopDemoTour}
          />
        )}
      </>
    );

  return (
    <div className={`console-shell sidebar-mode-${navigationMode}${sidebarDrawerMode ? " calendar-focus" : ""}`}>
      <a className="skip-link" href="#main-content">
        {i18n.t("landing:skip")}
      </a>
      {sidebarDrawerMode && sidebarDrawerOpen && (
        <button
          type="button"
          className="calendar-menu-backdrop"
          aria-label={interfaceText("closeTools")}
          onClick={() => {
            setSidebarMenuOpen(false);
          }}
        />
      )}
      {(!sidebarDrawerMode || sidebarDrawerOpen) && (
        <aside
          ref={calendarSidebarRef}
          id="console-navigation"
          tabIndex={sidebarDrawerMode ? -1 : undefined}
          className={`console-sidebar${sidebarDrawerMode ? " calendar-sidebar-drawer" : ""}`}
          role={sidebarDrawerMode ? "dialog" : undefined}
          aria-modal={sidebarDrawerMode ? true : undefined}
          aria-label={sidebarDrawerMode ? i18n.t("screenCopy:teacherConsole.tools") : undefined}
        >
          {sidebarDrawerMode && (
            <button
              type="button"
              className="calendar-sidebar-close"
              data-demo-nav-close
              aria-label={interfaceText("closeTools")}
              onClick={() => {
                setSidebarMenuOpen(false);
              }}
            >
              ×
            </button>
          )}
          <div className="sidebar-heading">
            <a
              className="brand-home"
              data-demo-nav="home"
              href={`${location.pathname}${location.search}`}
              aria-label={brandText("home")}
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                event.preventDefault();
                openClassPicker();
              }}
            >
              <BrandWordmark />
            </a>
            {!sidebarDrawerMode && (
              <button
                type="button"
                className="sidebar-toggle"
                data-sidebar-toggle
                aria-label={interfaceText("sidebarHidden")}
                title={interfaceText("sidebarHidden")}
                aria-controls="console-navigation"
                aria-expanded="true"
                onClick={() => chooseSidebarMode("drawer")}
              >
                <SidebarToggleIcon />
              </button>
            )}
          </div>
          <nav className="console-personal-tools" aria-label={workText("globalNavigation")}>
            <h2 className="personal-tools-caption">{workText("globalNavigation")}</h2>
            {(["today"] as const).map((tab) => (
              <PersonalToolEntry key={tab} tab={tab} active={activeTab === tab} onOpen={() => openTool(tab)} />
            ))}
          </nav>
          <section className="sidebar-class-context" aria-label={interfaceText("switchClass")}>
            <p className="personal-tools-class-caption">{workText("classContext")}</p>
            <h1 className="sidebar-context-heading" aria-label={selectedClass?.name || interfaceText("classSelect")}>
              {classSwitcher}
            </h1>
          </section>
          <nav aria-label={interfaceText("primaryTools")} data-demo-tour="navigation">
            {visibleNav.map((item) => (
              <div className={item.submenu ? "nav-menu" : ""} key={item.id}>
                {item.section && <small className="nav-section">{item.section}</small>}
                {item.submenu ? (
                  <>
                    <button
                      className={activeTab === item.id ? "nav-item active has-submenu" : "nav-item has-submenu"}
                      data-tab={item.id}
                      data-demo-nav={item.id}
                      aria-expanded={recordMenuOpen}
                      aria-controls="records-submenu"
                      aria-current={activeTab === item.id ? "page" : undefined}
                      onClick={() => setRecordMenuOpen((current) => !current)}
                    >
                      <span>
                        <NavIcon id={item.id} />
                      </span>
                      <strong>{item.label}</strong>
                      <i aria-hidden="true">⌄</i>
                    </button>
                    {recordMenuOpen && (
                      <div className="nav-submenu" id="records-submenu">
                        <button
                          type="button"
                          data-demo-nav="records-performance"
                          aria-current={activeTab === "records" && !isCoursework ? "page" : undefined}
                          className={activeTab === "records" && !isCoursework ? "active" : ""}
                          onClick={() => openRecordMode("performance")}
                        >
                          <span>
                            <NavIcon id="performance" />
                          </span>
                          <div>
                            <b>{i18n.t("guidedTour:steps.demoPerformance.title")}</b>
                          </div>
                        </button>
                        {!hiddenTools.includes("homework") && (
                          <button
                            type="button"
                            data-demo-nav="records-coursework"
                            aria-current={activeTab === "records" && isCoursework ? "page" : undefined}
                            className={activeTab === "records" && isCoursework ? "active" : ""}
                            onClick={() => openRecordMode("grades")}
                          >
                            <span>
                              <NavIcon id="homework" />
                            </span>
                            <div>
                              <b>{i18n.t("adminRecords:kinds.coursework")}</b>
                            </div>
                          </button>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <button
                    aria-current={activeTab === item.id ? "page" : undefined}
                    className={activeTab === item.id ? "nav-item active" : "nav-item"}
                    data-tab={item.id}
                    data-demo-nav={item.id}
                    data-tour={item.id === "seating" ? "seating-nav" : undefined}
                    onClick={() => openTool(item.id)}
                  >
                    <span>
                      <NavIcon id={item.id} />
                    </span>
                    <strong>{item.label}</strong>
                  </button>
                )}
              </div>
            ))}
          </nav>
          <div className="sidebar-foot">
            <div className="sidebar-actions">
              <button type="button" className="topbar-return" onClick={goBack}>
                {interfaceText("back")}
              </button>
              {backupControl ?? quickBackup.button}
            </div>

            <div className="console-session-actions">{sessionActions}</div>
          </div>
        </aside>
      )}
      <main className="console-main" id="main-content" tabIndex={-1}>
        {yearNotice}
        {sidebarDrawerMode && (
          <div className="console-toolbar">
            {sidebarDrawerMode && (
              <div className="console-drawer-tools">
                <button
                  type="button"
                  className="calendar-menu-trigger sidebar-toggle"
                  data-demo-nav-trigger
                  data-sidebar-toggle={sidebarLayout.width !== "narrow" ? true : undefined}
                  aria-label={interfaceText(sidebarLayout.width === "narrow" ? "openTools" : "sidebarExpand")}
                  title={interfaceText(sidebarLayout.width === "narrow" ? "openTools" : "sidebarExpand")}
                  aria-controls="console-navigation"
                  aria-expanded={sidebarDrawerOpen}
                  onClick={() => {
                    if (sidebarLayout.width === "narrow") setSidebarMenuOpen(true);
                    else chooseSidebarMode("full");
                  }}
                >
                  <SidebarToggleIcon expand />
                </button>
                <button type="button" className="topbar-return" onClick={goBack}>
                  {interfaceText("back")}
                </button>
                <div className="console-drawer-context class-setup-toolbar-context">
                  {classSwitcher}
                  <small>
                    {activeTab === "records"
                      ? recordGroupLabel
                      : visibleNav.find((item) => item.id === activeTab)?.label}
                  </small>
                </div>
                <button type="button" className="class-manage-button" onClick={openClassPicker}>
                  {setupStatusText("manageClasses")}
                </button>
              </div>
            )}
          </div>
        )}
        <section className="console-content" aria-labelledby="page-heading" data-demo-tour-panel={activeTab}>
          {localMode && !readOnlyYear && selectedClass && setupFollowUpClassId === selectedClass.id && (
            <ClassSetupFollowUp
              classroom={selectedClass}
              classes={source.classes}
              students={source.students}
              charts={source.seatingCharts}
              onNext={(next) => {
                if (setupProgress(next, source.classes, source.students, source.seatingCharts).hasRoster) {
                  if (canLeaveClass()) moveTo("seating", next.id);
                } else openClassRoster(next.id);
              }}
              onStart={() => setSetupFollowUpClassId("")}
            />
          )}
          {localMode &&
            !readOnlyYear &&
            selectedClass &&
            !students.some((student) => student.active) &&
            !["today", "roster", "billing", "calendar"].includes(activeTab) && (
              <section className="paper-card class-setup-empty-roster" aria-label={setupStatusText("emptyRegion")}>
                <div>
                  <h2>{setupStatusText("emptyTitle", { name: selectedClass.name })}</h2>
                  <p>{setupStatusText("emptyDescription")}</p>
                </div>
                <button type="button" className="primary-action" onClick={() => openClassRoster(selectedClass.id)}>
                  {setupStatusText("importRoster")}
                </button>
              </section>
            )}
          {((!selectedClass && activeTab !== "today" && activeTab !== "roster" && activeTab !== "billing") ||
            activeTab === "quiz" ||
            (activeTab === "billing" && !localMode)) && (
            <h2 id="page-heading" className="sr-only">
              {localMode && activeTab === "billing"
                ? i18n.t("screenCopy:teacherConsole.localData")
                : activeTab === "records"
                  ? recordGroupLabel
                  : allowedNav.find((item) => item.id === activeTab)?.label}
            </h2>
          )}
          {!selectedClass && activeTab !== "today" && activeTab !== "roster" && activeTab !== "billing" ? (
            <EmptyClass onManage={() => moveTo("roster")} />
          ) : (
            <>
              {activeTab === "today" && (
                <AgendaPanel
                  courseworkSummary={
                    !hiddenTools.includes("homework") && (
                      <AgendaCourseworkSummary
                        rows={courseworkClassSummary(
                          sortClasses(source.classes),
                          source.students,
                          agendaCourseworkSource.homeworkAssignments,
                          agendaCourseworkSource.homeworkRecords,
                          today(),
                        )}
                        scope={
                          agendaCourseworkTerm
                            ? `${agendaCourseworkTerm.schoolYear} ${agendaCourseworkTerm.name}`
                            : selectedYear?.name
                        }
                        onOpenClass={(classId) => {
                          if (!canLeaveClass()) return;
                          setSelectedTermId(agendaCourseworkTerm?.id);
                          moveTo("records", classId);
                          setRecordMode("grades");
                          setRecordMenuOpen(true);
                          setCourseworkFollowupRequest((current) => current + 1);
                        }}
                      />
                    )
                  }
                  sessionStatus={sessionStatus}
                  classes={source.classes}
                  events={displayCalendarEvents}
                  onOpenEvent={(item) => {
                    setCalendarFocusDate(item.date);
                    moveTo("calendar", item.classId || selectedClassId, false, item);
                  }}
                />
              )}
              {activeTab === "records" && selectedClass && (
                <div className="student-record-workspace" key={`${selectedClass.id}:${selectedTerm?.id || "all"}`}>
                  <ToolPageHeader
                    title={recordGroupLabel}
                    help={isCoursework ? "coursework" : undefined}
                    sessionStatus={sessionStatus}
                    continued
                    navigation={
                      <ToolPageNavigation
                        label={i18n.t("screenCopy:teacherConsole.tabs", { recordGroupLabel: recordGroupLabel })}
                      >
                        {recordTabs.map(({ mode, label }, index) => (
                          <button
                            key={mode}
                            id={`student-record-tab-${mode}`}
                            type="button"
                            role="tab"
                            aria-selected={recordMode === mode}
                            aria-controls="student-record-panel"
                            tabIndex={recordMode === mode ? 0 : -1}
                            data-demo-nav={`records-${mode}`}
                            onClick={() => openRecordMode(mode)}
                            onKeyDown={(event) => {
                              const next =
                                event.key === "ArrowRight"
                                  ? (index + 1) % recordTabs.length
                                  : event.key === "ArrowLeft"
                                    ? (index + recordTabs.length - 1) % recordTabs.length
                                    : event.key === "Home"
                                      ? 0
                                      : event.key === "End"
                                        ? recordTabs.length - 1
                                        : undefined;
                              if (next === undefined) return;
                              event.preventDefault();
                              const target = recordTabs[next];
                              if (!target) return;
                              openRecordMode(target.mode);
                              document.getElementById(`student-record-tab-${target.mode}`)?.focus();
                            }}
                          >
                            {mode === "grades"
                              ? gradeText("dashboard.title")
                              : mode === "gradeTable"
                                ? gradeText("table.title")
                                : label}
                          </button>
                        ))}
                      </ToolPageNavigation>
                    }
                  />
                  <div id="student-record-panel" role="tabpanel" aria-labelledby={`student-record-tab-${recordMode}`}>
                    {/* Keep same-class panels mounted so switching tabs preserves filters and unsaved drafts. */}
                    <div
                      className="student-record-view"
                      hidden={recordMode !== "materials" && recordMode !== "answers"}
                    >
                      <RecordsPanel
                        {...pageContext}
                        mode={recordMode === "materials" ? "materials" : "answers"}
                        drawEnabled={recordMode === "answers"}
                        records={source.dailyRecords}
                        charts={effectiveCharts}
                        onSave={guardedActions.saveDailyAction}
                        onSaveDraw={guardedActions.saveDrawAction}
                        onNavigate={openTool}
                      />
                    </div>
                    <div className="student-record-view" hidden={recordMode !== "performance"}>
                      <ClassroomPerformance
                        onExport={(scope) => setExportRequest({ tab: "records", scope })}
                        recordTerm={selectedTerm}
                        classId={selectedClass.id}
                        students={students}
                        records={termSource.dailyRecords}
                      />
                    </div>
                    <div className="student-record-view" hidden={recordMode !== "grades"}>
                      <GradeOverview
                        followupRequest={courseworkFollowupRequest}
                        onCompareTerms={() => setTermComparisonOpen(true)}
                        termControl={
                          academicTerms.length > 0 ? (
                            <GradeCategory
                              label={gradeText("table.term")}
                              value={courseworkTerm?.id ?? ""}
                              onChange={selectTerm}
                              options={academicTerms.map((term) => ({
                                value: term.id,
                                label: `${term.schoolYear} ${term.name}`,
                              }))}
                            />
                          ) : undefined
                        }
                        classroom={selectedClass}
                        onOpenAssignment={(id) => {
                          if (document.querySelector('[data-save-state="saving"]')) return;
                          if (
                            document.querySelector('[data-save-state="dirty"]') &&
                            !window.confirm(termText("unsaved"))
                          )
                            return;
                          openRecordMode("assignments");
                          assignmentNavigation.current?.openAssignment?.(id);
                        }}
                        onOpenTable={(id) => {
                          setTableTarget((current) => ({ id, request: current.request + 1 }));
                          openRecordMode("gradeTable");
                        }}
                        classId={selectedClass.id}
                        students={students}
                        assignments={courseworkSource.homeworkAssignments}
                        records={courseworkSource.homeworkRecords}
                      />
                    </div>
                    <div className="student-record-view" hidden={recordMode !== "gradeTable"}>
                      <GradeTable
                        termControl={
                          academicTerms.length > 0 ? (
                            <GradeCategory
                              label={gradeText("table.term")}
                              value={courseworkTerm?.id ?? ""}
                              onChange={selectTerm}
                              options={academicTerms.map((term) => ({
                                value: term.id,
                                label: `${term.schoolYear} ${term.name}`,
                              }))}
                            />
                          ) : undefined
                        }
                        key={`${selectedClass.id}:${courseworkTerm?.id || "all"}:${tableTarget.request}`}
                        classroom={selectedClass}
                        students={students}
                        assignments={courseworkSource.homeworkAssignments}
                        records={courseworkSource.homeworkRecords}
                        initialAssignmentId={tableTarget.id}
                        onClearAssignment={() => setTableTarget((current) => ({ ...current, id: undefined }))}
                        termLabel={courseworkTerm ? `${courseworkTerm.schoolYear} ${courseworkTerm.name}` : ""}
                        onExport={(scope) =>
                          setExportRequest({ tab: "homework", scope, recordTerm: courseworkTerm ?? null })
                        }
                      />
                    </div>
                    <div className="student-record-view" hidden={recordMode !== "assignments"}>
                      <AssignmentRecords
                        readOnly={readOnlyYear}
                        classroom={selectedClass}
                        classes={source.classes}
                        dateRange={selectedYear?.legacy ? undefined : selectedYear}
                        onCopy={guardedActions.copyAssignments}
                        onFolderAction={(action) => guardedActions.saveAssignmentFolderAction(selectedClass.id, action)}
                        terms={academicTerms}
                        recordTerm={courseworkTerm}
                        onSelectTerm={selectTerm}
                        defaultRecordDate={defaultRecordDate}
                        navigationRef={assignmentNavigation}
                        workspaceId={context.workspaceId}
                        classId={selectedClass.id}
                        students={students}
                        assignments={source.homeworkAssignments}
                        records={source.homeworkRecords}
                        onSaveAssignment={guardedActions.saveAssignment}
                        onSaveRecord={guardedActions.saveHomework}
                        onDeleteAssignment={guardedActions.deleteAssignment}
                        deletionToken={(item) =>
                          assignmentDeletionPlan(
                            item.classId,
                            item.id,
                            source.calendarEvents,
                            source.homeworkAssignments,
                            source.homeworkRecords,
                          ).token
                        }
                      />
                    </div>
                  </div>
                </div>
              )}
              {activeTab === "groups" && (
                <GroupsPanel key={selectedClassId} {...pageContext} onSaveDraw={guardedActions.saveDrawAction} />
              )}
              {activeTab === "attendance" && (
                <AttendancePanel
                  key={`${selectedClassId}:${selectedTerm?.id || "all"}`}
                  {...pageContext}
                  records={termSource.attendanceRecords}
                  charts={effectiveCharts}
                  onSave={guardedActions.saveAttendance}
                />
              )}
              {activeTab === "homeroomHomework" && (
                <HomeroomHomeworkPanel
                  key={`${selectedClassId}:${selectedTerm?.id || "all"}`}
                  {...pageContext}
                  records={termSource.homeroomHomeworkRecords}
                  charts={effectiveCharts}
                  onSave={guardedActions.saveHomeroomHomework}
                  onDelete={guardedActions.deleteHomeroomHomework}
                  onUpdateClass={guardedActions.updateClassSettings}
                />
              )}
              {activeTab === "studentRecords" && selectedClass && (
                <div key={`${selectedClass.id}:${selectedTerm?.id || "all"}`}>
                  <ToolPageHeader
                    title={visibleNav.find((item) => item.id === "studentRecords")?.label}
                    sessionStatus={sessionStatus}
                    continued={homeroomTermView}
                    navigation={
                      <ToolPageNavigation
                        label={termText("title")}
                        onKeyDown={(event) => {
                          if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
                          event.preventDefault();
                          const next = event.key === "Home" || (event.key !== "End" && !homeroomTermView);
                          setHomeroomTermView(next);
                          document.getElementById(next ? "homeroom-term-tab" : "homeroom-record-tab")?.focus();
                        }}
                      >
                        <button
                          role="tab"
                          type="button"
                          aria-selected={homeroomTermView}
                          aria-controls="homeroom-term-panel"
                          id="homeroom-term-tab"
                          tabIndex={homeroomTermView ? 0 : -1}
                          onClick={() => setHomeroomTermView(true)}
                        >
                          {termText("title")}
                        </button>
                        <button
                          role="tab"
                          type="button"
                          aria-selected={!homeroomTermView}
                          aria-controls="homeroom-record-panel"
                          id="homeroom-record-tab"
                          tabIndex={homeroomTermView ? -1 : 0}
                          onClick={() => setHomeroomTermView(false)}
                        >
                          {termText("details")}
                        </button>
                      </ToolPageNavigation>
                    }
                  />
                  <div
                    role="tabpanel"
                    id="homeroom-term-panel"
                    aria-labelledby="homeroom-term-tab"
                    hidden={!homeroomTermView}
                  >
                    <TermOverview
                      terms={academicTerms}
                      selectedId={selectedTermId ?? selectedTerm?.id ?? ""}
                      students={students}
                      classId={selectedClass.id}
                      className={selectedClass.name}
                      classRole="homeroom"
                      onExport={(scope) => setExportRequest({ tab: "studentRecords", scope })}
                      hiddenTools={hiddenTools}
                      source={source}
                      onSelect={selectTerm}
                      onManage={manageTerms}
                    />
                  </div>
                  <div
                    role="tabpanel"
                    id="homeroom-record-panel"
                    aria-labelledby="homeroom-record-tab"
                    hidden={homeroomTermView}
                  >
                    <StudentRecordsPanel
                      {...pageContext}
                      attendanceRecords={termSource.attendanceRecords}
                      homeworkRecords={termSource.homeroomHomeworkRecords}
                      showAttendance={!hiddenTools.includes("attendance")}
                      showHomework={!hiddenTools.includes("homeroomHomework")}
                      onNavigate={openTool}
                      onRequestExport={(studentId, intent) =>
                        setExportRequest({
                          tab: "studentRecords",
                          studentId,
                          scope: {
                            intent,
                            sheetTitles: [
                              ...(!hiddenTools.includes("attendance")
                                ? [i18n.t("screenCopy:teacherConsole.absentDates")]
                                : []),
                              ...(!hiddenTools.includes("homeroomHomework")
                                ? [i18n.t("guidedTour:steps.demoHomeroomHomework.title")]
                                : []),
                            ],
                          },
                        })
                      }
                    />
                  </div>
                </div>
              )}
              {activeTab === "homeroom" && selectedClass && (
                <HomeroomAffairs
                  key={`${selectedClassId}:${selectedTerm?.id || "all"}`}
                  sessionStatus={sessionStatus}
                  classItem={selectedClass}
                  students={students}
                  readOnly={readOnlyYear}
                  onMemoAction={(action) => guardedActions.saveDailyMemoAction(selectedClass.id, action)}
                  showAffairs={Boolean(tourStep || demoTourStep)}
                >
                  <HomeroomPanel
                    key={`${selectedClassId}:${selectedTerm?.id || "all"}`}
                    {...pageContext}
                    mode="affairs"
                    records={termSource.homeroomRecords}
                    onAffairChange={guardedActions.saveAffairChange}
                    onUpdateClass={guardedActions.updateClassSettings}
                  />
                </HomeroomAffairs>
              )}
              {activeTab === "homeroomDuties" && (
                <HomeroomPanel
                  key={`${selectedClassId}:${selectedTerm?.id || "all"}`}
                  {...pageContext}
                  mode="duties"
                  records={termSource.homeroomRecords}
                  onAffairChange={guardedActions.saveAffairChange}
                  onUpdateClass={guardedActions.updateClassSettings}
                  onRequestExport={(assignments) => {
                    if (selectedClass)
                      setExportRequest({
                        tab: "homeroomDuties",
                        scope: { report: buildStudentDutyReport(selectedClass, students, assignments) },
                      });
                  }}
                />
              )}
              {activeTab === "seating" && (
                <SeatingPanel
                  key={`${context.workspaceId}:${rosterClassId(selectedClass, source.classes)}`}
                  {...pageContext}
                  onPrint={(chart) => {
                    if (selectedClass)
                      setExportRequest({
                        tab: "seating",
                        scope: {
                          intent: "print",
                          report: buildRecordReport("seating", selectedClass, { ...source, seatingCharts: [chart] }),
                        },
                      });
                  }}
                  sharedClassId={rosterClassId(selectedClass, source.classes)}
                  charts={source.seatingCharts}
                  onSave={guardedActions.saveSeating}
                  tourChooseStudent={tourStep === "seatingStudent"}
                  onTourStudentSelected={() => {
                    if (tourStepRef.current === "seatingStudent") setTourStep("seatingSeat");
                  }}
                  onTourSeatPlaced={() => {
                    if (tourStepRef.current === "seatingStudent" || tourStepRef.current === "seatingSeat")
                      setTourStep("seatingSave");
                  }}
                  onTourSaved={() => {
                    if (tourStepRef.current === "seatingSave") stopTour(true);
                  }}
                />
              )}
              {activeTab === "calendar" && (
                <CalendarPanel
                  key={`${selectedClassId}:${selectedTerm?.id || "all"}`}
                  {...pageContext}
                  events={displayCalendarEvents}
                  focusDate={calendarFocusDate}
                  focusEvent={calendarFocusEvent}
                  onManageSchoolCalendar={() => {
                    setAdminInitialSection("calendar");
                    moveTo("roster");
                  }}
                  onSave={guardedActions.saveEvent}
                  onDelete={guardedActions.deleteEvent}
                  onOpenCalendar={(date) => {
                    setCalendarFocusDate(date);
                    moveTo("calendar");
                  }}
                />
              )}
              {QUIZ_RELEASED && activeTab === "quiz" && (
                <QuizDirectoryPanel
                  {...pageContext}
                  quizzes={source.quizzes}
                  sessions={source.quizSessions}
                  responses={source.quizResponses}
                  onCreateSession={guardedActions.createSession}
                  onRequestExport={() => setExportRequest({ tab: "quiz" })}
                />
              )}
              {activeTab === "roster" && (
                <AdminPanel
                  {...pageContext}
                  initialSection={adminInitialSection}
                  onSectionChange={setAdminInitialSection}
                  initialRosterMethod={rosterImportMethod}
                  initialRosterOpen={rosterImportRequested}
                  termSettings={(active) => (
                    <>
                      {workspace && (preview || localMode) && selectedYear && (
                        <SchoolYearSettings
                          key={`school-year:${selectedYear.id}`}
                          workspace={workspace}
                          selected={selectedYear.id}
                          onAction={saveSchoolYear}
                          currentYearId={currentYearId}
                          date={hongKongDate()}
                          onSelect={selectSchoolYear}
                          onPreviewDelete={previewYearDeletion}
                          onBackup={localMode ? () => moveTo("billing") : undefined}
                        />
                      )}
                      <TermSettings
                        annualMode={preview || localMode}
                        key={`terms:${selectedYearId}`}
                        terms={academicTerms}
                        onSave={guardedActions.saveAcademicTerms}
                        active={active}
                        readOnly={readOnlyYear}
                        schoolYear={preview || localMode ? selectedYear : undefined}
                      />
                    </>
                  )}
                  toolSettings={
                    <ToolSettingsPanel
                      hiddenTools={hiddenTools}
                      localMode={localMode}
                      onSave={guardedActions.saveHiddenTools}
                      toast={setToast}
                    />
                  }
                  localMode={localMode || !QUIZ_RELEASED}
                  classes={source.classes}
                  quizzes={source.quizzes}
                  calendarEvents={source.calendarEvents}
                  historySource={source}
                  exportSource={source}
                  academicTerms={academicTerms}
                  onAddClass={guardedActions.addClass}
                  onDeleteEmptyClass={guardedActions.deleteEmptyClass}
                  onAddStudent={guardedActions.addStudent}
                  onImport={guardedActions.importStudents}
                  onSaveHistoryPlan={guardedActions.saveHistoryPlan}
                  onUpdateStudent={guardedActions.updateStudent}
                  onDeleteStudent={guardedActions.deleteStudent}
                  onSaveQuiz={guardedActions.saveQuiz}
                  onDeleteQuiz={guardedActions.deleteQuiz}
                  onGenerateQuiz={cloudActions.generateQuiz}
                  onSaveCalendarEvents={guardedActions.saveEvents}
                  onDeleteCalendarEvents={guardedActions.deleteEvents}
                  onSaveSchoolCalendar={guardedActions.saveSchoolCalendar}
                  schoolCalendarReadOnly={!preview && !localMode}
                  selectedSchoolYear={selectedYear}
                  onSelectSchoolYear={async (yearId) => {
                    await selectSchoolYear(yearId);
                    setClassPickerOpen(false);
                    setAdminInitialSection("calendar");
                  }}
                  onTourRosterImported={() => {
                    if (tourStepRef.current === "rosterSubmit") setTourStep("seatingNav");
                  }}
                />
              )}
              {activeTab === "billing" &&
                (localMode ? (
                  <LocalDataPanel
                    backupControl={backupControl}
                    sessionStatus={sessionStatus}
                    storageStatus={local.storageStatus}
                    onStorageStatusChange={local.updateStorageStatus}
                    onExport={local.actions.exportSnapshot}
                    onRestore={local.actions.restoreSnapshot}
                    onPreviewCloudImport={local.actions.previewCloudImport}
                    onImportCloudExport={local.actions.importCloudExport}
                  />
                ) : (
                  <BillingPanel {...pageContext} cloudActions={cloudActions} user={user} />
                ))}
            </>
          )}
        </section>
      </main>
      {termComparisonOpen &&
        activeTab === "records" &&
        recordMode === "grades" &&
        !classPickerOpen &&
        selectedClass && (
          <TermComparisonDialog
            terms={academicTerms}
            selectedId={selectedTermId ?? selectedTerm?.id ?? ""}
            students={students}
            classId={selectedClass.id}
            className={selectedClass.name}
            classRole="teaching"
            source={source}
            onSelect={selectTerm}
            onManage={() => {
              setTermComparisonOpen(false);
              manageTerms();
            }}
            onExport={(scope) => setExportRequest({ tab: "records", scope, recordTerm: courseworkTerm ?? null })}
            onClose={() => setTermComparisonOpen(false)}
          />
        )}
      {exportRequest && (
        <div
          className={exportRequest.scope?.intent === "print" ? undefined : "record-export-backdrop"}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !exportBusy) setExportRequest(null);
          }}
        >
          {selectedClass && (
            <RecordExportDialog
              dialogRef={exportDialogRef}
              tab={exportRequest.tab}
              classItem={selectedClass}
              source={exportRequest.recordTerm !== undefined ? source : termSource}
              studentId={exportRequest.studentId}
              scope={exportRequest.scope}
              term={exportRequest.recordTerm !== undefined ? (exportRequest.recordTerm ?? undefined) : selectedTerm}
              busy={exportBusy}
              onBusyChange={setExportBusy}
              onClose={() => setExportRequest(null)}
              onPrintError={setToast}
            />
          )}
        </div>
      )}
      {quickBackup.dialog}
      {toast && (
        <div className="toast" role="status" aria-live="polite" aria-atomic="true">
          {toast}
        </div>
      )}
      {localMode && tourStep && !guidancePaused && (
        <GuidedTour
          step={tourStep}
          continueSetup={continuingSetup}
          onNext={nextTourStep}
          onBack={previousTourStep}
          onSkip={stopTour}
        />
      )}
      {preview && demoTourStep && (
        <GuidedTour
          variant="demo"
          step={demoTourStep}
          onNext={nextDemoTourStep}
          onBack={previousDemoTourStep}
          onSkip={stopDemoTour}
        />
      )}
    </div>
  );
}

function ClassPicker({
  onPersonalTool,
  readOnly = false,
  classes,
  students,
  charts,
  preview,
  localMode,
  sessionActions,
  sessionStatus,
  guideOpen,
  continuingSetup,
  onGuideOpen,
  onGuideClose,
  onGuideClassChange,
  onChoose,
  onManage,
  onRoster,
  onSeating,
  onSchoolCalendar,
  onToolSettings,
  onAddClass,
  onDemoTourStart,
  onTourReplay,
  tourClassId,
  tourCreatedName,
  onTourClassCreated,
}: {
  onPersonalTool?: (tab: "today") => void;
  readOnly?: boolean;
  classes: Classroom[];
  students: Student[];
  charts: SeatingChart[];
  preview: boolean;
  localMode: boolean;
  sessionActions: React.ReactNode;
  sessionStatus: React.ReactNode;
  guideOpen: boolean;
  continuingSetup: boolean;
  onGuideOpen: () => void;
  onGuideClose: () => void;
  onGuideClassChange: (classId: string) => void;
  onChoose: (item: Classroom) => void;
  onManage: () => void;
  onRoster: (classId: string) => void;
  onSeating: (classId: string) => void;
  onSchoolCalendar: () => void;
  onToolSettings: () => void;
  onAddClass: (name: string, role: Classroom["classRole"], sharedDataClassId?: string) => Promise<void>;
  onDemoTourStart: () => void;
  onTourReplay?: () => void;
  tourClassId: string;
  tourCreatedName: string;
  onTourClassCreated: (name: string) => void;
}) {
  const { t: copy } = useTranslation("classroomInterface");
  const { t: workText } = useTranslation("personalTools");
  const [name, setName] = useState("");
  const [role, setRole] = useState<Classroom["classRole"]>("teaching");
  const [sharedDataClassId, setSharedDataClassId] = useState("");
  const { t: statusText } = useTranslation("classSetupStatus");
  const [guideClassId, setGuideClassId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const homeroom = sortClasses(classes.filter((item) => item.active && item.classRole === "homeroom"));
  const teaching = sortClasses(classes.filter((item) => item.active && item.classRole === "teaching"));
  const sourceClasses = classes.filter((item) => item.active && !item.sharedDataClassId);
  const guideClass =
    classes.find((item) => item.id === tourClassId) ||
    [...classes].reverse().find((item) => Boolean(tourCreatedName) && item.name === tourCreatedName) ||
    classes.find((item) => item.id === guideClassId) ||
    classes.find((item) => {
      const progress = setupProgress(item, classes, students, charts);
      return !progress.hasRoster || !progress.hasSeating;
    }) ||
    classes[0];
  const progress = setupProgress(guideClass, classes, students, charts);
  const ClassHeading = onPersonalTool ? "h2" : "h1";
  const totalSteps = continuingSetup ? 2 : 3;
  const completeCount =
    (continuingSetup ? 0 : Number(progress.hasClass)) + Number(progress.hasRoster) + Number(progress.hasSeating);
  const classCard = (item: Classroom) => {
    const state = setupProgress(item, classes, students, charts);
    const action = statusText(state.hasRoster ? "arrangeSeating" : "importRoster");
    const entry = (
      <button
        type="button"
        key={item.id}
        className={`class-picker-card${localMode ? " class-setup-card-main" : ""}`}
        aria-label={statusText("classEntry", { name: item.name, count: state.studentCount })}
        onClick={() => onChoose(item)}
      >
        <strong>{item.name}</strong>
        <span>{statusText("studentCount", { count: state.studentCount })}</span>
        {localMode && (
          <small className={`class-setup-state${state.hasSeating ? " class-setup-state-complete" : ""}`}>
            {statusText(state.hasSeating ? "complete" : state.hasRoster ? "needsSeating" : "notImported")}
          </small>
        )}
      </button>
    );
    if (!localMode) return entry;
    return (
      <article className="class-setup-card" key={item.id} aria-label={statusText("cardRegion", { name: item.name })}>
        {entry}
        {!readOnly && !state.hasSeating && (
          <button
            type="button"
            className="class-setup-card-action"
            aria-label={statusText("classAction", { name: item.name, action })}
            onClick={() => (state.hasRoster ? onSeating(item.id) : onRoster(item.id))}
          >
            {action} →
          </button>
        )}
      </article>
    );
  };
  return (
    <main
      className={`class-picker-page${onPersonalTool ? " class-picker-work-home" : ""}`}
      id="main-content"
      tabIndex={-1}
    >
      {onPersonalTool && (
        <>
          <header className="work-home-heading">
            <div className="work-home-brand">
              <BrandWordmark />
            </div>
            <div>
              <h1>{workText("workHome")}</h1>
              <p>{workText("workHomeHint")}</p>
            </div>
            {sessionStatus}
          </header>
          <section className="class-picker-personal-tools" aria-label={workText("globalNavigation")}>
            <h2>{workText("globalNavigation")}</h2>
            <nav className="home-personal-entries" aria-label={workText("globalNavigation")}>
              <PersonalToolEntry tab="today" home onOpen={() => onPersonalTool("today")} />
            </nav>
          </section>
        </>
      )}
      <a className="skip-link" href="#class-picker-title">
        {i18n.t("screenCopy:teacherConsole.skipToClassSelection")}
      </a>
      <section className="class-picker-panel" data-demo-tour="class-picker" aria-labelledby="class-picker-title">
        <div className="class-picker-accent" aria-hidden="true" />
        <header className="class-picker-heading">
          {!onPersonalTool && (
            <div className="class-picker-brand">
              <BrandWordmark />
            </div>
          )}
          <ClassHeading id="class-picker-title">
            {continuingSetup
              ? copy("continueSetupTitle")
              : guideOpen && !preview
                ? i18n.t("screenCopy:teacherConsole.startClassSetup")
                : i18n.t("screenCopy:teacherConsole.whichClassAreYouTeachingToday")}
          </ClassHeading>
          {!onPersonalTool && sessionStatus}
          {preview && (
            <button type="button" className="class-picker-guide-link" onClick={onDemoTourStart}>
              {i18n.t("screenCopy:teacherConsole.featureTour")}
            </button>
          )}
          {localMode && !readOnly && onTourReplay && (
            <div className="class-picker-heading-actions">
              <button type="button" className="tour-replay-button" onClick={onTourReplay}>
                {i18n.t("classroomInterface:replayLocal")}{" "}
                <small>{i18n.t("screenCopy:teacherConsole.localPreviewOnly")}</small>
              </button>
            </div>
          )}
          {!preview && !localMode && !guideOpen && (
            <button type="button" className="class-picker-guide-link" onClick={onGuideOpen}>
              {i18n.t("screenCopy:teacherConsole.setupGuide")}
            </button>
          )}
        </header>
        {!preview && guideOpen && !readOnly && (
          <section
            className="setup-guide"
            aria-label={i18n.t("screenCopy:teacherConsole.gettingStartedGuide")}
            data-tour={continuingSetup ? "roster-seating-setup" : undefined}
          >
            <div className="setup-guide-head">
              <div>
                {!continuingSetup && <small>{i18n.t("screenCopy:teacherConsole.firstTimeSetup")}</small>}
                <h2>
                  {completeCount === totalSteps
                    ? i18n.t("screenCopy:teacherConsole.readyToBegin")
                    : continuingSetup
                      ? copy("rosterAndSeating")
                      : i18n.t("screenCopy:teacherConsole.setUpClassesRostersAndSeatingFirst")}
                </h2>
              </div>
              <strong>
                {completeCount}／{totalSteps} {i18n.t("dailyMemos:done")}
              </strong>
            </div>
            <div
              className="setup-progress"
              role="progressbar"
              aria-valuenow={completeCount}
              aria-valuemin={0}
              aria-valuemax={totalSteps}
              aria-label={i18n.t("screenCopy:teacherConsole.setupProgress")}
            >
              <span style={{ width: `${(completeCount / totalSteps) * 100}%` }} />
            </div>
            {classes.length > 0 && (
              <label className="setup-class-select">
                {i18n.t("screenCopy:teacherConsole.settingUp")}
                <select
                  value={guideClass?.id || ""}
                  onChange={(event) => {
                    setGuideClassId(event.target.value);
                    onGuideClassChange(event.target.value);
                  }}
                >
                  {classes
                    .filter((item) => item.active)
                    .map((item) => (
                      <option value={item.id} key={item.id}>
                        {item.name}
                      </option>
                    ))}
                </select>
              </label>
            )}
            <ol className="setup-steps">
              {!continuingSetup && (
                <li className={progress.hasClass ? "done" : "current"}>
                  <span aria-hidden="true">{progress.hasClass ? "✓" : "1"}</span>
                  <div>
                    <b>{i18n.t("screenCopy:teacherConsole.createClass")}</b>
                  </div>
                  <a href="#class-picker-new-class">
                    {progress.hasClass
                      ? i18n.t("screenCopy:teacherConsole.addAnotherClass")
                      : i18n.t("screenCopy:teacherConsole.completeTheDetailsBelow")}
                  </a>
                </li>
              )}
              <li className={progress.hasRoster ? "done" : progress.hasClass ? "current" : ""}>
                <span aria-hidden="true">{progress.hasRoster ? "✓" : continuingSetup ? "1" : "2"}</span>
                <div>
                  <b>{i18n.t("marketing:guideStep2Title")}</b>
                  {progress.hasRoster && (
                    <small>
                      {progress.studentCount} {i18n.t("screenCopy:teacherConsole.students")}
                    </small>
                  )}
                </div>
                <button
                  type="button"
                  data-tour="roster-link"
                  disabled={!guideClass}
                  onClick={() => onRoster(guideClass!.id)}
                >
                  {progress.hasRoster
                    ? i18n.t("screenCopy:teacherConsole.viewRoster")
                    : i18n.t("classSetupStatus:importRoster")}
                </button>
              </li>
              <li className={progress.hasSeating ? "done" : progress.hasRoster ? "current" : ""}>
                <span aria-hidden="true">{progress.hasSeating ? "✓" : continuingSetup ? "2" : "3"}</span>
                <div>
                  <b>{i18n.t("guidedTour:steps.seatingLink.title")}</b>
                </div>
                <button
                  type="button"
                  data-tour="seating-link"
                  disabled={!guideClass || !progress.hasRoster}
                  onClick={() => onSeating(guideClass!.id)}
                >
                  {progress.hasSeating
                    ? i18n.t("screenCopy:teacherConsole.viewSeats")
                    : i18n.t("screenCopy:teacherConsole.arrangeSeats")}
                </button>
              </li>
            </ol>
            {completeCount === totalSteps && guideClass && (
              <button type="button" className="primary-action setup-start" onClick={() => onChoose(guideClass)}>
                {i18n.t("screenCopy:teacherConsole.open")}
                {guideClass.name}
              </button>
            )}
            {!continuingSetup && (
              <div className="setup-next">
                <span>{i18n.t("terms:setupLater")}</span>
                {CALENDAR_IMPORT_RELEASED && (
                  <button type="button" onClick={onSchoolCalendar}>
                    {i18n.t("screenCopy:teacherConsole.importSchoolWideCalendar")}
                  </button>
                )}
                <button type="button" onClick={onToolSettings}>
                  {i18n.t("screenCopy:teacherConsole.chooseVisibleTools")}
                </button>
              </div>
            )}
            {!continuingSetup && classes.length > 0 && (
              <button type="button" className="setup-dismiss" onClick={onGuideClose}>
                {i18n.t("screenCopy:teacherConsole.backToClassSelection")}
              </button>
            )}
          </section>
        )}
        {!guideOpen && (
          <>
            <section className="class-picker-group homeroom" aria-labelledby="class-picker-homeroom">
              <div className="class-picker-group-label">
                <h2 id="class-picker-homeroom">{i18n.t("classSetup:steps.0")}</h2>
              </div>
              <div className="class-picker-cards">
                {homeroom.length ? (
                  homeroom.map(classCard)
                ) : (
                  <p>{i18n.t("screenCopy:teacherConsole.noHomeroomClassCreatedYet")}</p>
                )}
              </div>
            </section>
            <section className="class-picker-group teaching" aria-labelledby="class-picker-teaching">
              <div className="class-picker-group-label">
                <h2 id="class-picker-teaching">{i18n.t("classSetup:steps.1")}</h2>
              </div>
              <div className="class-picker-cards">
                {teaching.length ? (
                  teaching.map(classCard)
                ) : (
                  <p>{i18n.t("screenCopy:teacherConsole.noTeachingClassesCreatedYet")}</p>
                )}
              </div>
            </section>
          </>
        )}
        {!preview && guideOpen && !continuingSetup && !readOnly && (
          <form
            id="class-picker-new-class"
            aria-label={statusText("addClass")}
            className="class-picker-add"
            data-save-state={busy ? "saving" : error ? "error" : name.trim() ? "dirty" : "saved"}
            onSubmit={(event) => {
              event.preventDefault();
              if (!name.trim() || busy) return;
              const createdName = name.trim();
              setBusy(true);
              setError("");
              void onAddClass(createdName, role, sharedDataClassId || undefined)
                .then(() => {
                  setName("");
                  setSharedDataClassId("");
                  if (guideOpen) onGuideOpen();
                  onTourClassCreated(createdName);
                })
                .catch((caught) =>
                  setError(
                    caught instanceof Error
                      ? caught.message
                      : i18n.t("screenCopy:teacherConsole.couldNotCreateTheClass"),
                  ),
                )
                .finally(() => setBusy(false));
            }}
          >
            <label htmlFor="class-picker-class-name">
              {i18n.t("screenCopy:teacherConsole.newClassName")}
              <ClassNameInput
                id="class-picker-class-name"
                data-tour="class-name"
                value={name}
                onValueChange={(value) => {
                  setName(value);
                  setSharedDataClassId(suggestedSharedClassId(value, role, classes));
                }}
                placeholder={i18n.t("screenCopy:teacherConsole.eGAChinese")}
                maxLength={60}
                required
              />
            </label>
            <label>
              {i18n.t("screenCopy:teacherConsole.classPurpose")}
              <select
                data-tour="class-role"
                value={role}
                onChange={(event) => {
                  const nextRole = event.target.value as Classroom["classRole"];
                  setRole(nextRole);
                  setSharedDataClassId(suggestedSharedClassId(name, nextRole, classes));
                }}
              >
                <option value="teaching">{i18n.t("classSetup:steps.1")}</option>
                <option value="homeroom">{i18n.t("classSetup:steps.0")}</option>
              </select>
            </label>
            {sourceClasses.length > 0 && (
              <label className="class-picker-share">
                {i18n.t("classroomInterface:rosterAndSeating")}
                <select value={sharedDataClassId} onChange={(event) => setSharedDataClassId(event.target.value)}>
                  <option value="">{i18n.t("screenCopy:teacherConsole.createNewData")}</option>
                  {sourceClasses.map((item) => (
                    <option key={item.id} value={item.id}>
                      {i18n.t("screenCopy:teacherConsole.useDataFrom")}
                      {item.name}
                      {i18n.t("screenCopy:teacherConsole.label")}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <button type="submit" data-tour="class-submit" disabled={busy || !name.trim()}>
              {busy ? i18n.t("screenCopy:teacherConsole.creating") : i18n.t("screenCopy:teacherConsole.addClass")}
            </button>
            {error && <p role="alert">{error}</p>}
          </form>
        )}
        <footer className="class-picker-footer">
          <div>
            <button type="button" className="primary-action" onClick={onManage}>
              {i18n.t("guidedTour:steps.demoRoster.title")}
            </button>
          </div>
          {sessionActions}
        </footer>
      </section>
    </main>
  );
}

function upsert<T extends { id: string }>(items: T[], next: T): T[] {
  return items.some((item) => item.id === next.id)
    ? items.map((item) => (item.id === next.id ? next : item))
    : [next, ...items];
}

type PanelContext = {
  sessionStatus?: React.ReactNode;
  readOnly?: boolean;
  workspace: Workspace;
  workspaceId: string;
  classItem?: Classroom;
  students: Student[];
  preview: boolean;
  recordTerm?: AcademicTerm;
  defaultRecordDate?: string;
  toast: (message: string) => void;
};

function EmptyClass({ onManage }: { onManage: () => void }) {
  return (
    <section className="empty-state paper-card">
      <span>班</span>
      <h2>{i18n.t("screenCopy:teacherConsole.createYourFirstClass")}</h2>
      <button className="primary-action" onClick={onManage}>
        {i18n.t("screenCopy:teacherConsole.goToClassRoster")}
      </button>
    </section>
  );
}

function ToolSettingsPanel({
  hiddenTools,
  localMode,
  onSave,
  toast,
}: {
  hiddenTools: string[];
  localMode: boolean;
  onSave: (ids: string[]) => Promise<void>;
  toast: (message: string) => void;
}) {
  const [draft, setDraft] = useState<string[]>(hiddenTools);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => setDraft(hiddenTools), [hiddenTools.join("|")]);
  const available = configurableNav.filter((item) => item.id !== "quiz" || (QUIZ_RELEASED && !localMode));
  const changed = available.some((item) => draft.includes(item.id) !== hiddenTools.includes(item.id));
  const groups = [
    {
      title: i18n.t("marketing:homeroomToolsTitle"),
      items: available.filter((item) => item.roles?.includes("homeroom")),
    },
    {
      title: i18n.t("marketing:teachingToolsTitle"),
      items: available.filter((item) => item.roles?.includes("teaching")),
    },
    { title: i18n.t("screenCopy:teacherConsole.sharedTools"), items: available.filter((item) => !item.roles) },
  ];
  async function save() {
    try {
      setError("");
      setSaving(true);
      await onSave([
        ...hiddenTools.filter((id) => !available.some((item) => item.id === id)),
        ...available.filter((item) => draft.includes(item.id)).map((item) => item.id),
      ]);
      toast(i18n.t("screenCopy:teacherConsole.toolVisibilitySettingsSaved"));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : i18n.t("screenCopy:teacherConsole.couldNotSaveSettings"));
    } finally {
      setSaving(false);
    }
  }
  return (
    <div
      className="tool-settings-layout"
      data-save-state={saving ? "saving" : error ? "error" : changed ? "dirty" : "saved"}
    >
      <ToolPageToolbar
        scope={
          <div>
            <h2 className="sr-only">{i18n.t("guidedTour:steps.demoToolSettings.title")}</h2>
            <span>
              {available.length - draft.filter((id) => available.some((item) => item.id === id)).length}／
              {available.length} {i18n.t("screenCopy:teacherConsole.visible")}
            </span>
          </div>
        }
        actions={
          <>
            <button
              type="button"
              onClick={() => setDraft([])}
              disabled={!available.some((item) => draft.includes(item.id))}
            >
              {i18n.t("screenCopy:teacherConsole.showAll")}
            </button>
            <button
              className="primary-action"
              data-demo-action="tools-save"
              disabled={!changed || saving}
              onClick={() => void save()}
            >
              {saving ? i18n.t("screenCopy:teacherConsole.saving") : i18n.t("screenCopy:teacherConsole.saveSettings")}
            </button>
          </>
        }
      />
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <div className="tool-settings-grid">
        {groups.map((group) => (
          <section className="paper-card tool-settings-group" key={group.title}>
            <header>
              <h3>{group.title}</h3>
              <small>
                {group.items.filter((item) => !draft.includes(item.id)).length}／{group.items.length}{" "}
                {i18n.t("screenCopy:teacherConsole.visible")}
              </small>
            </header>
            <div>
              {group.items.map((item) => (
                <button
                  type="button"
                  role="switch"
                  aria-checked={!draft.includes(item.id)}
                  aria-label={i18n.t("screenCopy:teacherConsole.show", { label: item.label })}
                  data-demo-action={item.id === "homework" ? "tool-toggle" : undefined}
                  className={draft.includes(item.id) ? "tool-setting-row off" : "tool-setting-row"}
                  key={item.id}
                  onClick={() =>
                    setDraft((current) =>
                      current.includes(item.id) ? current.filter((id) => id !== item.id) : [...current, item.id],
                    )
                  }
                >
                  <span className="tool-setting-icon">
                    <NavIcon id={item.id} />
                  </span>
                  <b>{item.label}</b>
                  <span className="tool-setting-switch" aria-hidden="true" />
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function agendaIso(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
function agendaAddDays(iso: string, days: number) {
  const date = new Date(`${iso}T12:00:00`);
  date.setDate(date.getDate() + days);
  return agendaIso(date);
}
function agendaDateLabel(iso: string, options: Intl.DateTimeFormatOptions) {
  return new Date(`${iso}T12:00:00`).toLocaleDateString(interfaceLocale(), options);
}
function agendaOverlaps(item: CalendarEvent, start: string, end: string) {
  return item.date <= end && (item.endDate || item.date) >= start;
}

function AgendaPanel({
  sessionStatus,
  courseworkSummary,
  classes,
  events,
  onOpenEvent,
}: {
  sessionStatus?: React.ReactNode;
  courseworkSummary?: React.ReactNode;
  classes: Classroom[];
  events: CalendarEvent[];
  onOpenEvent: (item: CalendarEvent) => void;
}) {
  const [view, setView] = useNavigationState("agenda:view", "fortnight", navigationChoice(["fortnight", "year"]));
  const now = new Date();
  const initialSchoolYear = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
  const [schoolYear, setSchoolYear] = useNavigationState(
    "agenda:year",
    initialSchoolYear,
    (value): value is number => navigationInteger(value) && value >= 1900 && value <= 9998,
  );
  const [selectedMonth, setSelectedMonth] = useNavigationState(
    "agenda:month",
    now.getMonth() >= 8 ? now.getMonth() - 8 : now.getMonth() + 4,
    (value): value is number => navigationInteger(value) && value >= 0 && value < 12,
  );
  const [selectedDate, setSelectedDate] = useNavigationState("agenda:date", today, navigationDate);
  const start = today();
  const end = agendaAddDays(start, 13);
  const gridStart = agendaAddDays(start, -new Date(`${start}T12:00:00`).getDay());
  const gridEnd = agendaAddDays(end, 6 - new Date(`${end}T12:00:00`).getDay());
  const gridLength =
    Math.round((new Date(`${gridEnd}T12:00:00`).getTime() - new Date(`${gridStart}T12:00:00`).getTime()) / 86400000) +
    1;
  const gridDates = Array.from({ length: gridLength }, (_, index) => agendaAddDays(gridStart, index));
  const classNames = new Map(classes.map((item) => [item.id, item.name]));
  const relevant = events.filter((item) => !item.classId || classNames.has(item.classId));
  const sorted = (items: CalendarEvent[]) =>
    [...items].sort((a, b) => a.date.localeCompare(b.date) || a.title.localeCompare(b.title, "zh-HK"));
  const fortnightEvents = sorted(relevant.filter((item) => agendaOverlaps(item, start, end)));
  const months = Array.from({ length: 12 }, (_, index) => {
    const first = new Date(schoolYear, 8 + index, 1);
    const last = new Date(schoolYear, 9 + index, 0);
    const monthStart = agendaIso(first);
    const monthEnd = agendaIso(last);
    return {
      start: monthStart,
      end: monthEnd,
      label: i18n.t("screenCopy:teacherConsole.label_e6d84", {
        value: first.getFullYear(),
        value1: first.getMonth() + 1,
      }),
      events: sorted(relevant.filter((item) => agendaOverlaps(item, monthStart, monthEnd))),
    };
  });
  const eventScope = (item: CalendarEvent) =>
    item.classId
      ? classNames.get(item.classId) || i18n.t("screenCopy:teacherConsole.otherClasses")
      : i18n.t("screenCopy:teacherConsole.schoolWide");
  const renderEvent = (item: CalendarEvent) => {
    const scope = eventScope(item);
    if (isCalendarHoliday(item))
      return (
        <CalendarHolidayMarker
          key={item.id}
          event={item}
          scope={item.classId ? scope : undefined}
          onOpen={onOpenEvent}
          showRange
        />
      );
    return (
      <button
        type="button"
        className={`agenda-event ${calendarKind(item)}`}
        aria-description={calendarArrangementLabel(item)}
        key={item.id}
        onClick={() => onOpenEvent(item)}
        aria-label={i18n.t("screenCopy:teacherConsole.viewCalendar", {
          value: agendaDateLabel(item.date, { month: "numeric", day: "numeric" }),
          scope: scope,
          value2: calendarKindLabel(item),
          title: item.title,
        })}
      >
        <span className="agenda-event-meta">
          <span className="agenda-event-scope">{scope}</span>
          <span>{calendarKindLabel(item)}</span>
          <CalendarArrangement event={item} />
          {item.endDate && item.endDate !== item.date && (
            <span>
              {i18n.t("classroomPerformance:filters.to")}{" "}
              {agendaDateLabel(item.endDate, { month: "numeric", day: "numeric" })}
            </span>
          )}
        </span>
        <strong>{item.title}</strong>
        {item.note && <small>{item.note}</small>}
      </button>
    );
  };
  return (
    <div className="agenda-home">
      <ToolPageHeader
        title={i18n.t("personalTools:dayLabel")}
        sessionStatus={sessionStatus}
        navigation={
          <ToolPageNavigation role="group" label={i18n.t("screenCopy:teacherConsole.agendaRange")}>
            <button
              type="button"
              aria-pressed={view === "fortnight"}
              className={view === "fortnight" ? "active" : ""}
              onClick={() => setView("fortnight")}
            >
              {i18n.t("screenCopy:teacherConsole.nextTwoWeeks")}
            </button>
            <button
              type="button"
              data-demo-action="today-year"
              aria-pressed={view === "year"}
              className={view === "year" ? "active" : ""}
              onClick={() => setView("year")}
            >
              {i18n.t("screenCopy:teacherConsole.fullYear")}
            </button>
          </ToolPageNavigation>
        }
      />
      <PublicHolidayNotice
        years={view === "year" ? [schoolYear, schoolYear + 1] : [Number(start.slice(0, 4)), Number(end.slice(0, 4))]}
      />
      {courseworkSummary}
      {view === "fortnight" ? (
        <section className="agenda-content" aria-label={i18n.t("screenCopy:teacherConsole.eventsInTheNextTwoWeeks")}>
          <header className="agenda-range-head">
            <h2>
              {agendaDateLabel(start, { year: "numeric", month: "long", day: "numeric" })}{" "}
              {i18n.t("classroomPerformance:filters.to")}{" "}
              {agendaDateLabel(end, { year: "numeric", month: "long", day: "numeric" })}
            </h2>
            <span>
              <CalendarEventCount events={fortnightEvents} />
            </span>
          </header>
          <div
            className="agenda-calendar"
            role="group"
            aria-label={i18n.t("screenCopy:teacherConsole.nextTwoWeeksCalendar")}
          >
            {weekdayLabels().map((day) => (
              <span className="agenda-calendar-weekday" key={day}>
                {day}
              </span>
            ))}
            {gridDates.map((date) => {
              const dayEvents = sorted(relevant.filter((item) => agendaOverlaps(item, date, date)));
              const holidays = dayEvents.filter(isCalendarHoliday);
              const reminders = dayEvents.filter((item) => !isCalendarHoliday(item));
              const inRange = date >= start && date <= end;
              return (
                <div
                  key={date}
                  className={`agenda-calendar-day${date === start ? " today" : ""}${selectedDate === date ? " selected" : ""}${inRange ? "" : " outside"}${isHongKongPublicHolidayDate(date) || holidays.length ? " holiday" : ""}`}
                >
                  <button
                    type="button"
                    className="agenda-calendar-date"
                    aria-pressed={selectedDate === date}
                    aria-label={i18n.t("screenCopy:teacherConsole.events_5f353", {
                      value: agendaDateLabel(date, { year: "numeric", month: "long", day: "numeric", weekday: "long" }),
                      length: reminders.length,
                    })}
                    onClick={() => setSelectedDate(date)}
                  >
                    <time dateTime={date}>{Number(date.slice(-2))}</time>
                    {date.slice(8) === "01" && (
                      <small>
                        {Number(date.slice(5, 7))} {i18n.t("screenCopy:teacherConsole.month")}
                      </small>
                    )}
                    {reminders.length > 0 && (
                      <span className="agenda-calendar-count">
                        {i18n.t("classroomInterface:shortEventCount", { count: reminders.length })}
                      </span>
                    )}
                  </button>
                  {holidays.map((item) => (
                    <CalendarHolidayMarker
                      key={item.id}
                      event={item}
                      date={date}
                      scope={item.classId ? eventScope(item) : undefined}
                      onOpen={onOpenEvent}
                    />
                  ))}
                  {reminders.slice(0, 2).map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      className={`agenda-calendar-event ${calendarKind(item)}`}
                      aria-description={calendarArrangementLabel(item)}
                      aria-label={i18n.t("screenCopy:teacherConsole.viewCalendar_89ab2", {
                        value: eventScope(item),
                        title: item.title,
                      })}
                      title={`${eventScope(item)}｜${item.title}`}
                      onClick={() => onOpenEvent(item)}
                    >
                      <small>{eventScope(item)}</small>
                      <span>{item.title}</span>
                      <CalendarArrangement event={item} />
                    </button>
                  ))}
                  {reminders.length > 2 && (
                    <button type="button" className="agenda-calendar-more" onClick={() => setSelectedDate(date)}>
                      {i18n.t("classroomInterface:moreEvents", { count: reminders.length - 2 })}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <section
            className="paper-card agenda-fortnight-list"
            aria-label={i18n.t("screenCopy:teacherConsole.allTasksInTheNextTwoWeeks")}
          >
            <header>
              <h3>{i18n.t("screenCopy:teacherConsole.allTasksInTheNextTwoWeeks")}</h3>
              <span>
                <CalendarEventCount events={fortnightEvents} />
              </span>
            </header>
            {fortnightEvents.length ? (
              <div>
                {fortnightEvents.map((item) => (
                  <div
                    className={`agenda-fortnight-row${isCalendarHoliday(item) ? " holiday" : ""}${agendaOverlaps(item, selectedDate, selectedDate) ? " selected" : ""}`}
                    key={item.id}
                  >
                    <time dateTime={item.date}>
                      {agendaDateLabel(item.date, { month: "numeric", day: "numeric", weekday: "short" })}
                    </time>
                    {renderEvent(item)}
                  </div>
                ))}
              </div>
            ) : (
              <p className="agenda-empty">{i18n.t("screenCopy:teacherConsole.noTasksInTheNextTwoWeeks")}</p>
            )}
          </section>
        </section>
      ) : (
        <section className="agenda-content" aria-label={i18n.t("screenCopy:teacherConsole.fullYearEvents")}>
          <header className="agenda-range-head agenda-year-head">
            <div>
              <small>{i18n.t("screenCopy:teacherConsole.schoolYearOverviewSeptemberToAugust")}</small>
              <h2>
                {schoolYear}–{schoolYear + 1} {i18n.t("schoolYears:selector")}
              </h2>
            </div>
            <div className="agenda-year-actions">
              <button
                type="button"
                aria-label={i18n.t("screenCopy:teacherConsole.previousSchoolYear")}
                className="calendar-arrow"
                onClick={() => {
                  setSchoolYear((value) => value - 1);
                  setSelectedMonth(0);
                }}
              >
                <CalendarArrowIcon direction="left" />
              </button>
              <button
                type="button"
                onClick={() => {
                  setSchoolYear(initialSchoolYear);
                  setSelectedMonth(now.getMonth() >= 8 ? now.getMonth() - 8 : now.getMonth() + 4);
                }}
              >
                {i18n.t("schoolYears:dateCurrent")}
              </button>
              <button
                type="button"
                aria-label={i18n.t("screenCopy:teacherConsole.nextSchoolYear")}
                className="calendar-arrow"
                onClick={() => {
                  setSchoolYear((value) => value + 1);
                  setSelectedMonth(0);
                }}
              >
                <CalendarArrowIcon direction="right" />
              </button>
            </div>
          </header>
          <div className="agenda-month-grid">
            {months.map((month, index) => (
              <button
                type="button"
                key={month.start}
                className={`agenda-month${selectedMonth === index ? " active" : ""}`}
                aria-pressed={selectedMonth === index}
                onClick={() => setSelectedMonth(index)}
              >
                <span>{month.label}</span>
                <strong>
                  <CalendarEventCount events={month.events} />
                </strong>
                <small>
                  {month.events.length
                    ? month.events
                        .slice(0, 2)
                        .map((item) => item.title)
                        .join(" · ")
                    : i18n.t("screenCopy:teacherConsole.noEvents")}
                </small>
              </button>
            ))}
          </div>
          <section
            className="paper-card agenda-month-detail"
            aria-label={i18n.t("screenCopy:teacherConsole.events_9916d", { label: months[selectedMonth]?.label })}
          >
            <header>
              <h3>{months[selectedMonth]?.label}</h3>
              <span>
                <CalendarEventCount events={months[selectedMonth]?.events || []} />
              </span>
            </header>
            {months[selectedMonth]?.events.length ? (
              <div className="agenda-month-list">
                {months[selectedMonth]!.events.map((item) => (
                  <div className="agenda-month-row" key={item.id}>
                    <time dateTime={item.date}>
                      {agendaDateLabel(
                        item.date < months[selectedMonth]!.start ? months[selectedMonth]!.start : item.date,
                        { month: "numeric", day: "numeric" },
                      )}
                    </time>
                    {renderEvent(item)}
                  </div>
                ))}
              </div>
            ) : (
              <p className="agenda-empty">{i18n.t("screenCopy:teacherConsole.noEventsThisMonth")}</p>
            )}
          </section>
        </section>
      )}
    </div>
  );
}

function RecordsPanel({
  readOnly = false,
  workspaceId,
  classItem,
  students,
  mode,
  records,
  charts,
  onSave,
  onSaveDraw,
  drawEnabled,
  onNavigate,
  recordTerm,
  defaultRecordDate,
}: PanelContext & {
  mode: "materials" | "answers";
  records: DailyRecord[];
  charts: SeatingChart[];
  onSave: (classId: string, date: string, action: DailyRecordAction) => Promise<unknown> | void;
  onSaveDraw: (classId: string, action: DrawAction) => Promise<DrawState>;
  drawEnabled: boolean;
  onNavigate: (tab: Tab) => void;
}) {
  const { t } = useTranslation("keyboard");
  const { t: answerText } = useTranslation("answerRanking");
  const { t: drawText } = useTranslation("responseDraw");
  const draw = useResponseStudentDraw({
    classroom: classItem,
    students,
    enabled: drawEnabled,
    readOnly,
    onSave: onSaveDraw,
  });
  const [recordedSelectionId, setRecordedSelectionId] = useState<string>();
  const [date, setDate] = useState(defaultRecordDate || today());
  const [pendingActions, setPendingActions] = useState<DailyRecordAction[]>([]);
  const pendingRef = useRef<DailyRecordAction[]>([]);
  const savingRef = useRef(false);
  const failedRef = useRef(false);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const [saveError, setSaveError] = useState("");
  const existing = records
    .filter((item) => item.classId === classItem?.id && item.date === date)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
  const base: DailyRecord = existing || {
    id: `${classItem?.id}_${date}`,
    workspaceId,
    classId: classItem?.id || "",
    date,
    students: {},
    updatedAt: "",
  };
  const draft = pendingActions.reduce(applyDailyRecordAction, base).students;
  const saving = pendingActions.length > 0 && !saveError;
  const displayedSaveState = draw.saving
    ? "saving"
    : pendingActions.length
      ? saveError
        ? "dirty"
        : "saving"
      : existing
        ? "saved"
        : "idle";
  useSaveBeforeUnload(pendingActions.length > 0);
  const activeStudents = students.filter((student) => student.active);
  const chart = charts.find((item) => item.classId === classItem?.id);
  const summaries = buildStudentSummaries(
    students,
    records.filter((item) => item.classId === classItem?.id),
    [],
  );
  const summaryByStudent = new Map(summaries.map((item) => [item.studentId, item]));
  const savedToday = existing?.students || {};
  const totals = activeStudents.map((student) => {
    const saved = summaryByStudent.get(student.id);
    const current = draft[student.id] || { missingMaterials: false, answers: 0 };
    const previous = savedToday[student.id] || { missingMaterials: false, answers: 0 };
    return {
      studentId: student.id,
      missingMaterials: Math.max(
        0,
        (saved?.missingMaterials || 0) - (previous.missingMaterials ? 1 : 0) + (current.missingMaterials ? 1 : 0),
      ),
      answers: Math.max(0, (saved?.answers || 0) - previous.answers + current.answers),
    };
  });
  const totalByStudent = new Map(totals.map((item) => [item.studentId, item]));
  const answerRank = answerMedalRanks(totals);
  const capacity = chart ? chart.rows * chart.columns : 0;
  const visibleSeatIndexes = Array.from({ length: capacity }, (_, index) => capacity - 1 - index);
  const recordColumnAisles = chart
    ? chart.columnAisles.map((index) => chart.columns - 2 - index).sort((a, b) => a - b)
    : [];
  const recordRowAisles = chart ? chart.rowAisles.map((index) => chart.rows - 2 - index).sort((a, b) => a - b) : [];

  async function savePending() {
    if (savingRef.current || !classItem) return;
    savingRef.current = true;
    try {
      while (pendingRef.current.length) {
        const action = pendingRef.current[0];
        await onSaveRef.current(classItem.id, date, action);
        pendingRef.current = pendingRef.current.filter((item) => item.id !== action.id);
        setPendingActions([...pendingRef.current]);
      }
    } catch (caught) {
      failedRef.current = true;
      setSaveError(`${caught instanceof Error ? caught.message : t("recordSaveFailed")} ${t("recordDraftRetained")}`);
    } finally {
      savingRef.current = false;
    }
  }

  function updateStudent(
    studentId: string,
    change: { type: "answer"; delta: 1 | -1 } | { type: "materials"; missing: boolean },
  ) {
    if (readOnly || !classItem) return;
    pendingRef.current.push({ id: id("daily-action"), studentId, ...change });
    setPendingActions([...pendingRef.current]);
    if (!failedRef.current) void savePending();
  }

  function retrySave() {
    if (readOnly) return;
    failedRef.current = false;
    setSaveError("");
    void savePending();
  }

  return (
    <section
      className="records-classroom"
      aria-label={t(mode === "materials" ? "materialsRegion" : "answersRegion")}
      aria-busy={saving || draw.saving}
      data-autosave
      data-save-state={displayedSaveState}
    >
      <h2 className="sr-only">
        {mode === "materials" ? i18n.t("keyboard:materialsRegion") : i18n.t("keyboard:answersRegion")}
      </h2>
      <ToolPageToolbar
        scope={
          <div className="records-heading-actions" role="group" aria-label={t("saveTopGroup")}>
            <span className="record-autosave-status" role="status" aria-label={t("recordSaveStatus")}>
              {t(saveError ? "recordSaveFailed" : saving ? "recordSaving" : existing ? "recordSaved" : "recordReady")}
            </span>
            <label>
              {i18n.t("classroomPerformance:students.date")}
              <input
                min={recordTerm?.startDate}
                max={recordTerm?.endDate}
                type="date"
                disabled={pendingActions.length > 0 || draw.saving}
                value={date}
                onChange={(event) => setDate(event.target.value)}
              />
            </label>
          </div>
        }
        actions={
          mode === "answers" && (
            <div className="response-draw-actions">
              <span className="response-draw-count">
                {drawText("remaining", { remaining: draw.remainingIds.length, total: activeStudents.length })}
              </span>
              <button
                type="button"
                className="primary-action"
                disabled={readOnly || draw.drawing || draw.saving || !draw.remainingIds.length}
                onClick={draw.draw}
              >
                {draw.drawing
                  ? i18n.t("screenCopy:teacherConsole.selecting")
                  : draw.saving
                    ? i18n.t("screenCopy:teacherConsole.saving")
                    : !draw.remainingIds.length && activeStudents.length
                      ? i18n.t("screenCopy:teacherConsole.roundComplete")
                      : drawText(draw.result ? "again" : "pick")}
              </button>
              {draw.remainingIds.length < activeStudents.length && (
                <button type="button" disabled={readOnly || draw.drawing || draw.saving} onClick={draw.reset}>
                  {i18n.t(
                    draw.remainingIds.length
                      ? "screenCopy:teacherConsole.resetRoster"
                      : "screenCopy:teacherConsole.startNextRound",
                  )}
                </button>
              )}
            </div>
          )
        }
      />

      {mode === "answers" && draw.saveFailed && (
        <p className="form-error" role="alert">
          {i18n.t("drawHistory:saveFailed")}
        </p>
      )}

      {saveError && (
        <div className="record-autosave-error">
          <p className="form-error" role="alert" aria-label={t("recordSaveError")}>
            {saveError}
          </p>
          <button type="button" disabled={readOnly} onClick={retrySave}>
            {t("recordRetry")}
          </button>
        </div>
      )}
      <div className={`records-summary ${mode}`} aria-label={i18n.t("screenCopy:teacherConsole.classStatistics")}>
        {mode === "materials" ? (
          <>
            <div>
              <strong>{totals.reduce((sum, item) => sum + item.missingMaterials, 0)}</strong>
              <span>{i18n.t("screenCopy:teacherConsole.totalMissingMaterials")}</span>
            </div>
            <div>
              <strong>{Object.values(draft).filter((item) => item.missingMaterials).length}</strong>
              <span>{i18n.t("screenCopy:teacherConsole.missingMaterialsToday")}</span>
            </div>
          </>
        ) : (
          <>
            <div>
              <strong>{totals.reduce((sum, item) => sum + item.answers, 0)}</strong>
              <span>{i18n.t("screenCopy:teacherConsole.totalResponses")}</span>
            </div>
            <div>
              <strong>{totals.filter((item) => item.answers > 0).length}</strong>
              <span>{i18n.t("screenCopy:teacherConsole.studentsWhoHaveAnswered")}</span>
            </div>
            <div>
              <strong>{Object.values(draft).reduce((sum, item) => sum + item.answers, 0)}</strong>
              <span>{i18n.t("screenCopy:teacherConsole.responsesToday")}</span>
            </div>
          </>
        )}
      </div>

      <section className={`paper-card classroom-record-board ${mode}`}>
        {mode === "answers" && (draw.drawing || draw.saving || draw.result) && (
          <div className="response-draw-result">
            <div
              role="status"
              aria-label={drawText("result")}
              aria-live={draw.drawing ? "off" : "polite"}
              aria-atomic="true"
            >
              {draw.drawing || draw.saving ? (
                <>
                  <span className="response-draw-hint">
                    {i18n.t(draw.drawing ? "screenCopy:teacherConsole.selecting" : "screenCopy:teacherConsole.saving")}
                  </span>
                  <strong className="response-draw-name" aria-hidden="true">
                    {activeStudents.find((student) => student.id === draw.highlightedId)?.name}
                  </strong>
                </>
              ) : (
                draw.result && (
                  <>
                    <span className="response-draw-hint">{i18n.t("screenCopy:teacherConsole.selectedThisTime")}</span>
                    <strong className="response-draw-name">
                      {drawText("student", { number: studentNumberLabel(draw.result), name: draw.result.name })}
                    </strong>
                    {!Object.values(chart?.seats || {}).includes(draw.result.studentId) && (
                      <small className="response-draw-hint">{drawText("unseated")}</small>
                    )}
                  </>
                )
              )}
            </div>
            {draw.result && (
              <button
                type="button"
                disabled={readOnly || recordedSelectionId === draw.result.id}
                onClick={() => {
                  if (!draw.result || readOnly || recordedSelectionId === draw.result.id) return;
                  setRecordedSelectionId(draw.result.id);
                  updateStudent(draw.result.studentId, { type: "answer", delta: 1 });
                }}
              >
                {drawText(recordedSelectionId === draw.result.id ? "recorded" : "record")}
              </button>
            )}
          </div>
        )}
        {!chart || !Object.keys(chart.seats).length ? (
          <div className="record-seat-empty">
            <span>座</span>
            <h3>{i18n.t("screenCopy:teacherConsole.noSeatingChartYet")}</h3>
            <button className="primary-action" onClick={() => onNavigate("seating")}>
              {i18n.t("screenCopy:teacherConsole.openSeatingChart")}
            </button>
          </div>
        ) : (
          <>
            <div className="record-view-label">
              <span>{classItem?.name}</span>
              <small>
                {i18n.t("screenCopy:teacherConsole.teacherView")} {chart.rows}{" "}
                {i18n.t("screenCopy:teacherConsole.rows")} {chart.columns} {i18n.t("screenCopy:teacherConsole.columns")}{" "}
                {chart.rowAisles.length + chart.columnAisles.length} {i18n.t("screenCopy:teacherConsole.aisles")}
              </small>
            </div>
            <div className="record-seat-scroller">
              <div
                className="record-seat-grid"
                style={{
                  gridTemplateColumns: seatingTracks(
                    chart.columns,
                    recordColumnAisles,
                    "minmax(112px, 1fr)",
                    "12px",
                    "46px",
                  ),
                  gridTemplateRows: seatingTracks(chart.rows, recordRowAisles, "minmax(112px, auto)", "10px", "36px"),
                }}
              >
                {visibleSeatIndexes.map((seatIndex, visualIndex) => {
                  const row = Math.floor(seatIndex / chart.columns);
                  const column = seatIndex % chart.columns;
                  const visualRow = Math.floor(visualIndex / chart.columns) * 2 + 1;
                  const visualColumn = (visualIndex % chart.columns) * 2 + 1;
                  const position = { gridRow: visualRow, gridColumn: visualColumn };
                  const student = activeStudents.find((item) => item.id === chart.seats[`${row}_${column}`]);
                  if (!student)
                    return (
                      <div className="record-seat empty" style={position} key={seatIndex}>
                        <span>{i18n.t("localExport:printEmptySeat")}</span>
                      </div>
                    );
                  const item = draft[student.id] || { missingMaterials: false, answers: 0 };
                  const total = totalByStudent.get(student.id) || { missingMaterials: 0, answers: 0 };
                  const rank = answerRank.get(student.id);
                  if (mode === "materials")
                    return (
                      <article
                        className={`record-seat occupied ${studentGenderClass(student)}${item.missingMaterials ? " missing" : ""}`}
                        style={position}
                        key={seatIndex}
                      >
                        <button
                          className="record-seat-hit"
                          disabled={readOnly}
                          data-demo-action="record-student"
                          aria-pressed={item.missingMaterials}
                          onClick={() =>
                            updateStudent(student.id, { type: "materials", missing: !item.missingMaterials })
                          }
                        >
                          <small>
                            {studentNumberLabel(student)} {i18n.t("screenCopy:teacherConsole.no")}
                          </small>
                          <b>{student.name}</b>
                          <span>
                            {item.missingMaterials
                              ? i18n.t("screenCopy:teacherConsole.recordedTodayTotal", {
                                  missingMaterials: total.missingMaterials,
                                })
                              : i18n.t("screenCopy:teacherConsole.total", { missingMaterials: total.missingMaterials })}
                          </span>
                        </button>
                      </article>
                    );
                  return (
                    <article
                      className={`record-seat occupied ${studentGenderClass(student)}${rank ? " ranked" : ""}${draw.highlightedId === student.id ? (draw.result ? " draw-selected" : " draw-candidate") : ""}`}
                      style={position}
                      key={seatIndex}
                    >
                      {draw.result?.studentId === student.id && (
                        <span className="response-draw-seat-label" title={drawText("selected")} aria-hidden="true">
                          ✓
                        </span>
                      )}
                      {rank && (
                        <span
                          className={`answer-rank rank-${rank}`}
                          role="img"
                          aria-label={answerText("medalRank", {
                            rank,
                            medal: answerText(rank === 1 ? "gold" : rank === 2 ? "silver" : "bronze"),
                          })}
                        >
                          {rank === 1 ? "🥇" : rank === 2 ? "🥈" : "🥉"}
                        </span>
                      )}
                      <button
                        className="record-seat-hit"
                        disabled={readOnly}
                        data-demo-action="record-student"
                        onClick={() => updateStudent(student.id, { type: "answer", delta: 1 })}
                      >
                        <small>
                          {studentNumberLabel(student)} {i18n.t("screenCopy:teacherConsole.no")}
                        </small>
                        <b>{student.name}</b>
                        <span className="answer-total">
                          <strong>{total.answers}</strong> <span>{i18n.t("screenCopy:teacherConsole.answers")}</span>
                        </span>
                      </button>
                      <button
                        className="answer-minus"
                        data-demo-action="record-minus"
                        aria-label={t("decreaseAnswer", { number: studentNumberLabel(student), name: student.name })}
                        title={t("decreaseAnswer", { number: studentNumberLabel(student), name: student.name })}
                        disabled={readOnly || item.answers === 0}
                        onClick={(event) => {
                          event.stopPropagation();
                          updateStudent(student.id, { type: "answer", delta: -1 });
                        }}
                      >
                        <svg
                          aria-hidden="true"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                        >
                          <path d="M5 12h14" />
                        </svg>
                      </button>
                    </article>
                  );
                })}
              </div>
            </div>
            <div className="record-blackboard">
              {i18n.t("localExport:printBoard")} <small>BLACKBOARD</small>
            </div>
          </>
        )}
      </section>
    </section>
  );
}

function HomeworkPanel({
  readOnly = false,
  workspaceId,
  classItem,
  onSave,
  onOpenCalendar,
  date,
  onDateChange,
  onClose,
  toast,
  recordTerm,
}: PanelContext & {
  onSave: (item: CalendarEvent) => Promise<unknown> | void;
  onOpenCalendar: (date: string) => void;
  date: string;
  onDateChange: (date: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation("classroomInterface");
  const [itemType, setItemType] = useState<string>("功課");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    nameRef.current?.focus();
  }, []);
  useEffect(() => {
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape" && !saving && formRef.current?.contains(document.activeElement)) {
        event.preventDefault();
        onClose();
      }
    }
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [saving, onClose]);
  async function create(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!classItem || saving) return;
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const title = String(form.get("title") || "").trim();
    const date = String(form.get("date") || "");
    const endDate = String(form.get("endDate") || "");
    if (!title || !date) {
      setError(t("required"));
      return;
    }
    if (endDate && endDate < date) {
      setError(t("endBeforeStart"));
      return;
    }
    const schoolKind = calendarKinds.find((kind) => kind.key === itemType && kind.type === "school");
    const now = isoNow();
    try {
      setError("");
      setSaving(true);
      await onSave({
        id: id("event"),
        workspaceId,
        classId: classItem.id,
        date,
        endDate: endDate || undefined,
        allDay: true,
        timezone: "Asia/Hong_Kong",
        title,
        type: schoolKind ? "school" : TEACHING_ITEM_EVENT_TYPES[itemType as keyof typeof TEACHING_ITEM_EVENT_TYPES],
        ...(schoolKind ? { schoolKind: schoolKind.key as CalendarEvent["schoolKind"] } : {}),
        note: String(form.get("note") || "").trim(),
        source: { kind: "manual", sourceName: "課堂事項" },
        createdAt: now,
        updatedAt: now,
      });
      toast(t("saved", { title }));
      onOpenCalendar(date);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t("saveFailed"));
    } finally {
      setSaving(false);
    }
  }
  return (
    <section className="calendar-entry" aria-labelledby="classroom-entry-title">
      <form
        ref={formRef}
        className="paper-card compact-form teaching-item-form"
        onSubmit={(event) => void create(event)}
      >
        <header className="teaching-item-heading">
          <h2 className="teaching-item-title" id="classroom-entry-title">
            {t("entryTitle")}
          </h2>
          <button
            type="button"
            className="teaching-item-close"
            aria-label={t("closeEntry")}
            disabled={readOnly || saving}
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <fieldset className="teaching-item-types" disabled={readOnly || saving}>
          <legend>{t("itemType")}</legend>
          <div className="teaching-item-type-options">
            {[...TEACHING_ITEM_TYPES, "other"].map((type) => (
              <label key={type} className={itemType === type ? "selected" : ""}>
                <input
                  className="teaching-item-radio"
                  type="radio"
                  name="itemType"
                  value={type}
                  checked={itemType === type}
                  data-demo-action={type === "默書" ? "homework-type" : undefined}
                  onChange={() => setItemType(type)}
                />
                {type === "other" ? t("other") : teachingItemLabel(type)}
              </label>
            ))}
          </div>
        </fieldset>
        <label>
          {t("itemName")}
          <input
            ref={nameRef}
            name="title"
            data-demo-action="calendar-title"
            required
            maxLength={80}
            placeholder={t("titlePlaceholder")}
          />
        </label>
        <label>
          {t("date")}
          <input
            name="date"
            min={recordTerm?.startDate}
            max={recordTerm?.endDate}
            type="date"
            value={date}
            onChange={(event) => onDateChange(event.target.value)}
            required
          />
        </label>
        <label>
          {t("note")}
          <textarea
            name="note"
            data-demo-action="homework-note"
            rows={3}
            maxLength={500}
            placeholder={t("notePlaceholder")}
          />
        </label>
        <details className="teaching-item-extra">
          <summary className="teaching-item-extra-summary">{t("moreSettings")}</summary>
          <fieldset className="teaching-item-types" disabled={readOnly || saving}>
            <legend>{t("schoolType")}</legend>
            <div className="teaching-item-type-options">
              {calendarKinds
                .filter((kind) => kind.type === "school")
                .map((kind) => (
                  <label key={kind.key} className={itemType === kind.key ? "selected" : ""}>
                    <input
                      className="teaching-item-radio"
                      type="radio"
                      name="itemType"
                      value={kind.key}
                      checked={itemType === kind.key}
                      onChange={() => setItemType(kind.key)}
                    />
                    {kind.label}
                  </label>
                ))}
            </div>
          </fieldset>
          <label>
            {t("endDate")}
            <input name="endDate" type="date" />
          </label>
        </details>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="teaching-item-actions">
          <button type="button" disabled={readOnly || saving} onClick={onClose}>
            {t("cancel")}
          </button>
          <button className="primary-action" data-demo-action="homework-save" disabled={readOnly || saving}>
            {saving ? t("saving") : t("save")}
          </button>
        </div>
      </form>
    </section>
  );
}

function GroupsPanel({
  sessionStatus,
  students,
  readOnly = false,
  classItem,
  onSaveDraw,
  toast,
}: PanelContext & {
  onSaveDraw: (classId: string, action: DrawAction) => Promise<DrawState>;
}) {
  const { t: historyText } = useTranslation("drawHistory");
  const { t: groupText } = useTranslation("randomGroups");
  const [historyPage, setHistoryPage] = useState(1);
  const active = useMemo(() => students.filter((item) => item.active), [students]);
  const activeKey = active.map((student) => student.id).join("|");
  const activeIds = useMemo(() => active.map((student) => student.id), [activeKey]);
  const [page, setPage] = useNavigationState(
    `groups:${classItem?.id}:page`,
    "draw",
    navigationChoice(["draw", "groups"]),
  );
  const [drawn, setDrawn] = useState<Student>();
  const [drawing, setDrawing] = useState(false);
  const [savingDraw, setSavingDraw] = useState(false);
  useSaveBeforeUnload(savingDraw);
  const [dateKey, setDateKey] = useState(hongKongDate);
  const [drawState, setDrawState] = useState<DrawState | undefined>(classItem?.drawState);
  const drawMode = drawState?.mode || "daily";
  const remainingIds = availableStudentIds(activeIds, drawState, dateKey);
  const [groupMode, setGroupMode] = useState<"size" | "count">("size");
  const [groupValue, setGroupValue] = useState(4);
  const [groups, setGroups] = useState<Student[][]>([]);
  const drawTimersRef = useRef<{ reel?: number; reveal?: number }>({});

  useEffect(() => {
    window.clearInterval(drawTimersRef.current.reel);
    window.clearTimeout(drawTimersRef.current.reveal);
    setDrawn(undefined);
    setDrawing(false);
    setGroups([]);
    return () => {
      window.clearInterval(drawTimersRef.current.reel);
      window.clearTimeout(drawTimersRef.current.reveal);
    };
  }, [activeKey]);
  useEffect(() => {
    setDrawState(classItem?.drawState);
  }, [classItem?.id, classItem?.drawState]);
  useEffect(() => {
    const timer = window.setInterval(() => setDateKey(hongKongDate()), 30000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    setDrawn(undefined);
  }, [dateKey]);

  const history = useMemo(
    () => [...(drawState?.history || [])].sort((a, b) => Date.parse(b.drawnAt) - Date.parse(a.drawnAt)),
    [drawState?.history],
  );
  const historyPages = Math.max(1, Math.ceil(history.length / 20));
  const visibleHistoryPage = Math.min(historyPage, historyPages);
  const historyRows = history.slice((visibleHistoryPage - 1) * 20, visibleHistoryPage * 20);
  const historyTime = new Intl.DateTimeFormat(interfaceLocale(), {
    timeZone: "Asia/Hong_Kong",
    dateStyle: "medium",
    timeStyle: "medium",
  });

  async function saveDrawState(action: DrawAction, result?: Student) {
    if (!classItem || savingDraw) return;
    setSavingDraw(true);
    try {
      const next = await onSaveDraw(classItem.id, action);
      setDrawState(next);
      if (result) {
        const entry = next.history?.find((item) => item.id === (action.type === "draw" ? action.entryId : ""));
        setDrawn(
          entry ? { ...result, name: entry.name, number: entry.number, sourceClass: entry.sourceClass } : result,
        );
        setHistoryPage(1);
      }
    } catch {
      if (action.type !== "clearHistory") setDrawn(undefined);
      toast(historyText(action.type === "clearHistory" ? "clearFailed" : "saveFailed"));
    } finally {
      setSavingDraw(false);
    }
  }

  function shuffle() {
    const mixed = [...active];
    for (let index = mixed.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(Math.random() * (index + 1));
      [mixed[index], mixed[swapIndex]] = [mixed[swapIndex]!, mixed[index]!];
    }
    return mixed;
  }

  function drawStudent() {
    if (!remainingIds.length || drawing || savingDraw) return;
    const studentId = remainingIds[Math.floor(Math.random() * remainingIds.length)]!;
    const finalStudent = active.find((student) => student.id === studentId);
    if (!finalStudent) return;
    setDrawing(true);
    const candidates = active.filter((student) => remainingIds.includes(student.id));
    let frame = 0;
    drawTimersRef.current.reel = window.setInterval(() => {
      setDrawn(candidates[frame % candidates.length]);
      frame += 1;
    }, 90);
    drawTimersRef.current.reveal = window.setTimeout(() => {
      window.clearInterval(drawTimersRef.current.reel);
      setDrawn(undefined);
      setDrawing(false);
      void saveDrawState({ type: "draw", studentId, entryId: crypto.randomUUID(), drawnAt: isoNow() }, finalStudent);
    }, 2000);
  }

  function resetDraw() {
    void saveDrawState({ type: "reset", date: hongKongDate() });
    setDrawn(undefined);
  }

  function clearDrawHistory() {
    if (!classItem || readOnly || drawing || savingDraw || !history.length) return;
    if (!window.confirm(historyText("clearConfirm", { name: classItem.name, count: history.length }))) return;
    void saveDrawState({ type: "clearHistory" });
  }

  function generateGroups() {
    setGroups(groupStudents(shuffle(), groupMode, groupValue));
  }

  return (
    <section
      className="groups-workspace"
      data-autosave
      data-save-state={savingDraw ? "saving" : "saved"}
      aria-label={i18n.t("screenCopy:teacherConsole.randomSelectionAndGroupingTools")}
    >
      <ToolPageHeader
        title={i18n.t("guidedTour:steps.demoGroups.title")}
        sessionStatus={sessionStatus}
        continued
        navigation={
          <ToolPageNavigation role="group" label={i18n.t("screenCopy:teacherConsole.chooseTool")}>
            <button
              type="button"
              aria-pressed={page === "draw"}
              className={page === "draw" ? "active" : ""}
              onClick={() => setPage("draw")}
            >
              {i18n.t("screenCopy:teacherConsole.pickStudents")}
            </button>
            <button
              type="button"
              data-demo-action="groups-tab"
              aria-pressed={page === "groups"}
              className={page === "groups" ? "active" : ""}
              onClick={() => setPage("groups")}
            >
              {i18n.t("screenCopy:teacherConsole.quickGroups")}
            </button>
          </ToolPageNavigation>
        }
      />

      {page === "draw" ? (
        <ToolPageToolbar
          scope={
            <DrawResetMode
              value={drawMode}
              disabled={readOnly || drawing || savingDraw}
              onChange={(mode) => void saveDrawState({ type: "mode", mode, date: hongKongDate() })}
            />
          }
          actions={
            <b>
              {active.length} {i18n.t("screenCopy:teacherConsole.studentsNoRepeatsThisRound")}
            </b>
          }
        />
      ) : (
        <ToolPageToolbar
          scope={
            <div className="group-settings">
              <div className="group-mode">
                <span>{groupText("mode")}</span>
                <SelectMenu
                  label={groupText("mode")}
                  value={groupMode}
                  options={[
                    { value: "size", label: groupText("bySize") },
                    { value: "count", label: groupText("byCount") },
                  ]}
                  onChange={(value) => {
                    if (value === "size" || value === "count") setGroupMode(value);
                  }}
                />
              </div>
              <label>
                {groupText(groupMode === "size" ? "bySize" : "byCount")}
                <input
                  type="number"
                  min={groupMode === "size" ? 2 : 1}
                  max={Math.max(2, active.length)}
                  value={groupValue}
                  onChange={(event) => setGroupValue(Number(event.target.value))}
                />
              </label>
            </div>
          }
          actions={
            <button
              type="button"
              data-demo-action="groups-generate"
              className="primary-action"
              disabled={!active.length}
              onClick={generateGroups}
            >
              {i18n.t("screenCopy:teacherConsole.createGroups")}
            </button>
          }
        />
      )}

      {page === "draw" ? (
        <section className="draw-page paper-card" aria-label={i18n.t("screenCopy:teacherConsole.pickStudents")}>
          <h2 className="sr-only">{i18n.t("screenCopy:teacherConsole.randomStudentPicker")}</h2>
          <div
            className={`draw-stage${drawing ? " drawing" : ""}`}
            aria-live={drawing ? "off" : "polite"}
            aria-busy={drawing}
            aria-atomic="true"
          >
            <div className="draw-character" aria-hidden="true">
              <span>•‿•</span>
            </div>
            <div className="draw-student">
              <small>
                {drawing
                  ? i18n.t("screenCopy:teacherConsole.selecting")
                  : drawn
                    ? i18n.t("screenCopy:teacherConsole.selectedThisTime")
                    : i18n.t("screenCopy:teacherConsole.readyToPick")}
              </small>
              <strong>{drawn ? studentNumberLabel(drawn, true) : "—"}</strong>
              <h3>{drawn?.name || i18n.t("screenCopy:teacherConsole.selectTheButtonToBegin")}</h3>
            </div>
            <div className="draw-round">
              <small>{i18n.t("screenCopy:teacherConsole.currentRound")}</small>
              <strong>
                {i18n.t("screenCopy:teacherConsole.remaining")} {remainingIds.length} / {active.length}{" "}
                {i18n.t("screenCopy:teacherConsole.students_49127")}
              </strong>
            </div>
          </div>
          <footer>
            <button
              type="button"
              className="primary-action large"
              disabled={readOnly || !remainingIds.length || drawing || savingDraw}
              onClick={drawStudent}
            >
              {drawing
                ? i18n.t("screenCopy:teacherConsole.selecting")
                : savingDraw
                  ? i18n.t("screenCopy:teacherConsole.saving")
                  : remainingIds.length
                    ? i18n.t("screenCopy:teacherConsole.pickAStudent")
                    : i18n.t("screenCopy:teacherConsole.roundComplete")}
            </button>
            <button
              type="button"
              disabled={readOnly || drawing || savingDraw || (remainingIds.length === active.length && !drawn)}
              onClick={resetDraw}
            >
              {remainingIds.length
                ? i18n.t("screenCopy:teacherConsole.resetRoster")
                : i18n.t("screenCopy:teacherConsole.startNextRound")}
            </button>
          </footer>
          <section className="draw-history" aria-label={historyText("title")}>
            <header className="draw-history-heading">
              <h3 className="draw-history-title">{historyText("title")}</h3>
              <div className="draw-history-actions">
                <span>{historyText("count", { count: history.length })}</span>
                {history.length > 0 && (
                  <button
                    type="button"
                    className="draw-history-clear"
                    disabled={readOnly || drawing || savingDraw}
                    onClick={clearDrawHistory}
                  >
                    {historyText("clear")}
                  </button>
                )}
              </div>
            </header>
            {history.length ? (
              <>
                <div className="draw-history-table">
                  <table>
                    <caption className="sr-only">{historyText("title")}</caption>
                    <thead>
                      <tr>
                        <th scope="col">{historyText("time")}</th>
                        <th scope="col">{historyText("number")}</th>
                        <th scope="col">{historyText("name")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyRows.map((entry) => (
                        <tr key={entry.id}>
                          <td>
                            <time className="draw-history-time" dateTime={entry.drawnAt}>
                              {historyTime.format(new Date(entry.drawnAt))}
                            </time>
                          </td>
                          <td>{studentNumberLabel(entry, true)}</td>
                          <td>{entry.name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {historyPages > 1 && (
                  <nav className="draw-history-pagination" aria-label={historyText("pagination")}>
                    <button
                      type="button"
                      disabled={visibleHistoryPage === 1}
                      onClick={() => setHistoryPage(visibleHistoryPage - 1)}
                    >
                      {historyText("previous")}
                    </button>
                    <span>{historyText("page", { page: visibleHistoryPage, pages: historyPages })}</span>
                    <button
                      type="button"
                      disabled={visibleHistoryPage === historyPages}
                      onClick={() => setHistoryPage(visibleHistoryPage + 1)}
                    >
                      {historyText("next")}
                    </button>
                  </nav>
                )}
              </>
            ) : (
              <p className="draw-history-empty">{historyText("empty")}</p>
            )}
          </section>
        </section>
      ) : (
        <section className="group-page paper-card" aria-label={i18n.t("screenCopy:teacherConsole.quickGroups")}>
          <h2 className="sr-only">{i18n.t("screenCopy:teacherConsole.quickBalancedGroups")}</h2>
          {groups.length ? (
            <div className="group-board" aria-live="polite">
              {groups.map((group, index) => (
                <article key={index}>
                  <header>
                    <b>{i18n.t("interfaceExtras:groupNumber", { number: index + 1 })}</b>
                    <small>
                      {group.length} {i18n.t("screenCopy:teacherConsole.students_49127")}
                    </small>
                  </header>
                  {group.map((student) => (
                    <span key={student.id}>
                      <b>{studentNumberLabel(student, true)}</b>
                      {student.name}
                    </span>
                  ))}
                </article>
              ))}
            </div>
          ) : (
            <div className="group-empty">
              <span aria-hidden="true">組</span>
              <h3>{i18n.t("screenCopy:teacherConsole.chooseAMethodToCreateGroups")}</h3>
            </div>
          )}
        </section>
      )}
    </section>
  );
}

function StudentRecordsPanel({
  readOnly = false,
  classItem,
  students,
  attendanceRecords,
  homeworkRecords,
  showAttendance,
  showHomework,
  onNavigate,
  onRequestExport,
  recordTerm,
}: PanelContext & {
  attendanceRecords: AttendanceRecord[];
  homeworkRecords: HomeroomHomeworkRecord[];
  showAttendance: boolean;
  showHomework: boolean;
  onNavigate: (tab: Tab) => void;
  onRequestExport: (studentId: string, intent?: "print") => void;
}) {
  const [requestedMode, setMode] = useNavigationState(
    `studentRecords:${classItem?.id}:mode`,
    "overview",
    navigationChoice(["overview", "attendance", "homework"]),
  );
  const mode =
    (requestedMode === "attendance" && !showAttendance) || (requestedMode === "homework" && !showHomework)
      ? "overview"
      : requestedMode;
  const [selectedId, setSelectedId] = useNavigationState(`studentRecords:${classItem?.id}:student`, "", navigationId);
  const [search, setSearch] = useState("");
  const classAttendance = attendanceRecords.filter((item) => showAttendance && item.classId === classItem?.id);
  const classHomework = homeworkRecords.filter((item) => showHomework && item.classId === classItem?.id);
  const filteredStudents = students.filter((student) =>
    `${studentNumberLabel(student)} ${studentNumberLabel(student, true)} ${student.name}`
      .toLocaleLowerCase()
      .includes(search.trim().toLocaleLowerCase()),
  );
  const directoryPagination = useStudentPagination(
    filteredStudents,
    JSON.stringify([classItem?.id, search, recordTerm?.id]),
  );
  const selected = search.trim()
    ? filteredStudents.find((student) => student.id === selectedId) || filteredStudents[0]
    : students.find((student) => student.id === selectedId) || students[0];
  const attendanceHistory = selected ? studentAttendanceHistory(classAttendance, selected.id) : [];
  const absenceHistory = attendanceHistory.filter((entry) => entry.status === "absent");
  const missingHistory = selected ? studentMissingHomeworkHistory(classHomework, selected.id) : [];
  const subjectSummary = Object.entries(
    missingHistory.reduce<Record<string, number>>(
      (counts, entry) => (entry.subject ? { ...counts, [entry.subject]: (counts[entry.subject] || 0) + 1 } : counts),
      {},
    ),
  ).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "zh-HK"));
  const activeStudents = students.filter((student) => student.active);
  const classAttendanceTotals = classAttendance.reduce(
    (totals, record) => {
      Object.values(record.exceptions).forEach((status) => {
        totals[status] += 1;
      });
      return totals;
    },
    { late: 0, absent: 0 },
  );
  const classMissingTotal = classHomework.reduce((sum, record) => sum + record.missingStudentIds.length, 0);
  const recordedDates = new Set([
    ...classAttendance.map((record) => record.date),
    ...classHomework.map((record) => record.date),
  ]).size;
  const studentSummaries = activeStudents
    .map((student) => {
      const history = studentAttendanceHistory(classAttendance, student.id);
      return {
        student,
        absent: history.filter((entry) => entry.status === "absent").length,
        late: history.filter((entry) => entry.status === "late").length,
        missing: studentMissingHomeworkHistory(classHomework, student.id).length,
      };
    })
    .filter((item) => item.absent + item.late + item.missing > 0)
    .sort(
      (a, b) =>
        b.absent + b.late + b.missing - (a.absent + a.late + a.missing) || compareStudentNumbers(a.student, b.student),
    );
  const classSubjectSummary = Object.entries(
    classHomework.reduce<Record<string, number>>(
      (counts, record) =>
        record.subject
          ? { ...counts, [record.subject]: (counts[record.subject] || 0) + record.missingStudentIds.length }
          : counts,
      {},
    ),
  ).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "zh-HK"));
  const maxSubjectCount = Math.max(1, ...classSubjectSummary.map(([, count]) => count));
  const recentClassRecords = [
    ...classAttendance.map((record) => ({
      key: `attendance_${record.id}`,
      date: record.date,
      kind: "點名",
      title: i18n.t("interfaceExtras:attendanceSummary", {
        absent: Object.values(record.exceptions).filter((status) => status === "absent").length,
        late: Object.values(record.exceptions).filter((status) => status === "late").length,
      }),
      detail: i18n.t("screenCopy:teacherConsole.attendanceRecords"),
    })),
    ...classHomework.map((record) => ({
      key: `homework_${record.id}`,
      date: record.date,
      kind: "欠交",
      title: `${record.subject ? `${record.subject}｜` : ""}${homeroomHomeworkTaskLabel(record.taskType)}`,
      detail: `${record.taskDetail || i18n.t("screenCopy:teacherConsole.noTaskDescription")} · ${i18n.t("interfaceExtras:studentCount", { count: record.missingStudentIds.length })}`,
    })),
  ]
    .sort((a, b) => b.date.localeCompare(a.date) || a.kind.localeCompare(b.kind, "zh-HK"))
    .slice(0, 8);

  return (
    <div className="student-records-layout" data-save-state="saved">
      <aside className="paper-card student-records-directory">
        <header>
          <h2>{i18n.t("adminRecords:kinds.roster")}</h2>
          <small>
            {students.length} {i18n.t("screenCopy:teacherConsole.students")}
          </small>
        </header>
        <label className="student-record-search">
          {i18n.t("screenCopy:teacherConsole.searchStudents")}
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={i18n.t("screenCopy:teacherConsole.enterNameOrClassNumber")}
          />
        </label>
        <div className="student-record-list">
          {directoryPagination.items.map((student, studentIndex) => {
            const absenceCount = studentAttendanceHistory(classAttendance, student.id).filter(
              (entry) => entry.status === "absent",
            ).length;
            const missingCount = studentMissingHomeworkHistory(classHomework, student.id).length;
            return (
              <button
                key={student.id}
                data-demo-action={studentIndex === 1 ? "student-record-person" : undefined}
                className={student.id === selected?.id ? "selected" : ""}
                onClick={() => setSelectedId(student.id)}
                aria-current={student.id === selected?.id ? "true" : undefined}
              >
                <span className="student-record-number" data-source-class={student.sourceClass}>
                  {studentNumberLabel(student, true)}
                </span>
                <span className="student-record-name">
                  <b>{student.name}</b>
                  <small>
                    {student.active ? i18n.t("screenCopy:teacherConsole.active") : i18n.t("terms:disabled")}
                  </small>
                </span>
                <span className="student-record-flags">
                  {showAttendance && (
                    <small>
                      {i18n.t("attendanceRegister:absent")} {absenceCount}
                    </small>
                  )}
                  {showHomework && (
                    <small>
                      {i18n.t("gradeOverview:statuses.missing")} {missingCount}
                    </small>
                  )}
                </span>
              </button>
            );
          })}
          {!filteredStudents.length && (
            <p className="empty-copy">{i18n.t("screenCopy:teacherConsole.noMatchingStudents")}</p>
          )}
        </div>
        <StudentPagination {...directoryPagination} label="directory" />
      </aside>
      <section className="student-records-main">
        {selected ? (
          <>
            <header
              className={`paper-card student-record-profile${mode === "overview" ? " class-overview-profile" : ""}`}
            >
              <div>
                {mode === "overview" ? (
                  <h2>{i18n.t("screenCopy:teacherConsole.classOverview")}</h2>
                ) : (
                  <h2>
                    <span>{selected ? studentNumberLabel(selected, true) : ""}</span>
                    {selected?.name}
                  </h2>
                )}
              </div>
              {mode !== "overview" && selected && (
                <div className="student-record-profile-actions">
                  <span className={selected.active ? "student-record-enrolment" : "student-record-enrolment inactive"}>
                    {selected.active ? i18n.t("screenCopy:teacherConsole.active") : i18n.t("terms:disabled")}
                  </span>
                  <RecordExportButton
                    onClick={() => onRequestExport(selected.id)}
                    onPrint={() => onRequestExport(selected.id, "print")}
                  />
                </div>
              )}
            </header>
            <div
              className="student-record-tabs"
              role="tablist"
              aria-label={i18n.t("screenCopy:teacherConsole.studentRecordCategories")}
            >
              <button
                id="student-record-overview-tab"
                role="tab"
                aria-selected={mode === "overview"}
                aria-controls="student-record-detail"
                className={mode === "overview" ? "active" : ""}
                onClick={() => setMode("overview")}
              >
                {i18n.t("screenCopy:teacherConsole.classOverview")}
              </button>
              {showAttendance && (
                <button
                  id="student-record-attendance-tab"
                  role="tab"
                  aria-selected={mode === "attendance"}
                  aria-controls="student-record-detail"
                  className={mode === "attendance" ? "active" : ""}
                  disabled={!selected}
                  onClick={() => setMode("attendance")}
                >
                  {i18n.t("screenCopy:teacherConsole.attendanceRecords")}
                </button>
              )}
              {showHomework && (
                <button
                  id="student-record-homework-tab"
                  data-demo-action="student-record-homework"
                  role="tab"
                  aria-selected={mode === "homework"}
                  aria-controls="student-record-detail"
                  className={mode === "homework" ? "active" : ""}
                  disabled={!selected}
                  onClick={() => setMode("homework")}
                >
                  {i18n.t("guidedTour:steps.demoHomeroomHomework.title")}
                </button>
              )}
            </div>
            <div
              id="student-record-detail"
              role="tabpanel"
              aria-labelledby={`student-record-${mode}-tab`}
              className="student-record-detail"
            >
              {mode === "overview" ? (
                <div className="class-record-dashboard">
                  {(showAttendance || showHomework) && (
                    <section
                      className="class-record-metrics"
                      aria-label={i18n.t("screenCopy:teacherConsole.classRecordSummary")}
                    >
                      <div className="paper-card">
                        <small>{i18n.t("screenCopy:teacherConsole.recordedDays")}</small>
                        <strong>{recordedDates}</strong>
                        <span>
                          {[
                            showAttendance && i18n.t("marketing:attendanceTab"),
                            showHomework && i18n.t("interfaceExtras:missingLabel"),
                          ]
                            .filter(Boolean)
                            .join(i18n.t("interfaceExtras:or"))}
                        </span>
                      </div>
                      {showAttendance && (
                        <div className="paper-card">
                          <small>{i18n.t("screenCopy:teacherConsole.absences")}</small>
                          <strong>{classAttendanceTotals.absent}</strong>
                          <span>
                            {classAttendance.length} {i18n.t("screenCopy:teacherConsole.attendanceChecks")}
                          </span>
                        </div>
                      )}
                      {showAttendance && (
                        <div className="paper-card">
                          <small>{i18n.t("screenCopy:teacherConsole.lateArrivals")}</small>
                          <strong>{classAttendanceTotals.late}</strong>
                          <span>
                            {activeStudents.length} {i18n.t("screenCopy:teacherConsole.activeStudents")}
                          </span>
                        </div>
                      )}
                      {showHomework && (
                        <div className="paper-card">
                          <small>{i18n.t("screenCopy:teacherConsole.missingSubmissions")}</small>
                          <strong>{classMissingTotal}</strong>
                          <span>
                            {classHomework.length} {i18n.t("screenCopy:teacherConsole.records")}
                          </span>
                        </div>
                      )}
                    </section>
                  )}
                  {(showAttendance || showHomework) && (
                    <div className="class-record-dashboard-grid">
                      <section className="paper-card class-record-attention">
                        <header>
                          <div>
                            <h3>{i18n.t("screenCopy:teacherConsole.studentsWithMoreRecords")}</h3>
                          </div>
                        </header>
                        {studentSummaries.length ? (
                          <div>
                            {studentSummaries.slice(0, 7).map((item) => (
                              <button
                                type="button"
                                key={item.student.id}
                                onClick={() => {
                                  setSelectedId(item.student.id);
                                  setMode(showHomework ? "homework" : "attendance");
                                }}
                              >
                                <span>{studentNumberLabel(item.student, true)}</span>
                                <b>{item.student.name}</b>
                                {showAttendance && (
                                  <small>
                                    {i18n.t("attendanceRegister:absent")} {item.absent}
                                  </small>
                                )}
                                {showAttendance && (
                                  <small>
                                    {i18n.t("screenCopy:teacherConsole.late")} {item.late}
                                  </small>
                                )}
                                {showHomework && (
                                  <small>
                                    {i18n.t("gradeOverview:statuses.missing")} {item.missing}
                                  </small>
                                )}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <p className="class-record-empty">
                            {i18n.t("screenCopy:teacherConsole.noStudentRecordsToSummariseYet")}
                          </p>
                        )}
                      </section>
                      {showHomework && (
                        <section className="paper-card class-record-subject-chart">
                          <header>
                            <h3>{i18n.t("screenCopy:teacherConsole.missingWorkBySubject")}</h3>
                          </header>
                          {classSubjectSummary.length ? (
                            <div>
                              {classSubjectSummary.slice(0, 7).map(([subject, count]) => (
                                <div key={subject}>
                                  <span>
                                    <b>{subject}</b>
                                    <strong>{count}</strong>
                                  </span>
                                  <i
                                    aria-label={i18n.t("screenCopy:teacherConsole.missingSubmissions_305a6", {
                                      subject: subject,
                                      count: count,
                                    })}
                                  >
                                    <em style={{ width: `${(count / maxSubjectCount) * 100}%` }} />
                                  </i>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="class-record-empty">
                              {i18n.t("screenCopy:teacherConsole.noMissingWorkRecordsYet")}
                            </p>
                          )}
                        </section>
                      )}
                    </div>
                  )}
                  {(showAttendance || showHomework) && (
                    <section className="paper-card class-record-recent">
                      <header>
                        <div>
                          <h3>{i18n.t("screenCopy:teacherConsole.recentRecords")}</h3>
                        </div>
                        <div>
                          {showAttendance && (
                            <button type="button" onClick={() => onNavigate("attendance")}>
                              {i18n.t("screenCopy:teacherConsole.openAttendance")}
                            </button>
                          )}
                          {showHomework && (
                            <button type="button" onClick={() => onNavigate("homeroomHomework")}>
                              {i18n.t("screenCopy:teacherConsole.openMissingHomework")}
                            </button>
                          )}
                        </div>
                      </header>
                      {recentClassRecords.length ? (
                        <div>
                          {recentClassRecords.map((item) => (
                            <article key={item.key}>
                              <time>{item.date}</time>
                              <span className={item.kind === "點名" ? "attendance" : "missing"}>
                                {item.kind === "點名"
                                  ? i18n.t("marketing:attendanceTab")
                                  : i18n.t("interfaceExtras:missingLabel")}
                              </span>
                              <b>{item.title}</b>
                              <small>{item.detail}</small>
                            </article>
                          ))}
                        </div>
                      ) : (
                        <p className="class-record-empty">
                          {i18n.t("screenCopy:teacherConsole.noAttendanceOrMissingWorkRecordsYet")}
                        </p>
                      )}
                    </section>
                  )}
                </div>
              ) : mode === "attendance" ? (
                <>
                  <section className="paper-card student-record-history">
                    <header>
                      <div>
                        <h3>
                          {i18n.t("screenCopy:teacherConsole.absentDates")}{" "}
                          <span className="absence-count">{absenceHistory.length}</span>
                        </h3>
                      </div>
                      <button onClick={() => onNavigate("attendance")}>
                        {i18n.t("screenCopy:teacherConsole.openAttendance_8f396")}
                      </button>
                    </header>
                    {absenceHistory.length ? (
                      <div className="student-record-entries">
                        {absenceHistory.map((entry) => (
                          <article key={entry.date}>
                            <time>{entry.date}</time>
                            <span className="student-record-result absent">{i18n.t("attendanceRegister:absent")}</span>
                          </article>
                        ))}
                      </div>
                    ) : (
                      <p className="student-record-empty">{i18n.t("screenCopy:teacherConsole.noAbsencesRecorded")}</p>
                    )}
                  </section>
                </>
              ) : (
                <>
                  <div className="student-record-metrics homework-metrics">
                    <div className="paper-card">
                      <small>{i18n.t("screenCopy:teacherConsole.missingSubmissions_28ccc")}</small>
                      <strong>{missingHistory.length}</strong>
                    </div>
                    <div className="paper-card">
                      <small>{i18n.t("screenCopy:teacherConsole.subjectsInvolved")}</small>
                      <strong>{subjectSummary.length}</strong>
                    </div>
                    <div className="paper-card last-missing">
                      <small>{i18n.t("screenCopy:teacherConsole.latestMissingWork")}</small>
                      <strong>{missingHistory[0]?.date || "—"}</strong>
                    </div>
                  </div>
                  <section className="paper-card student-record-history">
                    <header>
                      <div>
                        <h3>{i18n.t("screenCopy:teacherConsole.missingTasks")}</h3>
                      </div>
                      <button onClick={() => onNavigate("homeroomHomework")}>
                        {i18n.t("screenCopy:teacherConsole.openMissingHomework_476e3")}
                      </button>
                    </header>
                    {subjectSummary.length > 0 && (
                      <div className="student-record-subjects">
                        <b>{i18n.t("screenCopy:teacherConsole.bySubject")}</b>
                        <div>
                          {subjectSummary.map(([subject, count]) => (
                            <span key={subject}>
                              {subject}
                              <strong>{count}</strong>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {missingHistory.length ? (
                      <div className="student-record-entries">
                        {missingHistory.map((entry, index) => (
                          <article key={`${entry.date}_${entry.subject}_${entry.taskDetail}_${index}`}>
                            <time>{entry.date}</time>
                            <span className="student-record-task">
                              <b>
                                {entry.subject ? `${entry.subject}｜` : ""}
                                {entry.taskLabel}
                              </b>
                              <small>{entry.taskDetail || i18n.t("screenCopy:teacherConsole.noTaskDescription")}</small>
                            </span>
                            <span className="student-record-result missing">
                              {i18n.t("gradeOverview:statuses.missing")}
                            </span>
                          </article>
                        ))}
                      </div>
                    ) : (
                      <p className="student-record-empty">
                        {i18n.t("screenCopy:teacherConsole.noMissingWorkRecordsForThisStudent")}
                      </p>
                    )}
                  </section>
                </>
              )}
            </div>
          </>
        ) : (
          <section className="paper-card student-record-no-students">
            <h2>
              {students.length
                ? i18n.t("screenCopy:teacherConsole.studentNotFound")
                : i18n.t("screenCopy:teacherConsole.noStudentRosterYet")}
            </h2>
          </section>
        )}
      </section>
    </div>
  );
}

function AttendancePanel({
  sessionStatus,
  readOnly = false,
  workspaceId,
  classItem,
  students,
  records,
  charts,
  onSave,
  recordTerm,
  defaultRecordDate,
}: PanelContext & {
  records: AttendanceRecord[];
  charts: SeatingChart[];
  onSave: (item: AttendanceRecord) => Promise<unknown> | void;
}) {
  const { t: copy } = useTranslation("classroomInterface");
  const { t } = useTranslation("attendanceRegister");
  const [date, setDate] = useState(defaultRecordDate || today());
  const [exceptions, setExceptions] = useState<Record<string, "late" | "absent">>({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const record = records.find((item) => item.classId === classItem?.id && item.date === date);
  const visibleStudents = students.filter((item) => item.active || record?.studentIds.includes(item.id));
  useSaveBeforeUnload(saving);
  const pagination = useStudentPagination(visibleStudents, JSON.stringify([classItem?.id, date, recordTerm?.id]));
  const studentIds = visibleStudents.map((item) => item.id);
  const counts = attendanceCounts(studentIds, exceptions);
  const chart = charts.find((item) => item.classId === classItem?.id && Object.keys(item.seats).length > 0);
  const teacherView = chart?.view !== "student";
  const visibleSeatIndexes = chart ? Array.from({ length: chart.rows * chart.columns }, (_, index) => index) : [];
  if (teacherView) visibleSeatIndexes.reverse();
  const visibleRowAisles = chart
    ? teacherView
      ? chart.rowAisles.map((index) => chart.rows - 2 - index)
      : chart.rowAisles
    : [];
  const visibleColumnAisles = chart
    ? teacherView
      ? chart.columnAisles.map((index) => chart.columns - 2 - index)
      : chart.columnAisles
    : [];
  const seatedStudentIds = new Set(chart ? Object.values(chart.seats) : []);
  const unseatedStudents = visibleStudents.filter((student) => !seatedStudentIds.has(student.id));
  const unseatedPagination = useStudentPagination(
    unseatedStudents,
    JSON.stringify([classItem?.id, date, recordTerm?.id]),
  );
  const denseSeating = Boolean(chart && (visibleStudents.length > 30 || chart.columns > 6));

  useEffect(() => {
    setExceptions(record?.exceptions || {});
    setError("");
  }, [classItem?.id, date, record]);

  async function cycle(studentId: string) {
    if (readOnly || !classItem || !studentIds.length || savingRef.current) return;
    const previous = exceptions;
    const next = { ...previous };
    if (next[studentId] === "absent") delete next[studentId];
    else next[studentId] = "absent";
    const focused = document.activeElement instanceof HTMLButtonElement ? document.activeElement : null;
    savingRef.current = true;
    setExceptions(next);
    setSaving(true);
    setError("");
    try {
      await onSave({
        id: `${classItem.id}_${date}`,
        workspaceId,
        classId: classItem.id,
        date,
        studentIds,
        exceptions: Object.fromEntries(Object.entries(next).filter(([id]) => studentIds.includes(id))),
        updatedAt: isoNow(),
      });
    } catch (caught) {
      setExceptions(previous);
      setError(caught instanceof Error ? caught.message : t("failed"));
    } finally {
      savingRef.current = false;
      setSaving(false);
      window.requestAnimationFrame(() => {
        if (focused?.isConnected && document.activeElement === document.body) focused.focus();
      });
    }
  }

  return (
    <div
      className="homeroom-register-layout"
      data-autosave
      data-save-state={saving ? "saving" : error ? "error" : "saved"}
    >
      <ToolPageHeader title={i18n.t("screenCopy:teacherConsole.classAttendance")} sessionStatus={sessionStatus}>
        <ToolPageToolbar
          scope={
            <>
              <label>
                {i18n.t("screenCopy:teacherConsole.attendanceDate")}
                <input
                  min={recordTerm?.startDate}
                  max={recordTerm?.endDate}
                  type="date"
                  value={date}
                  disabled={saving}
                  onChange={(event) => setDate(event.target.value)}
                />
              </label>
              <span
                role="status"
                aria-live="polite"
                className={record && !saving && !error ? "save-state saved" : "save-state"}
              >
                {saving ? t("saving") : error ? t("failed") : record ? t("saved") : t("ready")}
              </span>
            </>
          }
          actions={
            <div
              className="attendance-stats"
              role="group"
              aria-label={i18n.t("screenCopy:teacherConsole.attendanceStatistics")}
            >
              <span className="attendance-stat attendance-stat-present">
                {i18n.t("attendanceRegister:present")}{" "}
                <strong className="attendance-stat-count">{counts.present + counts.late}</strong>
              </span>
              <span className="attendance-stat attendance-stat-absent">
                {i18n.t("attendanceRegister:absent")} <strong className="attendance-stat-count">{counts.absent}</strong>
              </span>
              <span className="attendance-stat">
                {i18n.t("screenCopy:teacherConsole.classTotal")}{" "}
                <strong className="attendance-stat-count">{studentIds.length}</strong>
              </span>
            </div>
          }
        />
      </ToolPageHeader>
      <section className="paper-card homeroom-roster-card attendance-seating-card">
        <header>
          <div>
            <h2>
              {chart ? i18n.t("screenCopy:teacherConsole.attendanceBySeat") : i18n.t("adminRecords:kinds.roster")}
            </h2>
          </div>
        </header>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        {chart ? (
          <div
            className={`attendance-seat-board ${teacherView ? "teacher-view" : "student-view"} ${denseSeating ? "dense" : ""}`}
          >
            <div className="blackboard">{i18n.t("localExport:printBoard")}</div>
            <div className="attendance-seat-scroller">
              <div
                className="attendance-seat-grid"
                style={{
                  gridTemplateColumns: seatingTracks(
                    chart.columns,
                    visibleColumnAisles,
                    "minmax(96px, 1fr)",
                    "8px",
                    denseSeating ? "28px" : "38px",
                  ),
                  gridTemplateRows: seatingTracks(
                    chart.rows,
                    visibleRowAisles,
                    denseSeating ? "minmax(68px, auto)" : "minmax(84px, auto)",
                    "8px",
                    denseSeating ? "26px" : "32px",
                  ),
                }}
              >
                {visibleSeatIndexes.map((seatIndex, visualIndex) => {
                  const row = Math.floor(seatIndex / chart.columns);
                  const column = seatIndex % chart.columns;
                  const student = visibleStudents.find((item) => item.id === chart.seats[`${row}_${column}`]);
                  const position = {
                    gridRow: Math.floor(visualIndex / chart.columns) * 2 + 1,
                    gridColumn: (visualIndex % chart.columns) * 2 + 1,
                  };
                  if (!student)
                    return (
                      <div className="attendance-seat-empty" style={position} key={seatIndex}>
                        {i18n.t("localExport:printEmptySeat")}
                      </div>
                    );
                  const status = exceptions[student.id] === "absent" ? "absent" : "present";
                  const label = t(status);
                  const next = status === "present" ? "absent" : "present";
                  const genderLabel = studentGenderLabel(student);
                  return (
                    <button
                      type="button"
                      key={student.id}
                      data-demo-action="attendance-student"
                      style={position}
                      className={`attendance-seat-student ${studentGenderClass(student)} ${status}`}
                      disabled={readOnly}
                      aria-disabled={readOnly || saving}
                      onClick={() => void cycle(student.id)}
                      aria-label={t("studentAction", {
                        number: studentNumberLabel(student),
                        name: student.name,
                        gender: genderLabel
                          ? i18n.t("screenCopy:teacherConsole.label_07242", { genderLabel: genderLabel })
                          : "",
                        current: label,
                        next: t(next),
                      })}
                    >
                      <span className="student-number" data-source-class={student.sourceClass}>
                        {studentNumberLabel(student, true)}
                      </span>
                      <b>{student.name}</b>
                      <span className="student-status">{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {unseatedStudents.length > 0 && (
              <div className="attendance-unseated">
                <b>
                  {i18n.t("screenCopy:teacherConsole.unseated")} {unseatedStudents.length}{" "}
                  {i18n.t("screenCopy:teacherConsole.students_49127")}
                </b>
                <div className="homeroom-student-grid">
                  {unseatedPagination.items.map((student) => {
                    const status = exceptions[student.id] === "absent" ? "absent" : "present";
                    const label = t(status);
                    const next = status === "present" ? "absent" : "present";
                    return (
                      <button
                        key={student.id}
                        data-demo-action="attendance-student"
                        className={`homeroom-student-card ${status}`}
                        disabled={readOnly}
                        aria-disabled={readOnly || saving}
                        onClick={() => void cycle(student.id)}
                        aria-label={t("studentAction", {
                          number: studentNumberLabel(student),
                          name: student.name,
                          gender: "",
                          current: label,
                          next: t(next),
                        })}
                      >
                        <span className="student-number" data-source-class={student.sourceClass}>
                          {studentNumberLabel(student, true)}
                        </span>
                        <b>{student.name}</b>
                        <span className="student-status">{label}</span>
                      </button>
                    );
                  })}
                </div>
                <StudentPagination {...unseatedPagination} label="unseated" disabled={saving} />
              </div>
            )}
          </div>
        ) : (
          <div className="homeroom-student-grid">
            {pagination.items.map((student) => {
              const status = exceptions[student.id] === "absent" ? "absent" : "present";
              const label = t(status);
              const next = status === "present" ? "absent" : "present";
              return (
                <button
                  key={student.id}
                  data-demo-action="attendance-student"
                  className={`homeroom-student-card ${status}`}
                  disabled={readOnly}
                  aria-disabled={readOnly || saving}
                  onClick={() => void cycle(student.id)}
                  aria-label={t("studentAction", {
                    number: studentNumberLabel(student),
                    name: student.name,
                    gender: "",
                    current: label,
                    next: t(next),
                  })}
                >
                  <span className="student-number" data-source-class={student.sourceClass}>
                    {studentNumberLabel(student, true)}
                  </span>
                  <b>{student.name}</b>
                  <span className="student-status">{label}</span>
                </button>
              );
            })}
          </div>
        )}
        {!chart && <StudentPagination {...pagination} disabled={saving} />}
        {!visibleStudents.length && <p className="empty-copy">{copy("noStudents")}</p>}
      </section>
    </div>
  );
}

function HomeroomHomeworkPanel({
  sessionStatus,
  readOnly = false,
  workspaceId,
  classItem,
  students,
  records,
  charts,
  onSave,
  onDelete,
  onUpdateClass,
  toast,
  recordTerm,
  defaultRecordDate,
}: PanelContext & {
  records: HomeroomHomeworkRecord[];
  charts: SeatingChart[];
  onSave: (item: HomeroomHomeworkRecord) => Promise<unknown> | void;
  onDelete: (recordId: string) => Promise<unknown> | void;
  onUpdateClass: (
    classId: string,
    patch: Pick<Classroom, "customSubjects" | "customDuties" | "dutyAssignments">,
  ) => Promise<void>;
}) {
  const { t: copy } = useTranslation("classroomInterface");
  const { t: saveText } = useTranslation("automaticSave");
  const [date, setDate] = useState(defaultRecordDate || today());
  const [customSubject, setCustomSubject] = useState("");
  const [selectedRecordId, setSelectedRecordId] = useState(() => id("homeroom_homework"));
  const [defaults, setDefaults] = useState<{ subject: string; taskType: HomeroomHomeworkTaskType }>({
    subject: "",
    taskType: "exercise",
  });
  const draftCreatedAt = useMemo(() => isoNow(), [selectedRecordId]);
  const [studentView, setStudentView] = useNavigationState(
    `homeroomHomework:${classItem?.id}:view`,
    "list",
    navigationChoice(["list", "seating"]),
  );
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const subjects = [...DEFAULT_HOMEROOM_SUBJECTS, ...(classItem?.customSubjects || [])];
  const selectedRecord = records.find((item) => item.id === selectedRecordId);
  const dateRecords = records
    .filter((item) => item.classId === classItem?.id && item.date === date)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const chart = charts.find((item) => item.classId === classItem?.id && Object.keys(item.seats).length > 0);
  const isToday = date === today();
  const visibleStudents = students
    .filter((item) => item.active || selectedRecord?.missingStudentIds.includes(item.id))
    .sort(compareStudentNumbers);
  type HomeworkDraft = {
    subject: string;
    taskType: HomeroomHomeworkTaskType;
    taskDetail: string;
    missingIds: string[];
  };
  const autosave = useAutosavedDraft<HomeworkDraft>({
    scope: `${workspaceId}:${classItem?.id}:${date}:${selectedRecordId}`,
    source: selectedRecord
      ? {
          subject: selectedRecord.subject,
          taskType: selectedRecord.taskType || "exercise",
          taskDetail: selectedRecord.taskDetail || "",
          missingIds: selectedRecord.missingStudentIds,
        }
      : { ...defaults, taskDetail: "", missingIds: [] },
    writable: !readOnly && !deleting && Boolean(classItem),
    // Existing records allow an empty description; selecting missing students
    // must still save even when the teacher leaves the optional detail blank.
    isValid: (draft) =>
      (draft.taskType === "replySlip" || Boolean(draft.subject)) &&
      (draft.missingIds.length > 0 || Boolean(selectedRecord)),
    onSave: (draft) => {
      const now = isoNow();
      return onSave({
        id: selectedRecordId,
        workspaceId,
        classId: classItem!.id,
        date,
        subject: draft.taskType === "replySlip" ? "" : draft.subject,
        taskType: draft.taskType,
        taskDetail: draft.taskDetail.trim(),
        missingStudentIds: draft.missingIds.filter((studentId) =>
          visibleStudents.some((student) => student.id === studentId),
        ),
        createdAt: selectedRecord?.createdAt || draftCreatedAt,
        updatedAt: now,
      });
    },
  });
  const { subject, taskType, taskDetail, missingIds } = autosave.value;
  const saving = autosave.saving || deleting;
  const requiresSubject = taskType !== "replySlip";
  function updateDraft<K extends keyof HomeworkDraft>(key: K, update: React.SetStateAction<HomeworkDraft[K]>) {
    autosave.update((current) => ({
      ...current,
      [key]: typeof update === "function" ? update(current[key]) : update,
    }));
  }
  const setSubject = (update: React.SetStateAction<string>) => updateDraft("subject", update);
  const setTaskType = (update: React.SetStateAction<HomeroomHomeworkTaskType>) => updateDraft("taskType", update);
  const setTaskDetail = (update: React.SetStateAction<string>) => updateDraft("taskDetail", update);
  const setMissingIds = (update: React.SetStateAction<string[]>) => updateDraft("missingIds", update);
  const pagination = useStudentPagination(
    visibleStudents,
    JSON.stringify([classItem?.id, date, selectedRecordId, subject, taskType, recordTerm?.id]),
  );
  const recentDetails = [
    ...new Set(
      records
        .filter((item) => item.classId === classItem?.id && item.taskDetail)
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
        .map((item) => item.taskDetail!),
    ),
  ].slice(0, 8);

  useEffect(() => {
    if (!chart && studentView === "seating") setStudentView("list");
  }, [chart, studentView]);

  function toggle(studentId: string) {
    if (readOnly) return;
    setMissingIds((current) =>
      current.includes(studentId) ? current.filter((item) => item !== studentId) : [...current, studentId],
    );
  }

  async function addSubject(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!classItem) return;
    const name = customSubject.trim();
    if (!name || name.length > 40 || subjects.some((item) => item.toLocaleLowerCase() === name.toLocaleLowerCase())) {
      setError(i18n.t("screenCopy:teacherConsole.enterAUniqueSubjectNameOfTo"));
      return;
    }
    try {
      setError("");
      await onUpdateClass(classItem.id, { customSubjects: [...(classItem.customSubjects || []), name] });
      setCustomSubject("");
      setSubject(name);
      toast(i18n.t("screenCopy:teacherConsole.addedSubject", { name: name }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : i18n.t("screenCopy:teacherConsole.couldNotAddTheSubject"));
    }
  }

  async function removeRecord(item: HomeroomHomeworkRecord) {
    if (readOnly || saving) return;
    if (
      !window.confirm(
        i18n.t("screenCopy:teacherConsole.delete", {
          value: item.subject ? `${item.subject}｜` : "",
          value1: item.taskDetail || homeroomHomeworkTaskLabel(item.taskType),
        }),
      )
    )
      return;
    try {
      setDeleting(true);
      await onDelete(item.id);
      if (selectedRecordId === item.id) {
        setDefaults({ subject, taskType });
        setSelectedRecordId(id("homeroom_homework"));
      }
      toast(i18n.t("screenCopy:teacherConsole.missingWorkRecordDeleted"));
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : i18n.t("screenCopy:teacherConsole.couldNotDeleteTheMissingWorkRecord"),
      );
    } finally {
      setDeleting(false);
    }
  }

  function newRecord() {
    if (!canChangeRecord()) return;
    setDefaults({ subject, taskType });
    setSelectedRecordId(id("homeroom_homework"));
    setError("");
  }

  function canChangeRecord() {
    return !saving && (!autosave.dirty || window.confirm(saveText("discard")));
  }

  const teacherView = chart?.view !== "student";
  const visibleSeatIndexes = chart ? Array.from({ length: chart.rows * chart.columns }, (_, index) => index) : [];
  if (teacherView) visibleSeatIndexes.reverse();
  const seatStudentIds = new Set(chart ? Object.values(chart.seats) : []);
  const unseatedStudents = visibleStudents.filter((student) => !seatStudentIds.has(student.id));
  const visibleRowAisles = chart
    ? teacherView
      ? chart.rowAisles.map((index) => chart.rows - 2 - index)
      : chart.rowAisles
    : [];
  const visibleColumnAisles = chart
    ? teacherView
      ? chart.columnAisles.map((index) => chart.columns - 2 - index)
      : chart.columnAisles
    : [];
  const unseatedPagination = useStudentPagination(
    unseatedStudents,
    JSON.stringify([classItem?.id, date, selectedRecordId, recordTerm?.id]),
  );
  const denseSeating = Boolean(chart && (visibleStudents.length > 30 || chart.columns > 6));
  const seatColumnSize = "minmax(96px, 1fr)";
  const seatRowSize = denseSeating ? "minmax(68px, auto)" : "minmax(84px, auto)";

  function studentButton(student: Student, className = "homeroom-student-card") {
    const missing = missingIds.includes(student.id);
    return (
      <button
        type="button"
        key={student.id}
        data-demo-action="missing-student"
        className={`${className} ${studentGenderClass(student)} ${missing ? "missing" : "submitted"}`}
        disabled={readOnly}
        onClick={() => toggle(student.id)}
        aria-pressed={missing}
        aria-label={i18n.t("screenCopy:teacherConsole.no_ed3bb", {
          number: studentNumberLabel(student),
          name: student.name,
          value: i18n.t(missing ? "interfaceExtras:missingToggle" : "interfaceExtras:completeToggle"),
        })}
      >
        <span className="student-number" data-source-class={student.sourceClass}>
          {studentNumberLabel(student, true)}
        </span>
        <b>{student.name}</b>
        <span className="student-status">
          {missing ? i18n.t("screenCopy:teacherConsole.missing") : i18n.t("screenCopy:teacherConsole.submitted")}
        </span>
      </button>
    );
  }

  return (
    <div
      className="homeroom-homework-layout"
      data-autosave
      aria-busy={saving}
      data-save-state={saving ? "saving" : autosave.dirty ? "dirty" : selectedRecord ? "saved" : "idle"}
    >
      <ToolPageHeader title={i18n.t("guidedTour:steps.demoHomeroomHomework.title")} sessionStatus={sessionStatus}>
        <ToolPageToolbar
          scope={
            <span className="missing-count" aria-live="polite">
              {i18n.t("screenCopy:teacherConsole.selected")} {missingIds.length}{" "}
              {i18n.t("screenCopy:teacherConsole.students_74c77")}
            </span>
          }
          actions={
            <AutosaveStatus
              name={i18n.t("guidedTour:steps.demoHomeroomHomework.title")}
              {...autosave}
              readOnly={readOnly}
              hasSaved={Boolean(selectedRecord)}
            />
          }
        />
      </ToolPageHeader>
      <section className="paper-card homeroom-homework-setup">
        <header className="homeroom-homework-heading">
          <h2>
            {selectedRecord
              ? i18n.t("screenCopy:teacherConsole.editMissingTask")
              : i18n.t("screenCopy:teacherConsole.newMissingTask")}
          </h2>
        </header>
        <div className="homeroom-subject-settings">
          {requiresSubject ? (
            <label className="homeroom-subject-select">
              {i18n.t("screenCopy:teacherConsole.subject")}
              <select
                data-demo-action="missing-subject"
                value={subject}
                disabled={readOnly}
                onChange={(event) => setSubject(event.target.value)}
              >
                <option value="">{i18n.t("screenCopy:teacherConsole.chooseSubject")}</option>
                {subjects.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <div className="homeroom-subject-na">
              <small>{i18n.t("screenCopy:teacherConsole.subject")}</small>
              <b>{i18n.t("screenCopy:teacherConsole.notApplicable")}</b>
            </div>
          )}
          <form className="custom-subject-form" onSubmit={(event) => void addSubject(event)}>
            <label>
              {i18n.t("screenCopy:teacherConsole.customSubject")}
              <input
                value={customSubject}
                disabled={readOnly}
                onChange={(event) => setCustomSubject(event.target.value)}
                placeholder={i18n.t("screenCopy:teacherConsole.enterAnotherSubject")}
                maxLength={40}
              />
            </label>
            <button disabled={readOnly || !customSubject.trim()}>
              {i18n.t("screenCopy:teacherConsole.addSubject")}
            </button>
          </form>
          <details className="homeroom-date-more">
            <summary>{i18n.t("screenCopy:teacherConsole.recordAnotherDate")}</summary>
            <label>
              {i18n.t("adminRecords:selectedDates")}
              <input
                min={recordTerm?.startDate}
                max={recordTerm?.endDate}
                type="date"
                value={date}
                disabled={saving}
                onChange={(event) => {
                  if (!canChangeRecord()) return;
                  setDate(event.target.value);
                  setSelectedRecordId(id("homeroom_homework"));
                  setError("");
                }}
              />
            </label>
            {!isToday && (
              <button
                type="button"
                disabled={saving}
                onClick={() => {
                  if (!canChangeRecord()) return;
                  setDate(defaultRecordDate || today());
                  setSelectedRecordId(id("homeroom_homework"));
                  setError("");
                }}
              >
                {i18n.t("screenCopy:teacherConsole.backToToday")}
              </button>
            )}
          </details>
        </div>
        <div className="homeroom-homework-fields">
          <fieldset className="homeroom-task-types" disabled={readOnly}>
            <legend>{i18n.t("screenCopy:teacherConsole.missingWorkType")}</legend>
            {HOMEROOM_HOMEWORK_TASK_TYPES.map((item) => (
              <button
                type="button"
                key={item.value}
                aria-pressed={taskType === item.value}
                className={taskType === item.value ? "selected" : ""}
                onClick={() => {
                  setTaskType(item.value);
                  if (item.value === "replySlip") setSubject("");
                }}
              >
                {homeroomHomeworkTaskLabel(item.value)}
              </button>
            ))}
          </fieldset>
          <label className="homeroom-task-detail">
            {i18n.t("screenCopy:teacherConsole.taskDescription")}
            <input
              data-demo-action="missing-detail"
              aria-describedby="homeroom-homework-save-hint"
              list="recent-homeroom-homework"
              value={taskDetail}
              disabled={readOnly}
              onChange={(event) => setTaskDetail(event.target.value)}
              placeholder={i18n.t("screenCopy:teacherConsole.eGWorkbookPpParentReplySlip")}
              maxLength={120}
            />
            <datalist id="recent-homeroom-homework">
              {recentDetails.map((item) => (
                <option value={item} key={item} />
              ))}
            </datalist>
          </label>
        </div>
        <p id="homeroom-homework-save-hint" className="field-hint">
          {copy("missingWorkAutosaveHint")}
        </p>
      </section>
      <section className={`paper-card homeroom-homework-board ${studentView === "seating" ? "seating-mode" : ""}`}>
        <header>
          <div>
            <h2>{i18n.t("screenCopy:teacherConsole.selectStudentsWithMissingWork")}</h2>
          </div>
          <div className="homework-board-actions">
            <span className="missing-count" aria-live="polite">
              {i18n.t("screenCopy:teacherConsole.selected")} {missingIds.length}{" "}
              {i18n.t("screenCopy:teacherConsole.students_74c77")}
            </span>
            <button type="button" disabled={readOnly || !missingIds.length} onClick={() => setMissingIds([])}>
              {i18n.t("screenCopy:teacherConsole.clearAll")}
            </button>
          </div>
        </header>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div
          className="homeroom-student-view-tabs"
          role="group"
          aria-label={i18n.t("screenCopy:teacherConsole.studentSelectionMethod")}
        >
          <button
            type="button"
            aria-pressed={studentView === "list"}
            className={studentView === "list" ? "selected" : ""}
            onClick={() => setStudentView("list")}
          >
            {i18n.t("studentPagination:list")}
          </button>
          {chart && (
            <button
              type="button"
              aria-pressed={studentView === "seating"}
              className={studentView === "seating" ? "selected" : ""}
              onClick={() => setStudentView("seating")}
            >
              {i18n.t("marketing:seatingTab")}
            </button>
          )}
        </div>
        {requiresSubject && !subject && <p className="homeroom-pick-subject">{copy("chooseSubject")}</p>}
        {(!requiresSubject || subject) && studentView === "list" && (
          <div className="homeroom-student-grid">{pagination.items.map((student) => studentButton(student))}</div>
        )}
        {(!requiresSubject || subject) && studentView === "list" && (
          <StudentPagination {...pagination} disabled={saving} />
        )}
        {(!requiresSubject || subject) && studentView === "seating" && chart && (
          <div className="homework-seat-scroller">
            <div className={`homework-seat-board ${denseSeating ? "dense" : ""}`}>
              <div className="blackboard">{i18n.t("localExport:printBoard")}</div>
              <div
                className="homework-seat-grid"
                style={{
                  gridTemplateColumns: seatingTracks(
                    chart.columns,
                    visibleColumnAisles,
                    seatColumnSize,
                    "8px",
                    denseSeating ? "28px" : "38px",
                  ),
                  gridTemplateRows: seatingTracks(
                    chart.rows,
                    visibleRowAisles,
                    seatRowSize,
                    "8px",
                    denseSeating ? "26px" : "32px",
                  ),
                }}
              >
                {visibleSeatIndexes.map((seatIndex, visualIndex) => {
                  const row = Math.floor(seatIndex / chart.columns);
                  const column = seatIndex % chart.columns;
                  const student = visibleStudents.find((item) => item.id === chart.seats[`${row}_${column}`]);
                  const position = {
                    gridRow: Math.floor(visualIndex / chart.columns) * 2 + 1,
                    gridColumn: (visualIndex % chart.columns) * 2 + 1,
                  };
                  return student ? (
                    <div style={position} key={seatIndex}>
                      {studentButton(student, "homework-seat-student")}
                    </div>
                  ) : (
                    <div className="homework-seat-empty" style={position} key={seatIndex}>
                      {i18n.t("localExport:printEmptySeat")}
                    </div>
                  );
                })}
              </div>
              {unseatedStudents.length > 0 && (
                <div className="homework-unseated">
                  <b>{i18n.t("screenCopy:teacherConsole.unseated_ae6ad")}</b>
                  <div>{unseatedPagination.items.map((student) => studentButton(student))}</div>
                  <StudentPagination {...unseatedPagination} label="unseated" disabled={saving} />
                </div>
              )}
            </div>
          </div>
        )}
        {!visibleStudents.length && <p className="empty-copy">{copy("noStudents")}</p>}
      </section>
      <aside className="paper-card homeroom-homework-history">
        <header>
          <h2>{i18n.t("screenCopy:teacherConsole.missingWorkForThisDate")}</h2>
          <button type="button" disabled={readOnly || saving} onClick={newRecord}>
            {i18n.t("screenCopy:teacherConsole.addAnotherTask")}
          </button>
        </header>
        {dateRecords.length ? (
          dateRecords.map((item) => (
            <article key={item.id} className={item.id === selectedRecordId ? "selected" : ""}>
              <button
                type="button"
                className="homework-history-select"
                disabled={saving}
                onClick={() => {
                  if (item.id !== selectedRecordId && canChangeRecord()) {
                    setSelectedRecordId(item.id);
                    setError("");
                  }
                }}
              >
                <b>
                  {item.subject ? `${item.subject}｜` : ""}
                  {homeroomHomeworkTaskLabel(item.taskType)}
                </b>
                <span>{item.taskDetail || i18n.t("screenCopy:teacherConsole.noTaskDescription")}</span>
                <small>
                  {item.missingStudentIds.length} {i18n.t("screenCopy:teacherConsole.missing_224ea")}
                </small>
              </button>
              <button
                type="button"
                className="homework-history-delete"
                aria-label={i18n.t("screenCopy:teacherConsole.deleteRecord", {
                  value: item.subject ? i18n.t("interfaceExtras:subjectPrefix", { subject: item.subject }) : "",
                  value1: item.taskDetail || i18n.t("screenCopy:teacherConsole.missingTasks"),
                })}
                disabled={readOnly || saving}
                onClick={() => void removeRecord(item)}
              >
                {i18n.t("dailyMemos:delete")}
              </button>
            </article>
          ))
        ) : (
          <p>{i18n.t("screenCopy:teacherConsole.noMissingWorkRecordedForThisDate")}</p>
        )}
      </aside>
    </div>
  );
}

function HomeroomPanel({
  sessionStatus,
  readOnly = false,
  workspaceId,
  classItem,
  students,
  mode,
  records,
  onAffairChange,
  onUpdateClass,
  toast,
  recordTerm,
  defaultRecordDate,
  onRequestExport,
}: PanelContext & {
  mode: "affairs" | "duties";
  records: HomeroomRecord[];
  onRequestExport?: (assignments: Record<string, string>) => void;
  onAffairChange: (classId: string, date: string, change: HomeroomAffairChange) => Promise<unknown>;
  onUpdateClass: (
    classId: string,
    patch: Pick<Classroom, "customSubjects" | "customDuties" | "dutyAssignments">,
  ) => Promise<void>;
}) {
  const { t } = useTranslation("keyboard");
  const [expandedMonitors, setExpandedMonitors] = useState<string[]>([]);
  const [customSubject, setCustomSubject] = useState("");
  const [customDuty, setCustomDuty] = useState("");
  const [error, setError] = useState("");
  const latestRecord = records.find((item) => item.classId === classItem?.id);
  const savedAssignments = classItem?.dutyAssignments || latestRecord?.duties || {};
  const autosave = useAutosavedDraft({
    scope: `${workspaceId}:${classItem?.id}`,
    source: savedAssignments,
    writable: !readOnly && mode === "duties" && Boolean(classItem),
    onSave: (next) => onUpdateClass(classItem!.id, { dutyAssignments: next }),
  });
  const { value: assignments, update: setAssignments, saving } = autosave;
  const subjects = [...DEFAULT_HOMEROOM_SUBJECTS, ...(classItem?.customSubjects || [])];
  const duties = [...DEFAULT_CLASS_DUTIES, ...(classItem?.customDuties || [])];

  useEffect(() => {
    const saved = classItem?.dutyAssignments || latestRecord?.duties || {};
    setExpandedMonitors(
      Object.keys(saved)
        .filter((key) => key.startsWith("科長｜") && key.endsWith("｜二") && saved[key])
        .map((key) => key.slice(3, -2)),
    );
    setError("");
  }, [classItem?.id]);

  function assignMonitor(subject: string, slot: 1 | 2, studentId: string) {
    const firstKey = `科長｜${subject}`;
    const secondKey = `科長｜${subject}｜二`;
    setAssignments((current) => ({
      ...current,
      [slot === 1 ? firstKey : secondKey]: studentId,
      ...(studentId && current[slot === 1 ? secondKey : firstKey] === studentId
        ? { [slot === 1 ? secondKey : firstKey]: "" }
        : {}),
    }));
  }

  async function addCustom(event: React.FormEvent<HTMLFormElement>, kind: "subject" | "duty") {
    event.preventDefault();
    if (readOnly || !classItem) return;
    const name = (kind === "subject" ? customSubject : customDuty).trim();
    const existing = kind === "subject" ? subjects : duties;
    if (!name || name.length > 40 || existing.some((item) => item.toLocaleLowerCase() === name.toLocaleLowerCase())) {
      setError(i18n.t("screenCopy:teacherConsole.enterAUniqueNameOfToCharacters"));
      return;
    }
    try {
      setError("");
      await onUpdateClass(
        classItem.id,
        kind === "subject"
          ? { customSubjects: [...(classItem.customSubjects || []), name] }
          : { customDuties: [...(classItem.customDuties || []), name] },
      );
      if (kind === "subject") setCustomSubject("");
      else setCustomDuty("");
      toast(
        i18n.t("screenCopy:teacherConsole.added", {
          value: i18n.t(kind === "subject" ? "interfaceExtras:subject" : "interfaceExtras:duty"),
          name: name,
        }),
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : i18n.t("screenCopy:teacherConsole.couldNotAddTheItem"));
    }
  }

  if (mode === "affairs")
    return (
      <HomeroomNotebook
        records={records.filter((item) => item.classId === classItem?.id)}
        defaultDate={defaultRecordDate || today()}
        term={recordTerm}
        readOnly={readOnly}
        onChange={(date, change) => onAffairChange(classItem!.id, date, change)}
        toast={toast}
      />
    );

  return (
    <div
      className="homeroom-duty-layout"
      data-autosave
      aria-busy={saving}
      data-save-state={saving ? "saving" : autosave.dirty ? "dirty" : "saved"}
    >
      <ToolPageHeader title={i18n.t("marketing:dutiesTab")} sessionStatus={sessionStatus}>
        <div className="tool-page-actions">
          <AutosaveStatus
            name={i18n.t("marketing:dutiesTab")}
            {...autosave}
            readOnly={readOnly}
            hasSaved={Object.keys(assignments).length > 0}
          />
          <RecordActionButton
            action="export"
            aria-haspopup="dialog"
            disabled={!classItem}
            onClick={() => onRequestExport?.(assignments)}
          >
            {i18n.t("screenCopy:teacherConsole.exportDutiesPdf")}
          </RecordActionButton>
        </div>
      </ToolPageHeader>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <section className="paper-card homeroom-duty-section">
        <header>
          <h2>{i18n.t("screenCopy:teacherConsole.subjectRepresentatives")}</h2>
        </header>
        <div className="homeroom-duty-grid">
          {subjects.map((subject) => {
            const expanded = expandedMonitors.includes(subject);
            return (
              <div className="subject-monitor-card" key={subject}>
                <div className="subject-monitor-heading">
                  <b>{subject}</b>
                  <button
                    type="button"
                    disabled={readOnly}
                    aria-expanded={expanded}
                    onClick={() => {
                      if (expanded) assignMonitor(subject, 2, "");
                      setExpandedMonitors((current) =>
                        expanded ? current.filter((item) => item !== subject) : [...current, subject],
                      );
                    }}
                    aria-label={t(expanded ? "removeSecondMonitor" : "addSecondMonitor", { subject })}
                  >
                    {t(expanded ? "removeSecond" : "addSecond")}
                  </button>
                </div>
                <select
                  disabled={readOnly}
                  data-demo-action={subject === subjects[0] ? "duty-select" : undefined}
                  aria-label={i18n.t("screenCopy:teacherConsole.representative_8621e", { subject: subject })}
                  value={assignments[`科長｜${subject}`] || ""}
                  onChange={(event) => assignMonitor(subject, 1, event.target.value)}
                >
                  <option value="">{i18n.t("screenCopy:teacherConsole.notAssigned")}</option>
                  {students
                    .filter((item) => item.active)
                    .map((student) => (
                      <option
                        key={student.id}
                        value={student.id}
                        disabled={assignments[`科長｜${subject}｜二`] === student.id}
                      >
                        {studentNumberLabel(student)} · {student.name}
                      </option>
                    ))}
                </select>
                {expanded && (
                  <div className="subject-monitor-extra">
                    <div>
                      <span>{i18n.t("screenCopy:teacherConsole.secondRepresentative")}</span>
                    </div>
                    <select
                      disabled={readOnly}
                      aria-label={i18n.t("screenCopy:teacherConsole.representative_a40c4", { subject: subject })}
                      value={assignments[`科長｜${subject}｜二`] || ""}
                      onChange={(event) => assignMonitor(subject, 2, event.target.value)}
                    >
                      <option value="">{i18n.t("screenCopy:teacherConsole.notAssigned")}</option>
                      {students
                        .filter((item) => item.active)
                        .map((student) => (
                          <option
                            key={student.id}
                            value={student.id}
                            disabled={assignments[`科長｜${subject}`] === student.id}
                          >
                            {studentNumberLabel(student)} · {student.name}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <form className="custom-subject-form" onSubmit={(event) => void addCustom(event, "subject")}>
          <label>
            {i18n.t("screenCopy:teacherConsole.addCustomSubject")}
            <input
              disabled={readOnly || saving}
              value={customSubject}
              onChange={(event) => setCustomSubject(event.target.value)}
              maxLength={40}
            />
          </label>
          <button disabled={readOnly || saving || !customSubject.trim()}>
            {i18n.t("screenCopy:teacherConsole.addRepresentative")}
          </button>
        </form>
      </section>
      <section className="paper-card homeroom-duty-section">
        <header>
          <h2>{i18n.t("screenCopy:teacherConsole.otherDuties")}</h2>
        </header>
        <div className="homeroom-duty-grid">
          {duties.map((duty) => (
            <label key={duty}>
              <span>{duty}</span>
              <select
                disabled={readOnly}
                value={assignments[duty] || ""}
                onChange={(event) => setAssignments((current) => ({ ...current, [duty]: event.target.value }))}
              >
                <option value="">{i18n.t("screenCopy:teacherConsole.notAssigned")}</option>
                {students
                  .filter((item) => item.active)
                  .map((student) => (
                    <option key={student.id} value={student.id}>
                      {studentNumberLabel(student)} · {student.name}
                    </option>
                  ))}
              </select>
            </label>
          ))}
        </div>
        <form className="custom-subject-form" onSubmit={(event) => void addCustom(event, "duty")}>
          <label>
            {i18n.t("screenCopy:teacherConsole.addCustomClassRole")}
            <input
              disabled={readOnly || saving}
              value={customDuty}
              onChange={(event) => setCustomDuty(event.target.value)}
              maxLength={40}
            />
          </label>
          <button disabled={readOnly || saving || !customDuty.trim()}>
            {i18n.t("screenCopy:teacherConsole.addRole")}
          </button>
        </form>
      </section>
    </div>
  );
}

function QuizDirectoryPanel({
  classItem,
  students,
  preview,
  quizzes,
  sessions,
  responses,
  onCreateSession,
  onRequestExport,
  toast,
}: PanelContext & {
  quizzes: QuizDocument[];
  sessions: QuizSession[];
  responses: QuizResponse[];
  onCreateSession: (
    quizId: string,
    classId: string,
    duration: number,
  ) => Promise<(QuizSession & { joinUrl?: string }) | undefined>;
  onRequestExport: () => void;
}) {
  const { t: copy } = useTranslation("classroomInterface");
  const [section, setSection] = useState<"start" | "history" | "gradebook">("start");
  const [selectedQuizId, setSelectedQuizId] = useState("");
  const [duration, setDuration] = useState(10);
  const [activeSession, setActiveSession] = useState<QuizSession & { joinUrl?: string }>();
  const [qr, setQr] = useState("");
  const [sessionStarting, setSessionStarting] = useState(false);
  const [sessionError, setSessionError] = useState("");
  const classQuizzes = quizzes.filter((item) => !item.classId || item.classId === classItem?.id);

  const classSessions = sessions
    .filter((item) => item.classId === classItem?.id)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const selectedQuiz = classQuizzes.find((item) => item.id === selectedQuizId);
  const sessionResponses = responses.filter((item) => item.sessionId === activeSession?.id);

  const responsePagination = useStudentPagination(sessionResponses, activeSession?.id || "");
  const pagination = useStudentPagination(
    students.filter((item) => item.active),
    classItem?.id || "",
  );

  useEffect(() => {
    if (!selectedQuizId || classQuizzes.some((item) => item.id === selectedQuizId)) return;
    setSelectedQuizId("");
    setActiveSession(undefined);
  }, [classItem?.id, classQuizzes, selectedQuizId]);
  useEffect(() => {
    const url = activeSession?.joinUrl || (activeSession ? `${location.origin}/join/${activeSession.code}` : "");
    if (url) QRCode.toDataURL(url, { margin: 1, width: 220, color: { dark: "#17352D", light: "#FFFFFF" } }).then(setQr);
    else setQr("");
  }, [activeSession]);

  async function startSession() {
    if (!selectedQuiz || !classItem) return;
    try {
      setSessionError("");
      setSessionStarting(true);
      const session = await onCreateSession(selectedQuiz.id, classItem.id, duration);
      if (session) {
        setActiveSession(session);
        toast(i18n.t("screenCopy:teacherConsole.quizCodeCreated", { code: session.code }));
      }
    } catch (caught) {
      setSessionError(
        caught instanceof Error ? caught.message : i18n.t("screenCopy:teacherConsole.couldNotCreateTheQuizTryAgain"),
      );
    } finally {
      setSessionStarting(false);
    }
  }

  function resumeSession(session: QuizSession) {
    setSelectedQuizId(session.quizId);
    setActiveSession({ ...session, joinUrl: `${location.origin}/join/${session.code}` });
    setSection("start");
  }

  return (
    <div className="quiz-classroom">
      <nav className="quiz-section-tabs" aria-label={i18n.t("screenCopy:teacherConsole.questionBankAndQuizTabs")}>
        <button className={section === "start" ? "active" : ""} onClick={() => setSection("start")}>
          {i18n.t("screenCopy:teacherConsole.startQuiz")}
        </button>
        <button className={section === "history" ? "active" : ""} onClick={() => setSection("history")}>
          {i18n.t("screenCopy:teacherConsole.quizRecords")}
        </button>
        <button className={section === "gradebook" ? "active" : ""} onClick={() => setSection("gradebook")}>
          {i18n.t("screenCopy:teacherConsole.studentHistory")}
        </button>
      </nav>

      {section === "start" &&
        (!selectedQuiz ? (
          <section className="paper-card quiz-directory">
            <header>
              <h2>{i18n.t("screenCopy:teacherConsole.chooseQuestionBank")}</h2>
              <span>{classQuizzes.length} 組</span>
            </header>
            {classSessions.some((item) => item.status === "open") && (
              <section className="active-session-strip">
                <div>
                  <small>{i18n.t("screenCopy:teacherConsole.inProgress")}</small>
                  <b>{i18n.t("screenCopy:teacherConsole.returnToOpenQuiz")}</b>
                </div>
                {classSessions
                  .filter((item) => item.status === "open")
                  .map((session) => (
                    <button key={session.id} onClick={() => resumeSession(session)}>
                      <span>{session.code}</span>
                      <b>{session.title}</b>
                      <small>
                        {responses.filter((item) => item.sessionId === session.id).length}{" "}
                        {i18n.t("screenCopy:teacherConsole.submitted_61549")}
                      </small>
                    </button>
                  ))}
              </section>
            )}
            <div className="quiz-directory-grid">
              {classQuizzes.length ? (
                classQuizzes.map((quiz, index) => (
                  <button key={quiz.id} onClick={() => setSelectedQuizId(quiz.id)}>
                    <span className="chapter-number">{String(index + 1).padStart(2, "0")}</span>
                    <div>
                      <small>{quiz.sourceName || i18n.t("screenCopy:teacherConsole.customQuestionBank")}</small>
                      <b>{quiz.title}</b>
                      <p>
                        {quiz.questions.length} {i18n.t("screenCopy:teacherConsole.questions")}{" "}
                        {quiz.difficulty || "standard"}
                      </p>
                    </div>
                    <i aria-hidden="true">→</i>
                  </button>
                ))
              ) : (
                <div className="quiz-directory-empty">
                  <span>{i18n.t("screenCopy:teacherConsole.questions_39ec5")}</span>
                  <h3>{i18n.t("screenCopy:teacherConsole.noQuestionBanksInThisClassYet")}</h3>
                </div>
              )}
            </div>
          </section>
        ) : (
          <section className="quiz-player">
            <header className="paper-card quiz-player-head">
              <div>
                <span className="live-pill">
                  <i /> {i18n.t("screenCopy:teacherConsole.classQuiz")}
                </span>
                <h2>{selectedQuiz.title}</h2>
                <p>
                  {selectedQuiz.sourceName || i18n.t("screenCopy:teacherConsole.customQuestionBank")} ·{" "}
                  {selectedQuiz.questions.length} {i18n.t("screenCopy:teacherConsole.questions_39ec5")}
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectedQuizId("");
                  setActiveSession(undefined);
                }}
              >
                {i18n.t("screenCopy:teacherConsole.backToQuestionBank")}
              </button>
            </header>
            {!activeSession ? (
              <section className="paper-card quiz-session-setup">
                <div>
                  <h2>{i18n.t("screenCopy:teacherConsole.timeAllowed")}</h2>
                </div>
                <label>
                  {i18n.t("screenCopy:teacherConsole.timeAllowed")}
                  <select value={duration} onChange={(event) => setDuration(Number(event.target.value))}>
                    <option value={2}>{i18n.t("screenCopy:teacherConsole.minutes")}</option>
                    <option value={5}>{i18n.t("screenCopy:teacherConsole.minutes_93fd4")}</option>
                    <option value={8}>{i18n.t("screenCopy:teacherConsole.minutes_5125d")}</option>
                    <option value={10}>{i18n.t("screenCopy:teacherConsole.minutes_15ebc")}</option>
                    <option value={15}>{i18n.t("screenCopy:teacherConsole.minutes_fc4d4")}</option>
                  </select>
                </label>
                {sessionError && (
                  <p className="form-error" role="alert">
                    {sessionError}
                  </p>
                )}
                <button className="primary-action" disabled={sessionStarting} onClick={() => void startSession()}>
                  {sessionStarting
                    ? i18n.t("screenCopy:teacherConsole.creating")
                    : i18n.t("screenCopy:teacherConsole.openQuizAndGenerateQRCode")}
                </button>
              </section>
            ) : (
              <>
                <section className="paper-card live-quiz-stage">
                  <div>
                    <span>
                      {preview
                        ? i18n.t("screenCopy:teacherConsole.productPreviewSimulatedSession")
                        : i18n.t("screenCopy:teacherConsole.studentsScanToBegin")}
                    </span>
                    <h2>{activeSession.title}</h2>
                    <div className="session-code">
                      <small>{i18n.t("screenCopy:teacherConsole.classCode")}</small>
                      <strong>{activeSession.code}</strong>
                    </div>
                    <p>
                      {i18n.t("screenCopy:teacherConsole.timePerStudent")}
                      {activeSession.durationMinutes} {i18n.t("screenCopy:teacherConsole.minutes_3d6c1")}
                    </p>
                    {preview && <p>{i18n.t("screenCopy:teacherConsole.thisQRCodePreviewsTheLayoutOnly")}</p>}
                  </div>
                  {qr && (
                    <img
                      src={qr}
                      alt={i18n.t("screenCopy:teacherConsole.qrCodeToJoinQuiz", { code: activeSession.code })}
                    />
                  )}
                  {!preview && (
                    <a href={activeSession.joinUrl || `/join/${activeSession.code}`} target="_blank" rel="noreferrer">
                      {i18n.t("screenCopy:teacherConsole.openStudentPage")}
                    </a>
                  )}
                </section>
                <section className="paper-card live-results">
                  <header>
                    <div>
                      <small>{i18n.t("screenCopy:teacherConsole.liveSubmissions")}</small>
                      <strong>{sessionResponses.length}</strong>
                      <span>
                        ／{students.filter((item) => item.active).length}{" "}
                        {i18n.t("screenCopy:teacherConsole.students_49127")}
                      </span>
                    </div>
                    {!sessionResponses.length && <p>{copy("noAnswers")}</p>}
                  </header>
                  <div>
                    {responsePagination.items.map((response) => {
                      const student = students.find((item) => item.id === response.studentId);
                      return (
                        <article key={response.id}>
                          <b>
                            {student
                              ? `${studentNumberLabel(student)} · ${student.name}`
                              : i18n.t("cloudMigration:collections.students")}
                          </b>
                          <span>
                            {response.score} / {response.questionCount}
                          </span>
                        </article>
                      );
                    })}
                  </div>
                  <StudentPagination {...responsePagination} label="submissions" />
                </section>
              </>
            )}
          </section>
        ))}

      {section === "history" && (
        <section className="paper-card quiz-history">
          <header>
            <div>
              <h2>{i18n.t("screenCopy:teacherConsole.pastQuizRecords")}</h2>
            </div>
            <div className="quiz-export-actions">
              <strong>
                {classSessions.length} {i18n.t("terms:timesUnit")}
              </strong>
              {classItem?.classRole === "teaching" && <RecordExportButton onClick={onRequestExport} />}
            </div>
          </header>
          {classSessions.length ? (
            classSessions.map((session) => {
              const submitted = responses.filter((item) => item.sessionId === session.id);
              const average = submitted.length
                ? submitted.reduce((sum, item) => sum + (item.score / item.questionCount) * 100, 0) / submitted.length
                : 0;
              return (
                <article key={session.id}>
                  <time>{new Date(session.createdAt).toLocaleDateString(interfaceLocale())}</time>
                  <div>
                    <b>{session.title}</b>
                    <small>
                      {session.code} ·{" "}
                      {session.status === "open"
                        ? i18n.t("screenCopy:teacherConsole.inProgress")
                        : i18n.t("screenCopy:teacherConsole.ended")}
                    </small>
                  </div>
                  <span>
                    {submitted.length} {i18n.t("screenCopy:teacherConsole.students_49127")}
                  </span>
                  <strong>{average.toFixed(0)}%</strong>
                </article>
              );
            })
          ) : (
            <p className="empty-copy">{i18n.t("screenCopy:teacherConsole.noClassroomQuizzesOpenedYet")}</p>
          )}
        </section>
      )}

      {section === "gradebook" && (
        <section className="paper-card quiz-gradebook">
          <header>
            <div>
              <h2>{i18n.t("screenCopy:teacherConsole.studentQuizHistory")}</h2>
            </div>
            <div className="quiz-export-actions">
              <span>
                {students.filter((item) => item.active).length} {i18n.t("screenCopy:teacherConsole.students")}
              </span>
              {classItem?.classRole === "teaching" && <RecordExportButton onClick={onRequestExport} />}
            </div>
          </header>
          <div className="gradebook-table">
            <div className="gradebook-head">
              <span>{i18n.t("cloudMigration:collections.students")}</span>
              <span>{i18n.t("screenCopy:teacherConsole.completedQuizzes")}</span>
              <span>{i18n.t("screenCopy:teacherConsole.averageScore")}</span>
              <span>{i18n.t("screenCopy:teacherConsole.latestSubmission")}</span>
            </div>
            {pagination.items.map((student) => {
              const entries = responses.filter(
                (item) => item.studentId === student.id && item.classId === classItem?.id,
              );
              const average = entries.length
                ? entries.reduce((sum, item) => sum + (item.score / item.questionCount) * 100, 0) / entries.length
                : 0;
              const latest = [...entries].sort((a, b) => b.submittedAt.localeCompare(a.submittedAt))[0];
              return (
                <div key={student.id}>
                  <b>
                    {studentNumberLabel(student)} · {student.name}
                  </b>
                  <span>{entries.length}</span>
                  <strong>{entries.length ? `${average.toFixed(0)}%` : "—"}</strong>
                  <span>{latest ? new Date(latest.submittedAt).toLocaleDateString(interfaceLocale()) : "—"}</span>
                </div>
              );
            })}
          </div>
          <StudentPagination {...pagination} />
        </section>
      )}
    </div>
  );
}

function AdminPanel({
  sessionStatus,
  readOnly = false,
  workspaceId,
  workspace,
  classItem,
  students,
  classes,
  quizzes,
  calendarEvents,
  historySource,
  exportSource,
  academicTerms,
  initialSection,
  onSectionChange,
  initialRosterMethod,
  initialRosterOpen,
  toolSettings,
  termSettings,
  onAddClass,
  onDeleteEmptyClass,
  onAddStudent,
  onImport,
  onSaveHistoryPlan,
  onUpdateStudent,
  onDeleteStudent,
  onSaveQuiz,
  onDeleteQuiz,
  onGenerateQuiz,
  onSaveCalendarEvents,
  onDeleteCalendarEvents,
  onSaveSchoolCalendar,
  schoolCalendarReadOnly,
  selectedSchoolYear,
  onSelectSchoolYear,
  preview,
  localMode,
  toast,
  onTourRosterImported,
}: PanelContext & {
  initialSection: AdminSection;
  onSectionChange: (section: AdminSection) => void;
  initialRosterMethod: RosterImportMethod;
  initialRosterOpen: boolean;
  toolSettings: React.ReactNode;
  termSettings: (active: boolean) => React.ReactNode;
  localMode: boolean;
  classes: Classroom[];
  quizzes: QuizDocument[];
  calendarEvents: CalendarEvent[];
  historySource: HistorySource;
  exportSource: RecordExportSource;
  academicTerms: AcademicTerm[];
  onAddClass: (name: string, role: Classroom["classRole"], sharedDataClassId?: string) => Promise<void>;
  onDeleteEmptyClass: (classId: string, confirmedName: string) => Promise<void>;
  onAddStudent: (classId: string, row: RosterRow) => Promise<Student>;
  onImport: (classId: string, rows: ReturnType<typeof parseRosterText>) => Promise<void>;
  onSaveHistoryPlan: (plan: HistoryPlan) => Promise<void>;
  onUpdateStudent: (studentId: string, patch: Partial<Student>) => Promise<void>;
  onDeleteStudent: (studentId: string) => Promise<void>;
  onSaveQuiz: (quiz: QuizDocument) => Promise<unknown> | void;
  onDeleteQuiz: (id: string) => Promise<unknown> | void;
  onGenerateQuiz: (sourceText: string, count: number) => Promise<{ title: string; questions: unknown[] }>;
  onSaveCalendarEvents: (items: CalendarEvent[]) => Promise<unknown> | void;
  onDeleteCalendarEvents: (ids: string[]) => Promise<unknown> | void;
  onSaveSchoolCalendar: (changes: SchoolCalendarChange[]) => Promise<void>;
  schoolCalendarReadOnly: boolean;
  selectedSchoolYear?: SchoolYear;
  onSelectSchoolYear: (id: string) => Promise<void>;
  onTourRosterImported: () => void;
}) {
  const { t } = useTranslation("keyboard");
  const { t: termTextLabel } = useTranslation("terms");
  const { t: recordsText } = useTranslation("adminRecords");
  const { t: studentText } = useTranslation("rosterAdmin");
  const rosterHeadingRef = useRef<HTMLHeadingElement>(null);
  const rosterImportId = useId();
  const [rosterImportOpen, setRosterImportOpen] = useState(initialRosterOpen);
  useEffect(() => setRosterImportOpen(initialRosterOpen), [classItem?.id, initialRosterOpen]);
  const [studentUpdatePending, setStudentUpdatePending] = useState<string>();
  const section = initialSection;
  function changeSection(next: AdminSection) {
    if (section === "calendar" && next !== section) {
      const calendar = document.querySelector(".school-calendar-editor");
      if (calendar?.getAttribute("data-save-state") === "saving") return;
      if (
        calendar?.getAttribute("data-save-state") === "dirty" &&
        !window.confirm(i18n.t("schoolCalendar:leaveUnsaved"))
      )
        return;
    }
    onSectionChange(next);
  }
  const pagination = useStudentPagination(students, classItem?.id || "");
  const [addedStudentId, setAddedStudentId] = useState<string>();
  useEffect(() => {
    if (!addedStudentId) return;
    const index = students.findIndex((student) => student.id === addedStudentId);
    if (index < 0) return;
    pagination.setPage(Math.floor(index / studentPageSize) + 1);
    setAddedStudentId(undefined);
  }, [addedStudentId, students, pagination.setPage]);
  const [json, setJson] = useState("");
  const [sourceText, setSourceText] = useState("");
  const [questionCount, setQuestionCount] = useState(5);
  const [generating, setGenerating] = useState(false);
  const [addingClass, setAddingClass] = useState(false);
  const [classDraftDirty, setClassDraftDirty] = useState(false);
  const [error, setError] = useState("");
  const [classError, setClassError] = useState("");
  const [studentToDelete, setStudentToDelete] = useState<Student>();
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deletingStudent, setDeletingStudent] = useState(false);
  const deleteDialogRef = useDialogFocus<HTMLElement>(
    Boolean(studentToDelete),
    () => {
      if (!deletingStudent) {
        setStudentToDelete(undefined);
        setDeleteConfirmation("");
      }
    },
    () => rosterHeadingRef.current,
  );
  const classQuizzes = quizzes.filter((item) => !item.classId || item.classId === classItem?.id);

  function suggestSharedInClassForm(form: HTMLFormElement | null) {
    if (!form) return;
    const name = (form.elements.namedItem("name") as HTMLInputElement | null)?.value || "";
    const role = (form.elements.namedItem("role") as HTMLSelectElement | null)?.value as Classroom["classRole"];
    const shared = form.elements.namedItem("sharedDataClassId") as HTMLSelectElement | null;
    if (shared) shared.value = suggestedSharedClassId(name, role, classes);
  }

  async function addClass(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    try {
      setClassError("");
      setAddingClass(true);
      await onAddClass(
        String(form.get("name")),
        String(form.get("role")) as Classroom["classRole"],
        String(form.get("sharedDataClassId") || "") || undefined,
      );
      formElement.reset();
      setClassDraftDirty(false);
      toast(i18n.t("screenCopy:teacherConsole.classCreated"));
    } catch (caught) {
      setClassError(
        caught instanceof Error ? caught.message : i18n.t("screenCopy:teacherConsole.couldNotCreateTheClassTryAgain"),
      );
    } finally {
      setAddingClass(false);
    }
  }
  async function importQuiz() {
    try {
      setError("");
      const raw = JSON.parse(json) as Record<string, unknown>;
      const quiz = validateQuizImport(raw, { workspaceId, classId: classItem!.id, id: id("quiz") });
      await onSaveQuiz(quiz);
      setJson("");
      toast(i18n.t("screenCopy:teacherConsole.addedToQuestionBank", { title: quiz.title }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : i18n.t("screenCopy:teacherConsole.invalidJSONFormat"));
    }
  }
  async function generateQuiz() {
    try {
      setError("");
      setGenerating(true);
      const result = await onGenerateQuiz(sourceText, questionCount);
      setJson(JSON.stringify(result, null, 2));
      toast(i18n.t("screenCopy:teacherConsole.questionsGeneratedCheckAnswersAndSourcesBefore"));
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : i18n.t("screenCopy:teacherConsole.couldNotGenerateQuestions"),
      );
    } finally {
      setGenerating(false);
    }
  }
  async function toggleStudentActive(student: Student) {
    if (studentUpdatePending) return;
    try {
      setError("");
      setStudentUpdatePending(student.id);
      await onUpdateStudent(student.id, { active: !student.active });
      toast(t(student.active ? "studentDisabled" : "studentEnabled", { name: student.name }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t("studentUpdateFailed"));
    } finally {
      setStudentUpdatePending(undefined);
    }
  }

  async function permanentlyDeleteStudent() {
    if (!studentToDelete || deleteConfirmation !== studentToDelete.name) return;
    try {
      setError("");
      setDeletingStudent(true);
      await onDeleteStudent(studentToDelete.id);
      toast(i18n.t("screenCopy:teacherConsole.permanentlyDeletedAndRelatedData", { name: studentToDelete.name }));
      setStudentToDelete(undefined);
      setDeleteConfirmation("");
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : i18n.t("screenCopy:teacherConsole.couldNotDeleteStudentData"),
      );
    } finally {
      setDeletingStudent(false);
    }
  }

  return (
    <div
      className="admin-workspace"
      data-autosave={studentUpdatePending ? true : undefined}
      data-save-state={studentUpdatePending ? "saving" : undefined}
    >
      <ToolPageHeader
        title={i18n.t("guidedTour:steps.demoRoster.title")}
        sessionStatus={sessionStatus}
        continued={section === "tools"}
        navigation={
          <ToolPageNavigation role="navigation" label={i18n.t("screenCopy:teacherConsole.administrationScope")}>
            <button
              aria-current={section === "classes" ? "page" : undefined}
              className={section === "classes" ? "active" : ""}
              onClick={() => {
                changeSection("classes");
                setError("");
              }}
            >
              {i18n.t("screenCopy:teacherConsole.classesAndStudents")}
            </button>
            <button
              aria-current={section === "history" ? "page" : undefined}
              className={section === "history" ? "active" : ""}
              onClick={() => {
                changeSection("history");
                setError("");
              }}
            >
              {recordsText("importTitle")}
            </button>
            <button
              type="button"
              aria-current={section === "export" ? "page" : undefined}
              className={section === "export" ? "active" : ""}
              onClick={() => {
                changeSection("export");
                setError("");
              }}
            >
              {recordsText("exportTitle")}
            </button>
            {!localMode && (
              <button
                aria-current={section === "quizzes" ? "page" : undefined}
                className={section === "quizzes" ? "active" : ""}
                onClick={() => {
                  changeSection("quizzes");
                  setError("");
                }}
              >
                {i18n.t("screenCopy:teacherConsole.questionBankManagement")}
              </button>
            )}
            <button
              aria-current={section === "calendar" ? "page" : undefined}
              className={section === "calendar" ? "active" : ""}
              onClick={() => {
                changeSection("calendar");
                setError("");
              }}
            >
              {i18n.t("screenCopy:teacherConsole.schoolWideCalendar")}
            </button>
            <button
              type="button"
              aria-current={section === "terms" ? "page" : undefined}
              className={section === "terms" ? "active" : ""}
              onClick={() => changeSection("terms")}
            >
              {termTextLabel("settingsTitle")}
            </button>
            <button
              aria-current={section === "tools" ? "page" : undefined}
              className={section === "tools" ? "active" : ""}
              onClick={() => {
                changeSection("tools");
                setError("");
              }}
            >
              {i18n.t("guidedTour:steps.demoToolSettings.title")}
            </button>
          </ToolPageNavigation>
        }
      />
      {section !== "tools" && section !== "terms" && (
        <h2 className="sr-only">
          {section === "classes"
            ? i18n.t("screenCopy:teacherConsole.classesAndStudents")
            : section === "history"
              ? recordsText("importTitle")
              : section === "export"
                ? recordsText("exportTitle")
                : section === "quizzes"
                  ? i18n.t("screenCopy:teacherConsole.questionBankManagement")
                  : i18n.t("screenCopy:teacherConsole.schoolWideCalendar")}
        </h2>
      )}
      <div hidden={section !== "tools"}>{toolSettings}</div>
      <div hidden={section !== "terms"}>{termSettings(section === "terms")}</div>
      {section === "export" && (
        <AdminRecordExportPanel
          key={classItem?.id}
          classes={classes}
          classId={classItem?.id}
          source={exportSource}
          terms={academicTerms}
        />
      )}

      {section === "classes" && (
        <div className="roster-administration">
          <section className="paper-card roster-card">
            <header className="panel-heading">
              <div>
                <h2 ref={rosterHeadingRef} tabIndex={-1}>
                  {classItem?.name || i18n.t("rosterAdmin:chooseClass")}
                </h2>
                <small>{studentText("studentCount", { count: students.length })}</small>
                {classItem?.sharedDataClassId && (
                  <small>
                    {i18n.t("screenCopy:teacherConsole.sharesRosterAndSeatingWith")}
                    {classes.find((item) => item.id === classItem.sharedDataClassId)?.name}
                    {i18n.t("screenCopy:teacherConsole.label_36150")}
                  </small>
                )}
              </div>
              <button
                type="button"
                className="roster-import-trigger"
                aria-expanded={rosterImportOpen}
                aria-controls={rosterImportId}
                disabled={readOnly || !classItem}
                onClick={() => setRosterImportOpen((open) => !open)}
              >
                {i18n.t("rosterPaste:importTitle")}
              </button>
            </header>
            {classItem && (
              <div id={rosterImportId} className="roster-import-slot" hidden={!rosterImportOpen}>
                <RosterImportPanel
                  key={classItem.id}
                  className={classItem.name}
                  students={students}
                  initialMethod={initialRosterMethod}
                  readOnly={readOnly}
                  onImport={async (rows) => {
                    await onImport(rosterClassId(classItem, classes), rows);
                    toast(i18n.t("screenCopy:teacherConsole.importedStudents", { length: rows.length }));
                    onTourRosterImported();
                  }}
                />
              </div>
            )}
            <div className="roster-table">
              <div className="table-head">
                <span>{i18n.t("rosterAdmin:number")}</span>
                <span>{i18n.t("drawHistory:name")}</span>
                <span>{i18n.t("rosterAdmin:gender")}</span>
                <span>{i18n.t("screenCopy:teacherConsole.status")}</span>
                <span>{i18n.t("screenCopy:teacherConsole.administration")}</span>
              </div>
              {pagination.items.map((student) => (
                <div key={student.id}>
                  <span>{studentNumberLabel(student)}</span>
                  <b>{student.name}</b>
                  <span>{student.gender || "—"}</span>
                  <button
                    type="button"
                    aria-label={t("studentActiveLabel", { number: studentNumberLabel(student), name: student.name })}
                    aria-pressed={student.active}
                    disabled={readOnly || Boolean(studentUpdatePending)}
                    className={student.active ? "status-pill active" : "status-pill"}
                    onClick={() => void toggleStudentActive(student)}
                  >
                    {student.active
                      ? i18n.t("screenCopy:teacherConsole.activate")
                      : i18n.t("screenCopy:teacherConsole.deactivate")}
                  </button>
                  <button
                    type="button"
                    disabled={readOnly}
                    className="student-delete-trigger"
                    aria-label={t("deleteStudent", { number: studentNumberLabel(student), name: student.name })}
                    onClick={() => {
                      setStudentToDelete(student);
                      setDeleteConfirmation("");
                      setError("");
                    }}
                  >
                    {i18n.t("personalTools:permanentlyDelete")}
                  </button>
                </div>
              ))}
            </div>
            <StudentPagination {...pagination} />
          </section>
          <div className="roster-management-tools">
            <aside className="roster-secondary-actions">
              <NewStudentForm
                key={classItem?.id || "no-class"}
                classItem={classItem}
                students={students}
                readOnly={readOnly}
                onAdd={(row) => onAddStudent(rosterClassId(classItem, classes), row)}
                onCreated={(student) => setAddedStudentId(student.id)}
                toast={toast}
              />
              <details className="paper-card roster-class-setup">
                <summary aria-label={studentText("addClass")}>{studentText("addClass")}</summary>
                <form
                  className="compact-form"
                  onSubmit={addClass}
                  onChange={() => setClassDraftDirty(true)}
                  data-save-state={addingClass ? "saving" : classDraftDirty ? "dirty" : "saved"}
                >
                  <label htmlFor="admin-class-name">
                    {i18n.t("screenCopy:teacherConsole.className")}
                    <ClassNameInput
                      id="admin-class-name"
                      name="name"
                      required
                      placeholder={i18n.t("screenCopy:teacherConsole.eGBChinese")}
                      onValueChange={(_value, input) => suggestSharedInClassForm(input.form)}
                    />
                  </label>
                  <label>
                    {i18n.t("screenCopy:teacherConsole.classPurpose")}
                    <select name="role" onChange={(event) => suggestSharedInClassForm(event.currentTarget.form)}>
                      <option value="teaching">{i18n.t("classSetup:steps.1")}</option>
                      <option value="homeroom">{i18n.t("classSetup:homeLabel")}</option>
                    </select>
                  </label>
                  {classes.some((item) => item.active && !item.sharedDataClassId) && (
                    <label>
                      {i18n.t("classroomInterface:rosterAndSeating")}
                      <select name="sharedDataClassId">
                        <option value="">{i18n.t("screenCopy:teacherConsole.createNewData")}</option>
                        {classes
                          .filter((item) => item.active && !item.sharedDataClassId)
                          .map((item) => (
                            <option value={item.id} key={item.id}>
                              {i18n.t("screenCopy:teacherConsole.useDataFrom")}
                              {item.name}
                              {i18n.t("screenCopy:teacherConsole.label")}
                            </option>
                          ))}
                      </select>
                    </label>
                  )}
                  {classError && (
                    <p className="form-error" role="alert">
                      {classError}
                    </p>
                  )}
                  <button className="primary-action" disabled={readOnly || addingClass}>
                    {addingClass
                      ? i18n.t("screenCopy:teacherConsole.creating")
                      : i18n.t("screenCopy:teacherConsole.createClass")}
                  </button>
                </form>
              </details>
              {localMode && classItem && students.length === 0 && (
                <EmptyClassDeletion
                  key={classItem.id}
                  classroom={classItem}
                  readOnly={readOnly}
                  onDelete={onDeleteEmptyClass}
                />
              )}
            </aside>
          </div>
        </div>
      )}

      {section === "history" && !readOnly && (
        <RecordImportPanel
          classItem={classItem}
          students={students}
          source={historySource}
          workspaceId={workspaceId}
          onSave={onSaveHistoryPlan}
          toast={toast}
        />
      )}
      {section === "quizzes" && !localMode && (
        <div className="admin-quiz-grid">
          <div>
            <section className="paper-card quiz-import ai-generate">
              <header>
                <div>
                  <h2>{i18n.t("screenCopy:teacherConsole.generateQuestionsFromTeachingMaterials")}</h2>
                </div>
              </header>
              <p className="ai-privacy-warning" role="note">
                <b>{i18n.t("screenCopy:teacherConsole.doNotEnterStudentPersonalData")}</b>
                {i18n.t("screenCopy:teacherConsole.materialsAreSentToAThirdParty")}
              </p>
              <label>
                {i18n.t("screenCopy:teacherConsole.teachingMaterials")}
                <textarea
                  rows={7}
                  maxLength={12000}
                  value={sourceText}
                  onChange={(event) => setSourceText(event.target.value)}
                  placeholder={i18n.t("screenCopy:teacherConsole.pasteThePassageOrNotesForQuestions")}
                />
              </label>
              <label>
                {i18n.t("screenCopy:teacherConsole.numberOfQuestions")}
                <select value={questionCount} onChange={(event) => setQuestionCount(Number(event.target.value))}>
                  {[3, 4, 5].map((count) => (
                    <option key={count} value={count}>
                      {count} {i18n.t("screenCopy:teacherConsole.questions_39ec5")}
                    </option>
                  ))}
                </select>
              </label>
              <button
                className="primary-action"
                disabled={preview || generating || sourceText.trim().length < 100}
                onClick={() => void generateQuiz()}
              >
                {generating
                  ? i18n.t("screenCopy:teacherConsole.generating")
                  : i18n.t("screenCopy:teacherConsole.generateIntoTheEditorBelow")}
              </button>
              {preview && (
                <small>{i18n.t("screenCopy:teacherConsole.aiQuestionGenerationIsAvailableAfterConnecting")}</small>
              )}
            </section>
            <section className="paper-card quiz-import">
              <header>
                <div>
                  <h2>{i18n.t("screenCopy:teacherConsole.reviewAndImportQuestionSet")}</h2>
                </div>
                <span>Schema v1</span>
              </header>
              <textarea
                aria-label={i18n.t("screenCopy:teacherConsole.questionSetJSON")}
                rows={15}
                value={json}
                onChange={(event) => setJson(event.target.value)}
                placeholder={i18n.t("screenCopy:teacherConsole.nSchemaVersionNTitleClassQuizN")}
              />
              {error && (
                <p className="form-error" role="alert">
                  {error}
                </p>
              )}
              <button
                className="primary-action"
                disabled={!classItem || !json.trim()}
                onClick={() => void importQuiz()}
              >
                {i18n.t("screenCopy:teacherConsole.validateAndAddToQuestionBank")}
              </button>
            </section>
          </div>
          <section className="paper-card admin-quiz-library">
            <header>
              <div>
                <h2>
                  {classItem?.name}
                  {i18n.t("screenCopy:teacherConsole.questionBank")}
                </h2>
              </div>
              <span>{classQuizzes.length} 組</span>
            </header>
            {classQuizzes.length ? (
              classQuizzes.map((quiz) => (
                <article key={quiz.id}>
                  <div className="quiz-number">{quiz.questions.length}</div>
                  <div>
                    <b>{quiz.title}</b>
                    <p>
                      {quiz.sourceName || i18n.t("screenCopy:teacherConsole.manualImport")} ·{" "}
                      {quiz.difficulty || "standard"}
                    </p>
                  </div>
                  <button className="danger-text" onClick={() => void onDeleteQuiz(quiz.id)}>
                    {i18n.t("dailyMemos:delete")}
                  </button>
                </article>
              ))
            ) : (
              <div className="admin-quiz-empty">
                <span>{i18n.t("screenCopy:teacherConsole.questions_39ec5")}</span>
                <p>{i18n.t("screenCopy:teacherConsole.noQuestionSetsYetPasteJSONOn")}</p>
              </div>
            )}
          </section>
        </div>
      )}
      {section === "calendar" && (
        <SchoolCalendarEditor
          key={selectedSchoolYear?.id || "legacy"}
          workspace={workspace}
          schoolYear={selectedSchoolYear}
          classes={classes}
          events={calendarEvents}
          readOnly={readOnly || schoolCalendarReadOnly}
          onSave={onSaveSchoolCalendar}
          onSelectYear={onSelectSchoolYear}
          toast={toast}
        />
      )}
      {section === "calendar" && CALENDAR_IMPORT_RELEASED && (
        <SchoolCalendarAdmin
          readOnly={readOnly}
          workspaceId={workspaceId}
          events={calendarEvents}
          onSaveMany={onSaveCalendarEvents}
          onDeleteMany={onDeleteCalendarEvents}
          toast={toast}
        />
      )}
      {studentToDelete && (
        <div
          className="record-export-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !deletingStudent) {
              setStudentToDelete(undefined);
              setDeleteConfirmation("");
            }
          }}
        >
          <section
            ref={deleteDialogRef}
            tabIndex={-1}
            className="paper-card student-delete-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="student-delete-title"
            aria-describedby="student-delete-description"
          >
            <header>
              <div>
                <h2 id="student-delete-title">
                  {i18n.t("personalTools:permanentlyDelete")} {studentNumberLabel(studentToDelete)}{" "}
                  {i18n.t("screenCopy:teacherConsole.no")} {studentToDelete.name}
                </h2>
              </div>
              <button
                type="button"
                aria-label={i18n.t("screenCopy:teacherConsole.closePermanentDeletionDialog")}
                disabled={deletingStudent}
                onClick={() => {
                  setStudentToDelete(undefined);
                  setDeleteConfirmation("");
                }}
              >
                ×
              </button>
            </header>
            <p id="student-delete-description">
              {i18n.t("screenCopy:teacherConsole.thisCannotBeUndoneTheStudentQuiz")}
            </p>
            <label>
              {i18n.t("screenCopy:teacherConsole.enterStudentName")}
              {studentToDelete.name}
              {i18n.t("screenCopy:teacherConsole.toConfirm")}
              <input
                value={deleteConfirmation}
                onChange={(event) => setDeleteConfirmation(event.target.value)}
                autoComplete="off"
              />
            </label>
            {error && (
              <p className="form-error" role="alert">
                {error}
              </p>
            )}
            <div className="student-delete-actions">
              <button
                type="button"
                data-dialog-initial-focus
                disabled={deletingStudent}
                onClick={() => {
                  setStudentToDelete(undefined);
                  setDeleteConfirmation("");
                }}
              >
                {i18n.t("personalTools:cancel")}
              </button>
              <button
                type="button"
                className="danger-button"
                disabled={readOnly || deletingStudent || deleteConfirmation !== studentToDelete.name}
                onClick={() => void permanentlyDeleteStudent()}
              >
                {deletingStudent
                  ? i18n.t("screenCopy:teacherConsole.deleting")
                  : i18n.t("screenCopy:teacherConsole.permanentlyDeleteAllRelatedData")}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function SchoolCalendarAdmin({
  readOnly = false,
  workspaceId,
  events,
  onSaveMany,
  onDeleteMany,
  toast,
}: {
  readOnly?: boolean;
  workspaceId: string;
  events: CalendarEvent[];
  onSaveMany: (items: CalendarEvent[]) => Promise<unknown> | void;
  onDeleteMany: (ids: string[]) => Promise<unknown> | void;
  toast: (message: string) => void;
}) {
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const schoolEvents = events
    .filter((item) => !item.classId)
    .sort((a, b) => a.date.localeCompare(b.date) || a.title.localeCompare(b.title, "zh-HK"));
  const latestImport = [...schoolEvents.filter((item) => item.source?.importBatchId && item.source.importedAt)].sort(
    (a, b) => (b.source!.importedAt || "").localeCompare(a.source!.importedAt || ""),
  )[0]?.source;
  const latestImportEvents = latestImport?.importBatchId
    ? schoolEvents.filter((item) => item.source?.importBatchId === latestImport.importBatchId)
    : [];
  const sources = new Set(schoolEvents.map((item) => item.source?.sourceName).filter(Boolean)).size;

  async function importCandidates(items: CalendarImportCandidate[]) {
    if (!CALENDAR_IMPORT_RELEASED) return;
    const batchId = id("calendar_import");
    const importedAt = isoNow();
    const normalized: CalendarEvent[] = items.map((item) => ({
      id: id("event"),
      workspaceId,
      date: item.startDate!,
      endDate: item.endDate,
      allDay: item.allDay,
      timezone: "Asia/Hong_Kong",
      title: item.title.trim(),
      type: item.type,
      schoolKind: item.schoolKind,
      note: item.note?.trim() || undefined,
      createdAt: importedAt,
      updatedAt: importedAt,
      source: {
        kind: item.source.kind,
        importBatchId: batchId,
        sourceName: item.source.sourceName,
        sourceRef: item.source.sourceRef,
        sourceText: item.source.sourceText,
        externalUid: item.source.externalUid,
        confidence: item.confidence,
        fingerprint: calendarFingerprint(item),
        importedAt,
      },
    }));
    try {
      setError("");
      setSaving(true);
      if (normalized.length) await onSaveMany(normalized);
      toast(i18n.t("screenCopy:teacherConsole.importedSchoolWideEvents", { length: normalized.length }));
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : i18n.t("screenCopy:teacherConsole.couldNotImportTheSchoolWideCalendar"),
      );
      throw caught;
    } finally {
      setSaving(false);
    }
  }

  async function deleteEvents(items: CalendarEvent[], message: string) {
    if (!items.length) return;
    try {
      setError("");
      setSaving(true);
      await onDeleteMany(items.map((item) => item.id));
      toast(message);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : i18n.t("screenCopy:teacherConsole.couldNotDeleteTheCalendarEvent"),
      );
    } finally {
      setSaving(false);
    }
  }

  function removeOne(item: CalendarEvent) {
    if (
      window.confirm(i18n.t("screenCopy:teacherConsole.deleteThisSchoolWideEventWillDisappear", { title: item.title }))
    ) {
      void deleteEvents([item], i18n.t("screenCopy:teacherConsole.schoolWideEventDeleted"));
    }
  }

  function undoLatestImport() {
    if (
      window.confirm(
        i18n.t("screenCopy:teacherConsole.deleteTheLatestEventsImportedFrom", {
          value: latestImport?.sourceName || i18n.t("interfaceExtras:calendarFile"),
          length: latestImportEvents.length,
        }),
      )
    ) {
      void deleteEvents(latestImportEvents, i18n.t("screenCopy:teacherConsole.latestSchoolWideCalendarImportDeleted"));
    }
  }

  return (
    <section className="school-calendar-admin">
      <PublicHolidayNotice />
      <section className="paper-card school-calendar-admin-head">
        <div>
          <h2>{i18n.t("screenCopy:teacherConsole.sharedSchoolWideEvents")}</h2>
        </div>
        <div className="school-calendar-admin-actions">
          {latestImportEvents.length > 0 && (
            <button type="button" className="danger-text" disabled={readOnly || saving} onClick={undoLatestImport}>
              {i18n.t("screenCopy:teacherConsole.deleteLatestImport")}
            </button>
          )}
          {CALENDAR_IMPORT_RELEASED && (
            <button
              type="button"
              className="primary-action"
              disabled={readOnly || saving}
              onClick={() => setImporting(true)}
            >
              {i18n.t("screenCopy:teacherConsole.importCalendar")}
            </button>
          )}
        </div>
      </section>
      <div className="school-calendar-summary" aria-label={i18n.t("screenCopy:teacherConsole.schoolCalendarSummary")}>
        <div>
          <strong>{schoolEvents.length}</strong>
          <span>{i18n.t("screenCopy:teacherConsole.schoolWideEvents")}</span>
        </div>
        <div>
          <strong>{sources}</strong>
          <span>{i18n.t("screenCopy:teacherConsole.importedFiles")}</span>
        </div>
        <div>
          <strong>{schoolEvents.filter((item) => item.date >= today()).length}</strong>
          <span>{i18n.t("screenCopy:teacherConsole.eventsFromToday")}</span>
        </div>
      </div>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <section className="paper-card school-calendar-list">
        <header>
          <div>
            <h2>{i18n.t("screenCopy:teacherConsole.importedSchoolCalendar")}</h2>
          </div>
          <span>
            {schoolEvents.length} {i18n.t("screenCopy:teacherConsole.events_9cc64")}
          </span>
        </header>
        {schoolEvents.length ? (
          <div className="school-calendar-rows">
            {schoolEvents.map((item) => (
              <article key={item.id}>
                <time dateTime={item.date}>
                  <b>
                    {new Date(`${item.date}T12:00:00`).toLocaleDateString(interfaceLocale(), {
                      month: "short",
                      day: "numeric",
                    })}
                  </b>
                  <small>
                    {item.endDate && item.endDate !== item.date
                      ? i18n.t("screenCopy:teacherConsole.to", {
                          value: new Date(`${item.endDate}T12:00:00`).toLocaleDateString(interfaceLocale(), {
                            month: "short",
                            day: "numeric",
                          }),
                        })
                      : new Date(`${item.date}T12:00:00`).toLocaleDateString(interfaceLocale(), { weekday: "short" })}
                  </small>
                </time>
                <span className={`calendar-kind-pill ${calendarKind(item)}`}>{calendarKindLabel(item)}</span>
                <div>
                  <b>{item.title}</b>
                  <small>
                    {item.source?.sourceName
                      ? i18n.t("screenCopy:teacherConsole.source", { sourceName: item.source.sourceName })
                      : i18n.t("screenCopy:teacherConsole.sharedSchoolWideEvents")}
                    {item.note ? ` · ${item.note}` : ""}
                  </small>
                </div>
                <button
                  type="button"
                  className="danger-text"
                  disabled={saving}
                  aria-label={i18n.t("screenCopy:teacherConsole.delete_f46eb", { title: item.title })}
                  onClick={() => removeOne(item)}
                >
                  {i18n.t("dailyMemos:delete")}
                </button>
              </article>
            ))}
          </div>
        ) : (
          <div className="school-calendar-empty">
            <span>曆</span>
            <h3>{i18n.t("screenCopy:teacherConsole.noSchoolWideCalendarImportedYet")}</h3>
            {CALENDAR_IMPORT_RELEASED && (
              <button type="button" className="primary-action" onClick={() => setImporting(true)}>
                {i18n.t("screenCopy:teacherConsole.importFirstCalendar")}
              </button>
            )}
          </div>
        )}
      </section>
      {CALENDAR_IMPORT_RELEASED && importing && (
        <CalendarImportDialog
          existingEvents={schoolEvents}
          onClose={() => setImporting(false)}
          onImport={importCandidates}
        />
      )}
    </section>
  );
}

function SeatingPanel({
  sessionStatus,
  readOnly = false,
  workspaceId,
  classItem,
  sharedClassId,
  students,
  charts,
  onSave,
  onPrint,
  tourChooseStudent,
  onTourStudentSelected,
  onTourSeatPlaced,
  onTourSaved,
}: PanelContext & {
  onPrint: (chart: SeatingChart) => void;
  sharedClassId: string;
  charts: SeatingChart[];
  onSave: (item: SeatingChart) => Promise<unknown> | void;
  tourChooseStudent: boolean;
  onTourStudentSelected: () => void;
  onTourSeatPlaced: () => void;
  onTourSaved: () => void;
}) {
  const { t: copy } = useTranslation("classroomInterface");
  const { t } = useTranslation("keyboard");
  const existing = charts.find((item) => item.classId === sharedClassId);
  type ChartDraft = Pick<SeatingChart, "rows" | "columns" | "seats" | "view" | "rowAisles" | "columnAisles">;
  const autosave = useAutosavedDraft<ChartDraft>({
    scope: `${workspaceId}:${sharedClassId}`,
    source: {
      rows: existing?.rows || 5,
      columns: existing?.columns || 6,
      seats: existing?.seats || {},
      view: existing?.view || "teacher",
      rowAisles: existing?.rowAisles || [],
      columnAisles: existing?.columnAisles || [],
    },
    writable: !readOnly && Boolean(classItem),
    onSave: (draft) =>
      onSave({ id: sharedClassId, workspaceId, classId: sharedClassId, ...draft, updatedAt: isoNow() }),
    onSaved: onTourSaved,
  });
  const { rows, columns, seats, view, rowAisles, columnAisles } = autosave.value;
  function updateChart<K extends keyof ChartDraft>(key: K, update: React.SetStateAction<ChartDraft[K]>) {
    autosave.update((current) => ({
      ...current,
      [key]: typeof update === "function" ? update(current[key]) : update,
    }));
  }
  const setRows = (update: React.SetStateAction<number>) => updateChart("rows", update);
  const setColumns = (update: React.SetStateAction<number>) => updateChart("columns", update);
  const setSeats = (update: React.SetStateAction<Record<string, string>>) => updateChart("seats", update);
  const setView = (update: React.SetStateAction<"teacher" | "student">) => updateChart("view", update);
  const setRowAisles = (update: React.SetStateAction<number[]>) => updateChart("rowAisles", update);
  const setColumnAisles = (update: React.SetStateAction<number[]>) => updateChart("columnAisles", update);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const suppressPanClick = useRef(false);
  const pan = useRef<{
    pointerId: number;
    x: number;
    y: number;
    startX: number;
    startY: number;
    active: boolean;
    containers: HTMLElement[];
  } | null>(null);

  function beginPan(event: React.PointerEvent<HTMLDivElement>) {
    suppressPanClick.current = false;
    pan.current = null;
    if (!(event.target instanceof HTMLElement) || !event.target.closest(".room-card, .student-bank-list")) return;
    if (event.target.closest("[data-seat-student-id]") && !readOnly) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    const containers: HTMLElement[] = [];
    let node: HTMLElement | null = event.target;
    while (node) {
      const style = getComputedStyle(node);
      if (/(auto|scroll)/.test(`${style.overflowX} ${style.overflowY}`)) containers.push(node);
      node = node.parentElement;
    }
    const page = document.scrollingElement;
    if (page instanceof HTMLElement && !containers.includes(page)) containers.push(page);
    pan.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      startX: event.clientX,
      startY: event.clientY,
      active: false,
      containers,
    };
  }

  function movePan(event: React.PointerEvent<HTMLDivElement>) {
    const current = pan.current;
    if (!current || current.pointerId !== event.pointerId) return;
    if (event.pointerType === "mouse" && event.buttons === 0) {
      pan.current = null;
      return;
    }
    if (!current.active) {
      if (Math.hypot(event.clientX - current.startX, event.clientY - current.startY) < 5) return;
      current.active = true;
      // Capture only after a drag starts, so a normal click still reaches an empty seat.
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    suppressPanClick.current = true;
    event.preventDefault();
    event.stopPropagation();
    let dx = current.x - event.clientX,
      dy = current.y - event.clientY;
    current.x = event.clientX;
    current.y = event.clientY;
    for (const container of current.containers) {
      const left = container.scrollLeft,
        top = container.scrollTop;
      container.scrollLeft += dx;
      container.scrollTop += dy;
      dx -= container.scrollLeft - left;
      dy -= container.scrollTop - top;
    }
  }

  function endPan(event: React.PointerEvent<HTMLDivElement>) {
    if (pan.current?.pointerId !== event.pointerId) return;
    if (pan.current.active) {
      event.preventDefault();
      event.stopPropagation();
    }
    pan.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
  }

  const [draggingStudentId, setDraggingStudentId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [dragPreview, setDragPreview] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const layoutRef = useRef<HTMLDivElement>(null);
  const dragPreviewRef = useRef<HTMLDivElement>(null);
  const previousPositions = useRef<Map<string, DOMRect> | null>(null);
  const landingStudentId = useRef<string | null>(null);
  const seatAnimations = useRef<Animation[]>([]);
  const [bankOpen, setBankOpen] = useState(!existing || Object.keys(existing.seats).length === 0);
  const dragRef = useRef<{
    studentId: string;
    pointerId: number;
    startX: number;
    startY: number;
    offsetX: number;
    offsetY: number;
    width: number;
    height: number;
    active: boolean;
  } | null>(null);
  const suppressClickRef = useRef(false);

  useLayoutEffect(() => {
    const previous = previousPositions.current;
    const landing = landingStudentId.current;
    previousPositions.current = null;
    landingStudentId.current = null;
    for (const animation of seatAnimations.current) animation.cancel();
    seatAnimations.current = [];
    if (!previous || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    for (const element of layoutRef.current?.querySelectorAll<HTMLElement>("[data-seat-student-id]") || []) {
      const from = previous.get(element.dataset.seatStudentId || "");
      if (!from) continue;
      const to = element.getBoundingClientRect();
      const x = from.left + from.width / 2 - to.left - to.width / 2;
      const y = from.top + from.height / 2 - to.top - to.height / 2;
      if (Math.abs(x) < 1 && Math.abs(y) < 1 && element.dataset.seatStudentId !== landing) continue;
      seatAnimations.current.push(
        element.animate(
          [
            { transform: `translate(${x}px, ${y}px) scale(1.04)`, zIndex: 3 },
            { transform: "translate(0, 0) scale(1)", zIndex: 3 },
          ],
          { duration: 340, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" },
        ),
      );
    }
  }, [seats, bankOpen]);

  useEffect(
    () => () => {
      for (const animation of seatAnimations.current) animation.cancel();
    },
    [],
  );

  function captureSeatPositions(studentId?: string) {
    landingStudentId.current = studentId && dragPreviewRef.current ? studentId : null;
    const positions = new Map<string, DOMRect>();
    for (const element of layoutRef.current?.querySelectorAll<HTMLElement>("[data-seat-student-id]") || []) {
      positions.set(element.dataset.seatStudentId || "", element.getBoundingClientRect());
    }
    // The dropped student starts from the visible floating card, while its partner starts from its seat.
    if (studentId && dragPreviewRef.current) positions.set(studentId, dragPreviewRef.current.getBoundingClientRect());
    previousPositions.current = positions;
  }

  useEffect(() => {
    if (tourChooseStudent) setBankOpen(true);
  }, [tourChooseStudent]);

  const assigned = new Set(Object.values(seats));
  const unseated = students.filter((item) => item.active && !assigned.has(item.id));
  const pagination = useStudentPagination(unseated, classItem?.id || "");
  function currentChart(): SeatingChart {
    return {
      id: sharedClassId,
      workspaceId,
      classId: sharedClassId,
      rows,
      columns,
      view,
      seats,
      rowAisles,
      columnAisles,
      updatedAt: isoNow(),
    };
  }
  function moveStudent(studentId: string, targetKey: string) {
    captureSeatPositions(studentId);
    setSeats((current) => {
      const fromKey = Object.keys(current).find((key) => current[key] === studentId);
      if (fromKey === targetKey) return { ...current };
      const next = { ...current };
      const occupant = current[targetKey];
      if (fromKey) {
        if (occupant) next[fromKey] = occupant;
        else delete next[fromKey];
      }
      next[targetKey] = studentId;
      return next;
    });
    setSelectedStudentId(null);
    onTourSeatPlaced();
  }

  function returnToBank(studentId: string) {
    if (readOnly) return;
    captureSeatPositions(studentId);
    setBankOpen(true);
    setSeats((current) => {
      const next = { ...current };
      const fromKey = Object.keys(next).find((key) => next[key] === studentId);
      if (fromKey) delete next[fromKey];
      return next;
    });
    setSelectedStudentId(null);
  }

  function beginDrag(event: React.PointerEvent<HTMLElement>, studentId: string) {
    if (readOnly) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    dragRef.current = {
      studentId,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: event.clientX - bounds.left,
      offsetY: event.clientY - bounds.top,
      width: bounds.width,
      height: bounds.height,
      active: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function trackDrag(event: React.PointerEvent<HTMLElement>) {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (!drag.active && Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) > 7) {
      drag.active = true;
      suppressClickRef.current = true;
      setDraggingStudentId(drag.studentId);
    }
    if (!drag.active) return;
    event.preventDefault();
    setDragPreview({
      x: event.clientX - drag.offsetX,
      y: event.clientY - drag.offsetY,
      width: drag.width,
      height: drag.height,
    });
    const target = document.elementFromPoint(event.clientX, event.clientY) as HTMLElement | null;
    const seat = target?.closest<HTMLElement>("[data-seat-key]");
    setDropTarget(seat?.dataset.seatKey || (target?.closest("[data-student-bank]") ? "bank" : null));
  }

  function endDrag(event: React.PointerEvent<HTMLElement>) {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (drag.active) {
      const target = document.elementFromPoint(event.clientX, event.clientY) as HTMLElement | null;
      const seat = target?.closest<HTMLElement>("[data-seat-key]");
      if (seat?.dataset.seatKey) moveStudent(drag.studentId, seat.dataset.seatKey);
      else if (target?.closest("[data-student-bank]")) returnToBank(drag.studentId);
      else {
        captureSeatPositions(drag.studentId);
        setSeats((current) => ({ ...current }));
      }
    }
    dragRef.current = null;
    setDraggingStudentId(null);
    setDragPreview(null);
    setDropTarget(null);
    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 250);
  }

  function cancelDrag() {
    if (dragRef.current?.active) {
      captureSeatPositions(dragRef.current.studentId);
      setSeats((current) => ({ ...current }));
    }
    dragRef.current = null;
    setDraggingStudentId(null);
    setDragPreview(null);
    setDropTarget(null);
    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 250);
  }

  function resizeGrid(nextRows: number, nextColumns: number) {
    if (readOnly) return;
    nextRows = Math.max(2, Math.min(8, nextRows || rows));
    nextColumns = Math.max(2, Math.min(8, nextColumns || columns));
    setRows(nextRows);
    setColumns(nextColumns);
    setSeats((current) =>
      Object.fromEntries(
        Object.entries(current).filter(([key]) => {
          const [row, column] = key.split("_").map(Number);
          return row! < nextRows && column! < nextColumns;
        }),
      ),
    );
    setRowAisles((current) => current.filter((index) => index < nextRows - 1));
    setColumnAisles((current) => current.filter((index) => index < nextColumns - 1));
  }

  function toggleAisle(axis: "row" | "column", index: number) {
    if (readOnly) return;
    const update = axis === "row" ? setRowAisles : setColumnAisles;
    update((current) =>
      current.includes(index) ? current.filter((item) => item !== index) : [...current, index].sort((a, b) => a - b),
    );
  }

  function chooseStudent(studentId: string) {
    if (readOnly) return;
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    setSelectedStudentId((current) => (current === studentId ? null : studentId));
    if (selectedStudentId !== studentId) onTourStudentSelected();
  }

  function chooseSeat(key: string, studentId?: string) {
    if (readOnly) return;
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    if (selectedStudentId) {
      moveStudent(selectedStudentId, key);
      return;
    }
    if (studentId) setSelectedStudentId(studentId);
  }

  const selectedStudent = students.find((item) => item.id === selectedStudentId);
  const draggedStudent = students.find((item) => item.id === draggingStudentId);
  const firstEmptySeatKey = Array.from(
    { length: rows * columns },
    (_, index) => `${Math.floor(index / columns)}_${index % columns}`,
  ).find((key) => !seats[key]);
  const teacherView = view === "teacher";
  const visibleRowAisles = teacherView ? rowAisles.map((index) => rows - 2 - index) : rowAisles;
  const visibleColumnAisles = teacherView ? columnAisles.map((index) => columns - 2 - index) : columnAisles;
  return (
    <div
      ref={layoutRef}
      className={`seating-layout ${bankOpen ? "bank-open" : "bank-collapsed"}`}
      onPointerDownCapture={beginPan}
      onPointerMoveCapture={movePan}
      onPointerUpCapture={endPan}
      onPointerCancelCapture={endPan}
      onClickCapture={(event) => {
        if (suppressPanClick.current && event.detail > 0) {
          suppressPanClick.current = false;
          event.preventDefault();
          event.stopPropagation();
        }
      }}
      data-autosave
      aria-busy={autosave.saving}
      data-save-state={autosave.saving ? "saving" : autosave.dirty ? "dirty" : "saved"}
    >
      <ToolPageHeader
        title={i18n.t("marketing:seatingTab")}
        titleBadge={classItem?.sharedDataClassId ? copy("sharedSeating") : undefined}
        sessionStatus={sessionStatus}
      >
        <ToolPageToolbar
          scope={
            <div className="seat-controls">
              <div className="seat-dimensions">
                <label>
                  {i18n.t("screenCopy:teacherConsole.rows_2a2b7")}
                  <input
                    type="number"
                    min="2"
                    max="8"
                    value={rows}
                    disabled={readOnly}
                    onChange={(event) => resizeGrid(Number(event.target.value), columns)}
                  />
                </label>
                <span className="seat-dimensions-separator" aria-hidden="true">
                  ×
                </span>
                <label>
                  {i18n.t("screenCopy:teacherConsole.columns_ad3ee")}
                  <input
                    type="number"
                    min="2"
                    max="8"
                    value={columns}
                    disabled={readOnly}
                    onChange={(event) => resizeGrid(rows, Number(event.target.value))}
                  />
                </label>
              </div>
              <div className="seat-view-controls">
                <span className="tool-page-context">
                  {teacherView ? i18n.t("localExport:printTeacher") : i18n.t("localExport:printStudent")}
                </span>
                <button
                  data-demo-action="seating-view"
                  disabled={readOnly}
                  onClick={() => setView(view === "teacher" ? "student" : "teacher")}
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M4 7h16m-4-4 4 4-4 4M20 17H4m4-4-4 4 4 4" />
                  </svg>
                  {i18n.t("screenCopy:teacherConsole.switchView")}
                </button>
              </div>
            </div>
          }
          actions={
            <>
              <AutosaveStatus
                name={i18n.t("marketing:seatingTab")}
                {...autosave}
                readOnly={readOnly}
                hasSaved={Boolean(existing)}
                tourTarget="seating-save"
              />
              <RecordExportButton onPrint={() => onPrint(currentChart())} disabled={!classItem} />
            </>
          }
        />
      </ToolPageHeader>
      {dragPreview &&
        draggedStudent &&
        createPortal(
          <div
            ref={dragPreviewRef}
            className="seat-drag-preview"
            data-testid="seat-drag-preview"
            aria-hidden="true"
            style={{
              width: dragPreview.width,
              height: dragPreview.height,
              transform: `translate3d(${dragPreview.x}px, ${dragPreview.y}px, 0)`,
            }}
          >
            <div className={`seat-drag-card ${studentGenderClass(draggedStudent)}`}>
              <b className="seat-drag-number">{studentNumberLabel(draggedStudent)}</b>
              <span className="seat-drag-name">{draggedStudent.name}</span>
            </div>
          </div>,
          document.body,
        )}
      <aside
        className={`${dropTarget === "bank" ? "paper-card student-bank drop-target" : "paper-card student-bank"}${bankOpen ? "" : " collapsed"}`}
        data-student-bank
      >
        <div className="console-page-row seating-bank-heading">
          {" "}
          <button
            type="button"
            className="student-bank-toggle"
            aria-expanded={bankOpen}
            onClick={() => setBankOpen((current) => !current)}
          >
            <span>
              <b>{i18n.t("screenCopy:teacherConsole.unseatedStudents")}</b>
              {selectedStudent && (
                <small>
                  {i18n.t("screenCopy:teacherConsole.selected_cca56")}
                  {studentNumberLabel(selectedStudent)} {i18n.t("screenCopy:teacherConsole.no")} {selectedStudent.name}
                </small>
              )}
            </span>
            <strong>{unseated.length}</strong>
            <small>
              {bankOpen ? i18n.t("screenCopy:teacherConsole.collapse") : i18n.t("screenCopy:teacherConsole.expand")}
            </small>
          </button>
        </div>
        {bankOpen && (
          <div className="student-bank-list">
            {unseated.length ? (
              pagination.items.map((student, index) => (
                <button
                  className={[
                    selectedStudentId === student.id ? "selected" : "",
                    draggingStudentId === student.id ? "dragging" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  data-seat-student-id={student.id}
                  data-tour={index === 0 ? "seating-student" : undefined}
                  key={student.id}
                  onClick={() => chooseStudent(student.id)}
                  onPointerDown={(event) => beginDrag(event, student.id)}
                  onPointerMove={trackDrag}
                  onPointerUp={endDrag}
                  onPointerCancel={cancelDrag}
                >
                  <b>{studentNumberLabel(student)}</b>
                  <span>{student.name}</span>
                  <i aria-hidden="true">⋮⋮</i>
                </button>
              ))
            ) : (
              <p>{i18n.t("screenCopy:teacherConsole.allStudentsHaveSeats")}</p>
            )}
          </div>
        )}
        <StudentPagination {...pagination} label="unseated" />
      </aside>
      <section className={`paper-card room-card ${teacherView ? "teacher-view" : "student-view"}`}>
        <div className="blackboard">{i18n.t("localExport:printBoard")}</div>
        <div
          className="seat-grid"
          style={{
            gridTemplateColumns: seatingTracks(columns, visibleColumnAisles, "minmax(84px, 1fr)", "12px", "50px"),
            gridTemplateRows: seatingTracks(rows, visibleRowAisles, "minmax(82px, auto)", "10px", "40px"),
          }}
        >
          {Array.from({ length: rows * columns }, (_, index) => {
            const key = `${Math.floor(index / columns)}_${index % columns}`;
            const row = Math.floor(index / columns);
            const column = index % columns;
            const visualRow = teacherView ? rows - 1 - row : row;
            const visualColumn = teacherView ? columns - 1 - column : column;
            const student = students.find((item) => item.id === seats[key]);
            const genderLabel = studentGenderLabel(student);
            const classes = [
              student ? "occupied" : "empty",
              studentGenderClass(student),
              selectedStudentId === student?.id ? "selected" : "",
              draggingStudentId === student?.id ? "dragging" : "",
              dropTarget === key ? "drop-target" : "",
            ]
              .filter(Boolean)
              .join(" ");
            return (
              <button
                className={classes}
                style={{ gridRow: visualRow * 2 + 1, gridColumn: visualColumn * 2 + 1 }}
                data-seat-key={key}
                data-seat-student-id={student?.id}
                data-tour={key === firstEmptySeatKey ? "seating-seat" : undefined}
                key={key}
                onClick={() => chooseSeat(key, student?.id)}
                onPointerDown={(event) => student && beginDrag(event, student.id)}
                onPointerMove={trackDrag}
                onPointerUp={endDrag}
                onPointerCancel={cancelDrag}
                aria-label={
                  student
                    ? i18n.t("screenCopy:teacherConsole.no_bce6e", {
                        number: studentNumberLabel(student),
                        name: student.name,
                        value: genderLabel ? i18n.t("interfaceExtras:genderSuffix", { gender: genderLabel }) : "",
                      })
                    : t(selectedStudentId ? "emptySeatSelected" : "emptySeat", { row: row + 1, column: column + 1 })
                }
              >
                {student ? (
                  <>
                    <b>{studentNumberLabel(student)}</b>
                    <span>{student.name}</span>
                    <i aria-hidden="true">⋮⋮</i>
                  </>
                ) : (
                  <small>
                    {selectedStudentId
                      ? i18n.t("screenCopy:teacherConsole.placeHere")
                      : i18n.t("localExport:printEmptySeat")}
                  </small>
                )}
              </button>
            );
          })}
          {Array.from({ length: columns - 1 }, (_, index) => {
            const active = columnAisles.includes(index);
            return (
              <button
                type="button"
                aria-pressed={active}
                className={`seating-aisle-toggle vertical${active ? " active" : ""}`}
                style={{ gridColumn: (teacherView ? columns - 2 - index : index) * 2 + 2, gridRow: "1 / -1" }}
                key={`column-aisle-${index}`}
                onClick={() => toggleAisle("column", index)}
                aria-label={i18n.t("screenCopy:teacherConsole.aisleBetweenColumnsAnd", {
                  value: i18n.t(active ? "interfaceExtras:cancelWiden" : "interfaceExtras:widen"),
                  value1: index + 1,
                  value2: index + 2,
                })}
              >
                <span aria-hidden="true">{active ? "−" : "+"}</span>
              </button>
            );
          })}
          {Array.from({ length: rows - 1 }, (_, index) => {
            const active = rowAisles.includes(index);
            return (
              <button
                type="button"
                aria-pressed={active}
                className={`seating-aisle-toggle horizontal${active ? " active" : ""}`}
                style={{ gridRow: (teacherView ? rows - 2 - index : index) * 2 + 2, gridColumn: "1 / -1" }}
                key={`row-aisle-${index}`}
                onClick={() => toggleAisle("row", index)}
                aria-label={i18n.t("screenCopy:teacherConsole.aisleBetweenRowsAnd", {
                  value: i18n.t(active ? "interfaceExtras:cancelWiden" : "interfaceExtras:widen"),
                  value1: index + 1,
                  value2: index + 2,
                })}
              >
                <span aria-hidden="true">{active ? "−" : "+"}</span>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}

const calendarKinds = [
  {
    key: "publicHoliday",
    get label() {
      return i18n.t("schoolCalendar:kinds.publicHoliday");
    },
    type: "school",
  },
  {
    key: "schoolHoliday",
    get label() {
      return i18n.t("schoolCalendar:kinds.schoolHoliday");
    },
    type: "school",
  },
  {
    key: "teacherDevelopment",
    get label() {
      return i18n.t("schoolCalendar:kinds.teacherDevelopment");
    },
    type: "school",
  },
  {
    key: "parentsDay",
    get label() {
      return i18n.t("schoolCalendar:kinds.parentsDay");
    },
    type: "school",
  },
  {
    key: "specialClass",
    get label() {
      return i18n.t("schoolCalendar:kinds.specialClass");
    },
    type: "school",
  },
  {
    key: "specialDay",
    get label() {
      return i18n.t("schoolCalendar:kinds.specialDay");
    },
    type: "school",
  },
  {
    key: "exam",
    get label() {
      return i18n.t("schoolCalendar:kinds.exam");
    },
    type: "school",
  },
  {
    key: "activity",
    get label() {
      return i18n.t("schoolCalendar:kinds.activity");
    },
    type: "school",
  },
  {
    key: "homework",
    get label() {
      return i18n.t("gradeOverview:categories.homework");
    },
    type: "homework",
  },
  {
    key: "assessment",
    get label() {
      return i18n.t("gradeOverview:categories.assessment");
    },
    type: "assessment",
  },
  {
    key: "dictation",
    get label() {
      return i18n.t("gradeOverview:categories.dictation");
    },
    type: "dictation",
  },
  {
    key: "reminder",
    get label() {
      return i18n.t("screenCopy:teacherConsole.reminder");
    },
    type: "reminder",
  },
] as const;

function calendarKind(item: CalendarEvent) {
  return item.type === "school" ? item.schoolKind || "school" : item.type;
}

function calendarKindLabel(item: CalendarEvent) {
  if (item.type === "school" && item.schoolKind) return i18n.t(`schoolCalendar:kinds.${item.schoolKind}`);
  return (
    calendarKinds.find((kind) => kind.key === calendarKind(item))?.label ||
    i18n.t("screenCopy:teacherConsole.schoolCalendar")
  );
}

function CalendarEventCount({ events }: { events: CalendarEvent[] }) {
  const holidays = events.filter(isCalendarHoliday).length;
  return (
    <>
      {i18n.t("classroomInterface:eventCount", { count: events.length - holidays })}
      {holidays > 0 && <> · {i18n.t("classroomInterface:holidayCount", { count: holidays })}</>}
    </>
  );
}

function CalendarHolidayMarker({
  event,
  date = event.date,
  scope,
  onOpen,
  showRange = false,
}: {
  event: CalendarEvent;
  date?: string;
  scope?: string;
  onOpen: (item: CalendarEvent) => void;
  showRange?: boolean;
}) {
  return (
    <button
      type="button"
      className="calendar-holiday-marker"
      aria-label={i18n.t("classroomInterface:holidayDetail", {
        date,
        title: scope ? `${scope}：${event.title}` : event.title,
      })}
      title={`${calendarKindLabel(event)}：${event.title}`}
      onClick={() => onOpen(event)}
    >
      <span className="calendar-holiday-tag">{i18n.t("classroomInterface:holidayBadge")}</span>
      <span className="calendar-holiday-name">
        {scope && <small>{scope}</small>}
        {event.title}
        {showRange && event.endDate && event.endDate !== event.date && (
          <small>
            {event.date}–{event.endDate}
          </small>
        )}
      </span>
    </button>
  );
}

function CalendarPanel({
  sessionStatus,
  workspaceId,
  classItem,
  events,
  focusDate,
  focusEvent,
  onManageSchoolCalendar,
  onSave,
  onDelete,
  onOpenCalendar,
  toast,
  ...context
}: PanelContext & {
  events: CalendarEvent[];
  focusDate?: string;
  focusEvent?: CalendarEvent;
  onManageSchoolCalendar: () => void;
  onSave: (item: CalendarEvent) => Promise<unknown> | void;
  onDelete: (id: string) => Promise<unknown> | void;
  onOpenCalendar: (date: string) => void;
}) {
  const { t: interfaceText } = useTranslation("classroomInterface");
  const { t: holidayText } = useTranslation("publicHolidays");
  const [monthDate, setMonthDate] = useNavigationState(
    `calendar:${classItem?.id}:month`,
    () => {
      const initialDate = focusDate || context.defaultRecordDate;
      const target = initialDate ? new Date(`${initialDate}T12:00:00`) : new Date();
      return agendaIso(new Date(target.getFullYear(), target.getMonth(), 1));
    },
    navigationDate,
  );
  const month = new Date(`${monthDate}T12:00:00`);
  const setMonth = (value: Date) => setMonthDate(agendaIso(value));
  const [filter, setFilter] = useNavigationState<string>(
    `calendar:${classItem?.id}:filter`,
    "all",
    navigationChoice(["all", "school", "assessment", "dictation", "homework", "reminder"]),
  );
  const [entryDate, setEntryDate] = useState<string | null>(null);
  const entryTriggerRef = useRef<HTMLElement | null>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  function openEntry(date: string, trigger: HTMLElement) {
    entryTriggerRef.current = trigger;
    setEntryDate(date);
  }
  function closeEntry() {
    setEntryDate(null);
    (entryTriggerRef.current?.isConnected ? entryTriggerRef.current : addButtonRef.current)?.focus();
  }
  function changeEntryDate(date: string) {
    setEntryDate(date);
    if (!date) return;
    const target = new Date(`${date}T12:00:00`);
    setMonth(new Date(target.getFullYear(), target.getMonth(), 1));
  }
  const [modal, setModal] = useState<
    | { view: "form"; date: string; edit?: CalendarEvent }
    | { view: "detail"; event: CalendarEvent }
    | { view: "day"; date: string }
    | null
  >(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const calendarDialogRef = useDialogFocus(
    Boolean(modal),
    () => {
      if (!saving) setModal(null);
    },
    () => addButtonRef.current,
  );
  useEffect(() => {
    if (!focusDate) return;
    const target = new Date(`${focusDate}T12:00:00`);
    setMonth(new Date(target.getFullYear(), target.getMonth(), 1));
  }, [focusDate]);
  useEffect(() => {
    if (!focusEvent) return;
    const current = events.find((item) => item.id === focusEvent.id);
    if (current) {
      setError("");
      setModal({ view: "detail", event: current });
    }
  }, [focusEvent?.id]);
  useEffect(() => {
    if (modal?.view !== "detail") return;
    const current = events.find((event) => event.id === modal.event.id);
    if (!current) setModal(null);
    else if (current !== modal.event) setModal({ view: "detail", event: current });
  }, [events, modal]);
  const relevant = events.filter((item) => !item.classId || item.classId === classItem?.id);
  const visible = relevant.filter((item) => isCalendarHoliday(item) || filter === "all" || item.type === filter);
  const firstWeekday = month.getDay();
  const dayCount = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cellCount = Math.ceil((firstWeekday + dayCount) / 7) * 7;
  const monthLabel = i18n.t("screenCopy:teacherConsole.label_e6d84", {
    value: month.getFullYear(),
    value1: month.getMonth() + 1,
  });
  const dateFor = (day: number) =>
    `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

  async function saveEvent(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!classItem || modal?.view !== "form" || modal.edit?.source?.kind === "official") return;
    const form = new FormData(event.currentTarget);
    const date = String(form.get("date") || "");
    const endDate = String(form.get("endDate") || "");
    const title = String(form.get("title") || "").trim();
    const category = String(form.get("category") || "reminder");
    const kind = calendarKinds.find((item) => item.key === category);
    if (!date || !title || !kind) return;
    if (endDate && endDate < date) {
      setError(i18n.t("classroomInterface:endBeforeStart"));
      return;
    }
    const now = isoNow();
    try {
      setError("");
      setSaving(true);
      await onSave({
        id: modal.edit?.id || id("event"),
        workspaceId,
        classId: modal.edit ? modal.edit.classId : classItem.id,
        date,
        endDate: endDate || undefined,
        allDay: true,
        timezone: "Asia/Hong_Kong",
        title,
        type: kind.type,
        ...(kind.type === "school" ? { schoolKind: kind.key as CalendarEvent["schoolKind"] } : {}),
        note: String(form.get("note") || "").trim(),
        source: modal.edit?.source,
        schoolYearId: modal.edit?.schoolYearId,
        lessonArrangement: kind.type === "school" ? modal.edit?.lessonArrangement : undefined,
        createdAt: modal.edit?.createdAt || now,
        updatedAt: now,
      });
      const [year, monthNumber] = date.split("-").map(Number);
      setMonth(new Date(year!, monthNumber! - 1, 1));
      setModal(null);
      toast(
        modal.edit
          ? i18n.t("screenCopy:teacherConsole.calendarEventUpdated")
          : i18n.t("screenCopy:teacherConsole.calendarEventAdded"),
      );
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : i18n.t("screenCopy:teacherConsole.couldNotSaveTheCalendarEvent"),
      );
    } finally {
      setSaving(false);
    }
  }

  async function deleteEvent(item: CalendarEvent) {
    if (item.source?.kind === "official") return;
    try {
      setError("");
      setSaving(true);
      await onDelete(item.id);
      setModal(null);
      toast(i18n.t("screenCopy:teacherConsole.calendarEventDeleted"));
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : i18n.t("screenCopy:teacherConsole.couldNotDeleteTheCalendarEvent_d1188"),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={`calendar-workspace${entryDate !== null ? " has-entry" : ""}`}>
      <ToolPageHeader
        title={i18n.t("guidedTour:steps.demoCalendar.title")}
        help="calendar"
        sessionStatus={sessionStatus}
      >
        <ToolPageToolbar
          scope={
            <>
              <div className="tool-calendar-navigation">
                <button
                  type="button"
                  className="calendar-arrow month-arrow"
                  aria-label={i18n.t("screenCopy:teacherConsole.previousMonth")}
                  onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
                >
                  <CalendarArrowIcon direction="left" />
                </button>
                <h2>{monthLabel}</h2>
                <button
                  type="button"
                  className="calendar-arrow month-arrow"
                  data-demo-action="calendar-next"
                  aria-label={i18n.t("screenCopy:teacherConsole.nextMonth")}
                  onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
                >
                  <CalendarArrowIcon direction="right" />
                </button>
              </div>
              <button
                type="button"
                onClick={() => {
                  const now = new Date();
                  setMonth(new Date(now.getFullYear(), now.getMonth(), 1));
                }}
              >
                {i18n.t("screenCopy:teacherConsole.today")}
              </button>
              <select
                aria-label={i18n.t("screenCopy:teacherConsole.filterCalendarEvents")}
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
              >
                <option value="all">{i18n.t("gradeOverview:categories.all")}</option>
                <option value="school">{i18n.t("screenCopy:teacherConsole.schoolCalendar")}</option>
                <option value="homework">{i18n.t("gradeOverview:categories.homework")}</option>
                <option value="assessment">{i18n.t("gradeOverview:categories.assessment")}</option>
                <option value="dictation">{i18n.t("gradeOverview:categories.dictation")}</option>
                <option value="reminder">{i18n.t("screenCopy:teacherConsole.reminder")}</option>
              </select>
            </>
          }
          actions={
            <button
              type="button"
              className="primary-action"
              ref={addButtonRef}
              data-demo-action="calendar-add"
              onClick={(event) => openEntry(today(), event.currentTarget)}
            >
              {i18n.t("screenCopy:teacherConsole.newEvent")}
            </button>
          }
        />
      </ToolPageHeader>
      <section className="paper-card month-calendar">
        <PublicHolidayNotice years={[month.getFullYear()]} />

        <div className="month-calendar-legend" aria-label={i18n.t("screenCopy:teacherConsole.calendarColourLegend")}>
          <span className="calendar-holiday-legend">
            <b className="calendar-holiday-tag">{interfaceText("holidayBadge")}</b>
          </span>
          {calendarKinds
            .filter((kind) => kind.key !== "publicHoliday" && kind.key !== "schoolHoliday")
            .map((kind) => (
              <span key={kind.key}>
                <i className={`calendar-dot ${kind.key}`} />
                {kind.label}
              </span>
            ))}
        </div>
        <div className="month-calendar-scroll">
          <div className="month-calendar-grid">
            {weekdayLabels().map((day, index) => (
              <div className={`month-weekday${index === 0 ? " sunday" : ""}`} key={day}>
                {day}
              </div>
            ))}
            {Array.from({ length: cellCount }, (_, index) => {
              const day = index - firstWeekday + 1;
              if (day < 1 || day > dayCount) return <div className="month-day muted" key={`empty-${index}`} />;
              const date = dateFor(day);
              const dayEvents = visible
                .filter((item) => (item.endDate ? item.date <= date && item.endDate >= date : item.date === date))
                .sort((a, b) => a.title.localeCompare(b.title, "zh-HK"));
              const isPublicHoliday =
                isHongKongPublicHolidayDate(date) || dayEvents.some((item) => calendarKind(item) === "publicHoliday");
              const holidays = dayEvents.filter(isCalendarHoliday);
              const reminders = dayEvents.filter((item) => !isCalendarHoliday(item));
              const isSunday = index % 7 === 0;
              return (
                <div
                  className={`month-day${date === today() ? " today" : ""}${date === entryDate ? " selected" : ""}${isSunday ? " sunday" : ""}${isPublicHoliday ? " public-holiday" : ""}${holidays.length ? " holiday" : ""}`}
                  key={date}
                >
                  <button
                    type="button"
                    className="month-day-number"
                    aria-label={i18n.t("screenCopy:teacherConsole.addEvent", {
                      date: date,
                      value: isPublicHoliday ? i18n.t("interfaceExtras:publicHolidayDate") : "",
                    })}
                    aria-pressed={date === entryDate}
                    onClick={(event) => openEntry(date, event.currentTarget)}
                  >
                    {day}
                  </button>
                  {holidays.map((item) => (
                    <CalendarHolidayMarker
                      key={item.id}
                      event={item}
                      date={date}
                      onOpen={(event) => {
                        setError("");
                        setModal({ view: "detail", event });
                      }}
                    />
                  ))}
                  {reminders.length > 0 && (
                    <button
                      type="button"
                      className="month-day-count"
                      onClick={() => {
                        setError("");
                        setModal({ view: "day", date });
                      }}
                      aria-haspopup="dialog"
                      aria-label={interfaceText("dayCount", { date, count: reminders.length })}
                    >
                      {reminders.slice(0, 2).map((item) => (
                        <span
                          className="month-event-chip"
                          key={item.id}
                          title={`${calendarKindLabel(item)}：${item.title}`}
                        >
                          <i className={`calendar-dot ${calendarKind(item)}`} aria-hidden="true" />
                          <span>{item.title}</span>
                          <CalendarArrangement event={item} />
                        </span>
                      ))}
                      {reminders.length > 2 && (
                        <span className="month-event-overflow">
                          {interfaceText("moreEvents", { count: reminders.length - 2 })}
                        </span>
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {modal && (
          <div
            className="calendar-modal-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget && !saving) setModal(null);
            }}
          >
            <div
              ref={calendarDialogRef}
              tabIndex={-1}
              className="paper-card calendar-modal"
              role="dialog"
              aria-modal="true"
              aria-label={
                modal.view === "form"
                  ? modal.edit
                    ? i18n.t("screenCopy:teacherConsole.editEvent")
                    : i18n.t("screenCopy:teacherConsole.newEvent_4b257")
                  : modal.view === "day"
                    ? interfaceText("dayTitle", { date: modal.date })
                    : modal.event.title
              }
            >
              <header>
                <h2>
                  {modal.view === "form"
                    ? modal.edit
                      ? i18n.t("screenCopy:teacherConsole.editEvent")
                      : i18n.t("screenCopy:teacherConsole.newEvent_4b257")
                    : modal.view === "day"
                      ? interfaceText("dayTitle", { date: modal.date })
                      : modal.event.title}
                </h2>
                <button
                  type="button"
                  aria-label={i18n.t("personalTools:close")}
                  disabled={saving}
                  onClick={() => setModal(null)}
                >
                  ×
                </button>
              </header>
              {modal.view === "form" ? (
                <form onSubmit={(event) => void saveEvent(event)}>
                  <label>
                    {i18n.t("terms:start")}
                    <input name="date" type="date" defaultValue={modal.date} required />
                  </label>
                  <label>
                    {i18n.t("classroomInterface:endDate")}
                    <input name="endDate" type="date" defaultValue={modal.edit?.endDate || ""} />
                  </label>
                  <label>
                    {i18n.t("gradeOverview:table.categoryFilter")}
                    <select
                      name="category"
                      defaultValue={
                        modal.edit
                          ? calendarKind(modal.edit) === "school"
                            ? "specialDay"
                            : calendarKind(modal.edit)
                          : "reminder"
                      }
                    >
                      {calendarKinds.map((kind) => (
                        <option value={kind.key} key={kind.key}>
                          {kind.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    {i18n.t("classroomInterface:itemName")}
                    <input
                      name="title"
                      data-demo-action="calendar-title"
                      defaultValue={modal.edit?.title || ""}
                      maxLength={80}
                      required
                    />
                  </label>
                  <label>
                    {i18n.t("landing:typeNotes")}
                    <textarea name="note" defaultValue={modal.edit?.note || ""} rows={3} maxLength={500} />
                  </label>
                  {error && (
                    <p className="form-error" role="alert">
                      {error}
                    </p>
                  )}
                  <div className="teaching-item-actions">
                    <button type="button" disabled={saving} onClick={() => setModal(null)}>
                      {i18n.t("personalTools:cancel")}
                    </button>
                    <button
                      className="primary-action"
                      data-demo-action="calendar-save"
                      disabled={context.readOnly || saving}
                    >
                      {saving
                        ? i18n.t("screenCopy:teacherConsole.saving")
                        : i18n.t("screenCopy:teacherConsole.saveEvent")}
                    </button>
                  </div>
                </form>
              ) : modal.view === "day" ? (
                <div className="calendar-day-list">
                  {visible
                    .filter(
                      (item) =>
                        !isCalendarHoliday(item) &&
                        (item.endDate
                          ? item.date <= modal.date && item.endDate >= modal.date
                          : item.date === modal.date),
                    )
                    .sort((a, b) => a.title.localeCompare(b.title, "zh-HK"))
                    .map((item) => (
                      <button
                        type="button"
                        className={`month-event ${calendarKind(item)}`}
                        aria-description={calendarArrangementLabel(item)}
                        key={item.id}
                        onClick={() => {
                          setError("");
                          setModal({ view: "detail", event: item });
                        }}
                        aria-label={i18n.t("screenCopy:teacherConsole.view", {
                          value: calendarKindLabel(item),
                          title: item.title,
                          value2: item.note ? i18n.t("interfaceExtras:noteSuffix", { note: item.note }) : "",
                        })}
                      >
                        <span className="calendar-day-event-kind">
                          <b>{calendarKindLabel(item)}</b>
                          <CalendarArrangement event={item} />
                          {!item.classId && <em>{i18n.t("screenCopy:teacherConsole.schoolWide")}</em>}
                        </span>
                        <strong className="calendar-day-event-title">{item.title}</strong>
                        {item.note && (
                          <small>
                            {i18n.t("screenCopy:teacherConsole.notes")}
                            {item.note}
                          </small>
                        )}
                      </button>
                    ))}
                </div>
              ) : (
                <div className="calendar-detail">
                  <p>
                    {new Date(`${modal.event.date}T12:00:00`).toLocaleDateString(interfaceLocale(), {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      weekday: "long",
                    })}
                    {modal.event.endDate && modal.event.endDate !== modal.event.date
                      ? i18n.t("screenCopy:teacherConsole.to_1fe2b", {
                          value: new Date(`${modal.event.endDate}T12:00:00`).toLocaleDateString(interfaceLocale(), {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          }),
                        })
                      : ""}{" "}
                    · {modal.event.classId ? classItem?.name : i18n.t("screenCopy:teacherConsole.schoolWide_0628a")}
                  </p>
                  <span className={`calendar-kind-pill ${calendarKind(modal.event)}`}>
                    {calendarKindLabel(modal.event)}
                  </span>
                  <h3>{modal.event.title}</h3>
                  <CalendarArrangement event={modal.event} />
                  <p>
                    {modal.event.source?.kind === "official"
                      ? holidayText("officialDetail")
                      : modal.event.note || i18n.t("screenCopy:teacherConsole.noAdditionalNotes")}
                  </p>
                  {modal.event.source?.kind === "official" ? (
                    <a href={PUBLIC_HOLIDAY_SOURCE_URL} target="_blank" rel="noreferrer">
                      {holidayText("source")}
                    </a>
                  ) : (
                    modal.event.source && (
                      <p className="calendar-source-note">
                        {i18n.t("screenCopy:teacherConsole.source_dd8a9")}
                        {modal.event.source.sourceName || modal.event.source.kind}
                        {modal.event.source.sourceRef ? ` · ${modal.event.source.sourceRef}` : ""}
                      </p>
                    )
                  )}
                  {error && (
                    <p className="form-error" role="alert">
                      {error}
                    </p>
                  )}
                  {modal.event.source?.kind !== "official" && modal.event.type === "school" && (
                    <div className="calendar-detail-actions">
                      <button type="button" onClick={onManageSchoolCalendar}>
                        {i18n.t("schoolCalendar:manage")}
                      </button>
                    </div>
                  )}
                  {modal.event.source?.kind !== "official" &&
                    modal.event.type !== "school" &&
                    modal.event.classId === classItem?.id && (
                      <div className="calendar-detail-actions">
                        <button
                          type="button"
                          onClick={() => {
                            setError("");
                            if (context.readOnly) return;
                            setModal({ view: "form", date: modal.event.date, edit: modal.event });
                          }}
                        >
                          {i18n.t("screenCopy:teacherConsole.edit")}
                        </button>
                        <button
                          type="button"
                          className="danger-text"
                          disabled={saving}
                          onClick={() => void deleteEvent(modal.event)}
                        >
                          {i18n.t("dailyMemos:delete")}
                        </button>
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>
        )}
      </section>
      {entryDate !== null && (
        <HomeworkPanel
          {...context}
          workspaceId={workspaceId}
          classItem={classItem}
          toast={toast}
          date={entryDate}
          onDateChange={changeEntryDate}
          onClose={closeEntry}
          onSave={onSave}
          onOpenCalendar={(date) => {
            setEntryDate(null);
            setFilter("all");
            const target = new Date(`${date}T12:00:00`);
            setMonth(new Date(target.getFullYear(), target.getMonth(), 1));
            onOpenCalendar(date);
            (entryTriggerRef.current?.isConnected ? entryTriggerRef.current : addButtonRef.current)?.focus();
          }}
        />
      )}
    </div>
  );
}

function RosterPanel({
  workspaceId,
  classItem,
  students,
  onAddClass,
  onImport,
  onUpdateStudent,
  toast,
}: PanelContext & {
  onAddClass: (name: string, role: Classroom["classRole"]) => Promise<void>;
  onImport: (classId: string, rows: ReturnType<typeof parseRosterText>) => Promise<void>;
  onUpdateStudent: (studentId: string, patch: Partial<Student>) => Promise<void>;
}) {
  const { t: copy } = useTranslation("classroomInterface");
  const { t: pasteText } = useTranslation("rosterPaste");
  const pagination = useStudentPagination(students, classItem?.id || "");
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  async function addClass(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const target = event.currentTarget;
    const form = new FormData(target);
    await onAddClass(String(form.get("name")), String(form.get("role")) as Classroom["classRole"]);
    target.reset();
    toast(i18n.t("screenCopy:teacherConsole.classCreated"));
  }
  async function importRows() {
    try {
      setError("");
      const rows = parseRosterText(text);
      await onImport(classItem!.id, rows);
      setText("");
      toast(i18n.t("screenCopy:teacherConsole.importedStudents", { length: rows.length }));
    } catch (caught) {
      setError(
        caught instanceof RosterParseError
          ? pasteText(`errors.${caught.code}`, { line: caught.line, number: caught.studentNumber })
          : caught instanceof Error
            ? caught.message
            : pasteText("errors.format", { line: 1 }),
      );
    }
  }
  return (
    <div className="roster-layout">
      <aside>
        <form className="paper-card compact-form" onSubmit={addClass}>
          <h2>{i18n.t("classSetupStatus:addClass")}</h2>
          <label htmlFor="roster-class-name">
            {i18n.t("screenCopy:teacherConsole.className")}
            <ClassNameInput
              id="roster-class-name"
              name="name"
              required
              placeholder={i18n.t("screenCopy:teacherConsole.eGBChinese")}
            />
          </label>
          <label>
            {i18n.t("screenCopy:teacherConsole.type")}
            <select name="role">
              <option value="teaching">{i18n.t("classSetup:steps.1")}</option>
              <option value="homeroom">{i18n.t("classSetup:steps.0")}</option>
            </select>
          </label>
          <button className="primary-action">{i18n.t("screenCopy:teacherConsole.createClass")}</button>
        </form>
      </aside>
      <section className="paper-card roster-card">
        <header className="panel-heading">
          <div>
            <h2>{classItem?.name || i18n.t("rosterAdmin:chooseClass")}</h2>
          </div>
        </header>
        <div className="roster-table">
          <div className="table-head">
            <span>{i18n.t("rosterAdmin:number")}</span>
            <span>{i18n.t("drawHistory:name")}</span>
            <span>{i18n.t("rosterAdmin:gender")}</span>
            <span>{i18n.t("screenCopy:teacherConsole.status")}</span>
          </div>
          {pagination.items.map((student) => (
            <div key={student.id}>
              <span>{studentNumberLabel(student)}</span>
              <b>{student.name}</b>
              <span>{student.gender || "—"}</span>
              <button
                aria-pressed={student.active}
                className={student.active ? "status-pill active" : "status-pill"}
                onClick={() => void onUpdateStudent(student.id, { active: !student.active })}
              >
                {student.active
                  ? i18n.t("screenCopy:teacherConsole.activate")
                  : i18n.t("screenCopy:teacherConsole.deactivate")}
              </button>
            </div>
          ))}
        </div>
        <StudentPagination {...pagination} />
        <div className="import-drawer">
          <div>
            <b>{pasteText("title")}</b>
            <small>{copy("rosterFormat")}</small>
          </div>
          <textarea
            aria-label={pasteText("input")}
            value={text}
            onChange={(event) => setText(event.target.value)}
            rows={5}
            placeholder={pasteText("placeholder")}
          />
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <button className="primary-action" disabled={!classItem || !text.trim()} onClick={() => void importRows()}>
            {i18n.t("guidedTour:steps.rosterSubmit.title")}
          </button>
        </div>
      </section>
    </div>
  );
}

function QuizPanel({
  workspaceId,
  classItem,
  quizzes,
  sessions,
  responses,
  onSave,
  onDelete,
  onCreateSession,
  toast,
}: PanelContext & {
  quizzes: QuizDocument[];
  sessions: QuizSession[];
  responses: QuizResponse[];
  onSave: (quiz: QuizDocument) => Promise<unknown> | void;
  onDelete: (id: string) => Promise<unknown> | void;
  onCreateSession: (
    quizId: string,
    classId: string,
    duration: number,
  ) => Promise<(QuizSession & { joinUrl?: string }) | undefined>;
}) {
  const [json, setJson] = useState("");
  const [error, setError] = useState("");
  const [activeSession, setActiveSession] = useState<QuizSession & { joinUrl?: string }>();
  const [qr, setQr] = useState("");
  const classQuizzes = quizzes.filter((item) => !item.classId || item.classId === classItem?.id);
  useEffect(() => {
    if (activeSession?.joinUrl)
      QRCode.toDataURL(activeSession.joinUrl, {
        margin: 1,
        width: 220,
        color: { dark: "#17352D", light: "#FFFFFF" },
      }).then(setQr);
  }, [activeSession?.joinUrl]);
  async function importQuiz() {
    try {
      setError("");
      const raw = JSON.parse(json) as Record<string, unknown>;
      const quiz = validateQuizImport(raw, { workspaceId, classId: classItem!.id, id: id("quiz") });
      await onSave(quiz);
      setJson("");
      toast(i18n.t("screenCopy:teacherConsole.addedToQuestionBank", { title: quiz.title }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : i18n.t("screenCopy:teacherConsole.invalidJSONFormat"));
    }
  }
  async function start(quizId: string) {
    const session = await onCreateSession(quizId, classItem!.id, 10);
    if (session) {
      setActiveSession(session);
      toast(i18n.t("screenCopy:teacherConsole.quizCodeCreated", { code: session.code }));
    }
  }
  const sessionResponses = responses.filter((item) => item.sessionId === activeSession?.id);
  return (
    <div className="quiz-studio">
      <section className="paper-card quiz-import">
        <header>
          <div>
            <p className="eyebrow">JSON IMPORT</p>
            <h2>{i18n.t("screenCopy:teacherConsole.bringInAIGeneratedQuestions")}</h2>
          </div>
          <span>Schema v1</span>
        </header>
        <p>{i18n.t("screenCopy:teacherConsole.pasteJSONContainingTitleQuestionsFourOptions")}</p>
        <textarea
          aria-label={i18n.t("screenCopy:teacherConsole.questionSetJSON")}
          rows={12}
          value={json}
          onChange={(event) => setJson(event.target.value)}
          placeholder={i18n.t("screenCopy:teacherConsole.nSchemaVersionNTitleClassQuizN")}
        />
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <button className="primary-action" disabled={!json.trim()} onClick={() => void importQuiz()}>
          {i18n.t("screenCopy:teacherConsole.validateAndAddToQuestionBank")}
        </button>
      </section>
      <section className="paper-card quiz-library">
        <header>
          <div>
            <p className="eyebrow">QUIZ LIBRARY</p>
            <h2>{i18n.t("screenCopy:teacherConsole.classQuestionBank")}</h2>
          </div>
          <small>{classQuizzes.length} 組</small>
        </header>
        {classQuizzes.map((quiz) => (
          <article key={quiz.id}>
            <div className="quiz-number">{quiz.questions.length}</div>
            <div>
              <b>{quiz.title}</b>
              <p>
                {quiz.sourceName || i18n.t("screenCopy:teacherConsole.manualImport")} · {quiz.difficulty}
              </p>
            </div>
            <button onClick={() => void start(quiz.id)}>{i18n.t("screenCopy:teacherConsole.startMinuteQuiz")}</button>
            <button className="danger-text" onClick={() => void onDelete(quiz.id)}>
              {i18n.t("dailyMemos:delete")}
            </button>
          </article>
        ))}
      </section>
      {activeSession && (
        <section className="paper-card live-session">
          <div className="live-code">
            <small>{i18n.t("screenCopy:teacherConsole.studentQuizCode")}</small>
            <strong>{activeSession.code}</strong>
            <span>
              {i18n.t("screenCopy:teacherConsole.submitted_61215")} {sessionResponses.length}{" "}
              {i18n.t("screenCopy:teacherConsole.responses")}
            </span>
          </div>
          {qr && (
            <img src={qr} alt={i18n.t("screenCopy:teacherConsole.qrCodeToJoinQuiz", { code: activeSession.code })} />
          )}
          <div>
            <b>{activeSession.title}</b>
            <a href={activeSession.joinUrl || `/join/${activeSession.code}`} target="_blank" rel="noreferrer">
              {i18n.t("screenCopy:teacherConsole.openStudentPage")}
            </a>
          </div>
        </section>
      )}
    </div>
  );
}

function BillingPanel({
  workspace,
  workspaceId,
  preview,
  cloudActions,
  user,
  toast,
}: PanelContext & { cloudActions: ReturnType<typeof useWorkspaceActions>; user: User | null }) {
  const { t: brandText } = useTranslation("brand");
  const { t: copy } = useTranslation("classroomInterface");
  const trialDays = Math.max(0, Math.ceil((new Date(workspace.trialEndsAt).getTime() - Date.now()) / 86_400_000));
  const [exporting, setExporting] = useState(false);
  async function exportData() {
    if (exporting) return;
    setExporting(true);
    try {
      const data = preview ? { workspace, note: "產品預覽不包含真實課堂資料。" } : await cloudActions.exportWorkspace();
      downloadJson(
        data,
        preview
          ? i18n.t("screenCopy:teacherConsole.previewDataJson", { value: brandText("name") })
          : i18n.t("screenCopy:teacherConsole.fullExportJson", { value: brandText("name"), value1: today() }),
      );
      toast(i18n.t("screenCopy:teacherConsole.dataFileDownloadStarted"));
    } catch (error) {
      toast(
        error instanceof Error
          ? i18n.t("screenCopy:teacherConsole.exportFailed", { message: error.message })
          : i18n.t("screenCopy:teacherConsole.exportFailedTryAgain"),
      );
    } finally {
      setExporting(false);
    }
  }
  async function deletion() {
    if (preview) {
      toast(i18n.t("screenCopy:teacherConsole.previewModeDoesNotCreateOrDelete"));
      return;
    }
    if (!confirm(i18n.t("screenCopy:teacherConsole.theWorkspaceBecomesReadOnlyImmediatelyAnd"))) return;
    const result = await cloudActions.requestDeletion();
    toast(
      i18n.t("screenCopy:teacherConsole.readOnlyDataRetainedUntil", {
        value: new Date(result.graceEndsAt).toLocaleDateString(interfaceLocale()),
      }),
    );
  }
  if (preview)
    return (
      <section className="paper-card data-card">
        <h2>{i18n.t("screenCopy:teacherConsole.demoData")}</h2>
        <p>{copy("demoDataNotice")}</p>
        <a href="/app">{copy("demoTrialAction")}</a>
      </section>
    );
  return (
    <div className="billing-layout">
      <section className="paper-card plan-card">
        <div className="plan-stamp">
          {workspace.accessMode === "active" ? "PRO" : i18n.t("screenCopy:teacherConsole.trial")}
        </div>
        <h2>
          {workspace.accessMode === "active"
            ? brandText("monthlyPlan")
            : i18n.t("screenCopy:teacherConsole.dayFullTrial")}
        </h2>
        <p>
          {workspace.accessMode === "active"
            ? i18n.t("screenCopy:teacherConsole.allNonAIFeaturesAreAvailable")
            : i18n.t("screenCopy:teacherConsole.aboutDaysLeftNonAIFeaturesAre", { trialDays: trialDays })}
        </p>
        <dl>
          <div>
            <dt>{i18n.t("screenCopy:teacherConsole.teacherAccount")}</dt>
            <dd>{user?.email || i18n.t("screenCopy:teacherConsole.previewAccount")}</dd>
          </div>
          <div>
            <dt>{i18n.t("screenCopy:teacherConsole.dataSpace")}</dt>
            <dd>{workspace.name}</dd>
          </div>
          <div>
            <dt>{i18n.t("screenCopy:teacherConsole.retainedAfterCancellation")}</dt>
            <dd>
              {workspace.retentionDays} {i18n.t("screenCopy:teacherConsole.daysReadOnly")}
            </dd>
          </div>
        </dl>
        <button
          className="primary-action large"
          onClick={() =>
            preview
              ? toast(i18n.t("screenCopy:teacherConsole.thePaymentPageOpensAfterStripeIs"))
              : workspace.accessMode === "active"
                ? void cloudActions.billingPortal()
                : void cloudActions.checkout()
          }
        >
          {workspace.accessMode === "active"
            ? i18n.t("screenCopy:teacherConsole.managePaymentsAndSubscription")
            : i18n.t("screenCopy:teacherConsole.chooseMonthlyPlan")}
        </button>
      </section>
      <section className="paper-card data-card">
        <h2>{i18n.t("screenCopy:teacherConsole.youControlYourData")}</h2>
        <article>
          <div>
            <b>{i18n.t("screenCopy:teacherConsole.fullDataExport")}</b>
          </div>
          <RecordActionButton action="export" disabled={exporting} onClick={() => void exportData()}>
            {exporting ? i18n.t("adminRecords:working") : i18n.t("screenCopy:teacherConsole.exportNow")}
          </RecordActionButton>
        </article>
        <article>
          <div>
            <b>{i18n.t("screenCopy:teacherConsole.deleteWorkspace")}</b>
            <p>{i18n.t("screenCopy:teacherConsole.readOnlyImmediatelyRetainedForExportFor")}</p>
          </div>
          <button className="danger-button" onClick={() => void deletion()}>
            {i18n.t("screenCopy:teacherConsole.requestDeletion")}
          </button>
        </article>
      </section>
    </div>
  );
}
